import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, deleteDoc, getDoc, deleteField, setDoc, onSnapshot, arrayRemove, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import clubService from '../../services/clubService';
import { generateId, formatCurrency, formatDate, getRegistrationFeeLabel } from '../../utils/helpers';
import { Shield, Users, Plus, Settings, ChevronRight, ChevronDown, Trophy, Calendar, DollarSign, MessageCircle, Save, MapPin, X, PartyPopper, Clock, Trash2, RefreshCw, BarChart3, Receipt, Search, Database, CheckCircle } from 'lucide-react';
import Header from '../shared/Header';
import PlayerDashboard from '../player/PlayerDashboard';
import { sendGameReminder, sendAvailabilityReminder, checkAndSendUpcomingGameReminders } from '../../services/reminderService';
import { notifyTeamPlayersAboutNewGame, notifyPlayersAboutExpenseSplit, processSquadPublishNotifications, notifyTeamRosterChange } from '../../services/notificationService';
import WhatsAppGroupMessenger from './WhatsAppGroupMessenger';
import ClubCreation from './ClubCreation';
import OverviewView from './OverviewView';
import FinancialReports from './FinancialReports';
import ExpenseManagement from './ExpenseManagement';
import VolunteerManagement from './VolunteerManagement';
import ReceiptManagement from './ReceiptManagement';
import GamesList from './GamesList';
import AddGame from './AddGame';
import EditGame from './EditGame';
import ExpenseReport from './ExpenseReport';
import FinancialDashboard from './FinancialDashboard';
import PaymentManagement from './PaymentManagement';
import RegistrationReport from './reports/RegistrationReport';
import PaymentReport from './reports/PaymentReport';
import PlayersReport from './reports/PlayersReport';
import PlayerParticipationReport from './reports/PlayerParticipationReport';
import TournamentMigrationTool from './TournamentMigrationTool';
import ClubSettings from './ClubSettings';
import SquadSelection from './SquadSelection';
import TournamentDetailView from './TournamentDetailView';
import EventsList from '../Event/EventsList';
import AddEvent from '../Event/AddEvent';
import EditEvent from '../Event/EditEvent';
import EventWhatsAppMessenger from '../Event/EventWhatsAppMessenger';
import FeedbackButton from '../shared/FeedbackButton';
import ClubFeedback from './ClubFeedback';
import FeedbackManagement from './FeedbackManagement';
import PullToRefresh from 'react-simple-pull-to-refresh';
import PlayerRoleManagement from './PlayerRoleManagement';
import MemberStatsView from './MemberStatsView';
import MobileBottomNav from './shared/MobileBottomNav';
import CreateTournament from './CreateTournament';
import EditTournament from './EditTournament';
import MerchandiseOrders from './MerchandiseOrders';
import TeamMigrationTool from './TeamMigrationTool';
import SyncSchedule from './SyncSchedule';

const AdminDashboard = ({ user, onLogout }) => {
  console.log('🎯 AdminDashboard component mounted/rendered');
  console.log('🎯 User email:', user?.email, 'UID:', user?.uid);

  const { clubs, selectedClub, loading, createClub, updateClub, setSelectedClub, reloadClubs } = useClub(user.uid);
  const { isSuperAdmin, addClubAdmin, removeClubAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL-synced view state for proper browser navigation
  // Default to 'overview' for regular admins, 'clubs-list' for super admins
  const [viewState, setViewState] = useState(() => {
    const urlView = searchParams.get('view');
    if (urlView) return urlView;
    // Will be corrected by useEffect once isSuperAdmin is available
    return 'overview';
  });

  // Custom setView that updates both state and URL
  const setView = useCallback((newView, params = {}) => {
    setViewState(newView);
    setSearchParams({ view: newView, ...params }, { replace: false });
  }, [setSearchParams]);

  // Sync viewState when URL changes (browser back/forward)
  useEffect(() => {
    const urlView = searchParams.get('view');
    if (urlView && urlView !== viewState) {
      setViewState(urlView);
    }
  }, [searchParams]);

  // Alias for compatibility
  const view = viewState;

  // ============ PERMISSION LOGIC ============


  const [copied, setCopied] = useState(false);

  // Data state
  const [games, setGames] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [paymentSubmissions, setPaymentSubmissions] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [pendingPlayers, setPendingPlayers] = useState([]); // Players awaiting approval
  const [impersonatingUser, setImpersonatingUser] = useState(null); // For test mode impersonation
  const [events, setEvents] = useState([]);

  // Form states
  const [teamName, setTeamName] = useState('');
  const [teamWhatsAppLink, setTeamWhatsAppLink] = useState('');
  const [teamBallType, setTeamBallType] = useState('leather'); // NEW: leather or tennis
  const [teamSelectedPlayers, setTeamSelectedPlayers] = useState([]); // Player IDs selected during team creation
  const [tournamentName, setTournamentName] = useState('');
  const [tournamentStartDate, setTournamentStartDate] = useState('');
  const [tournamentEndDate, setTournamentEndDate] = useState(''); // NEW
  const [tournamentBallType, setTournamentBallType] = useState('leather'); // leather or tennis
  const [tournamentMaxSquad, setTournamentMaxSquad] = useState(11); // 8, 11, or 12
  const [tournamentAllowSuperSubs, setTournamentAllowSuperSubs] = useState(false);
  const [gameName, setGameName] = useState('');
  const [gameDate, setGameDate] = useState('');
  const [gameTime, setGameTime] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [gameOpponent, setGameOpponent] = useState('');
  const [gameVenue, setGameVenue] = useState('');
  const [askMealPreference, setAskMealPreference] = useState(false);
  const [homeTeam, setHomeTeam] = useState('');
  const [awayTeam, setAwayTeam] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedTournament, setSelectedTournament] = useState('');
  const [playerEmail, setPlayerEmail] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [selectedGameForPlayers, setSelectedGameForPlayers] = useState(null);
  const [squadSelection, setSquadSelection] = useState([]);
  const [playing11, setPlaying11] = useState([]); // Added missing state for squad selection
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [unassignedPlayers, setUnassignedPlayers] = useState([]);

  // Edit states
  const [editingGame, setEditingGame] = useState(null);
  const [editingTournament, setEditingTournament] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [viewingTournamentDetail, setViewingTournamentDetail] = useState(null); // Tournament detail view
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEventForWhatsApp, setSelectedEventForWhatsApp] = useState(null);
  const [addEventInitialValues, setAddEventInitialValues] = useState(null);

  // Expense states
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseGameId, setExpenseGameId] = useState('');
  const [expenseReceipt, setExpenseReceipt] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [removeOldReceipt, setRemoveOldReceipt] = useState(false);

  // Admin's player info (for reference)
  const [adminPlayerInfo, setAdminPlayerInfo] = useState(null); // Store admin's player info (team, etc.)

  // WhatsApp messenger state
  const [selectedGameForReminder, setSelectedGameForReminder] = useState(null);
  const [selectedTeamForWhatsApp, setSelectedTeamForWhatsApp] = useState(null);

  // Add player panel expansion state
  const [expandedAddPlayerTeams, setExpandedAddPlayerTeams] = useState({});
  const [selectedPlayersToAssign, setSelectedPlayersToAssign] = useState({});
  const [isAssigningMulti, setIsAssigningMulti] = useState(false);
  const [teamPlayerSearchTerm, setTeamPlayerSearchTerm] = useState({});

  // Club Settings States
  // Moved to ClubSettings.jsx

  // Club counts state (teams, tournaments per club)
  const [clubCounts, setClubCounts] = useState({});
  const [viewingAvailability, setViewingAvailability] = useState(null); // Game whose availability is being viewed

  // Delete player from club state
  const [showDeletePlayerModal, setShowDeletePlayerModal] = useState(false);
  const [showDeletePlayerSection, setShowDeletePlayerSection] = useState(false); // Collapsible section
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deletingPlayer, setDeletingPlayer] = useState(false);

  // Manage Players View State and Logic
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [isPendingPlayersExpanded, setIsPendingPlayersExpanded] = useState(true);
  const [isUnassignedPlayersExpanded, setIsUnassignedPlayersExpanded] = useState(true);
  const [expandedTeamRosters, setExpandedTeamRosters] = useState({});
  const [managePlayersTab, setManagePlayersTab] = useState('current');
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [targetCopyClubId, setTargetCopyClubId] = useState('');
  const [customCopyClubCode, setCustomCopyClubCode] = useState('');
  const [expandedPlayerTeamAssign, setExpandedPlayerTeamAssign] = useState({});
  const [selectedTeamsForPlayer, setSelectedTeamsForPlayer] = useState({});
  const [isAssigningPlayer, setIsAssigningPlayer] = useState(false);

  // TEMPORARY CLEANUP SCRIPT
  useEffect(() => {
    const cleanUpCorruptData = async () => {
      try {
        console.log("Starting DB cleanup for 'Target Club'...");
        const usersRef = collection(db, 'users');
        const snap = await getDocs(usersRef);
        let cleaned = 0;
        
        for (const userDoc of snap.docs) {
          const data = userDoc.data();
          if (data.joinedClubs) {
            let isDirty = false;
            const newJoinedClubs = { ...data.joinedClubs };
            let newJoinedClubsIds = [...(data.joinedClubsIds || [])];
            
            Object.entries(data.joinedClubs).forEach(([k, v]) => {
              if (v.clubName === 'Target Club') {
                delete newJoinedClubs[k];
                newJoinedClubsIds = newJoinedClubsIds.filter(id => id !== k);
                isDirty = true;
              }
            });
            
            if (isDirty) {
              const updates = {
                joinedClubs: newJoinedClubs,
                joinedClubsIds: newJoinedClubsIds
              };
              
              if (data.clubInfo && data.clubInfo.clubName === 'Target Club') {
                 updates.clubInfo = deleteField();
              }
              
              await updateDoc(doc(db, 'users', userDoc.id), updates);
              cleaned++;
              console.log(`Cleaned up corrupted club for user ${userDoc.id}`);
            }
          }
        }
        if (cleaned > 0) {
           alert(`Database Cleanup Complete: Removed ${cleaned} corrupted "Target Club" instances.`);
        }
      } catch (e) {
         console.error("Cleanup error:", e);
      }
    };
    
    // Only run if admin is fully loaded
    if (user?.uid) {
      cleanUpCorruptData();
    }
  }, [user?.uid]);

  const handleSelectPlayerToCopy = (playerId) => {
    setSelectedPlayerIds(prev => 
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    );
  };

  const handleExecuteCopyPlayers = async () => {
    if (selectedPlayerIds.length === 0) {
      alert("Please select at least one player to copy.");
      return;
    }
    
    let destinationClubId = targetCopyClubId;
    let destClubName = 'Target Club';
    
    if (!destinationClubId && customCopyClubCode.trim()) {
      try {
        const codeQuery = query(collection(db, 'club'), where('code', '==', customCopyClubCode.trim()));
        const codeSnap = await getDocs(codeQuery);
        if (codeSnap.empty) {
          alert("Could not find a club with that code.");
          return;
        }
        destinationClubId = codeSnap.docs[0].id;
        destClubName = codeSnap.docs[0].data().name;
      } catch (err) {
        console.error("Error looking up club code:", err);
        alert("Failed to lookup club code.");
        return;
      }
    }

    if (!destinationClubId) {
      alert("Please select a destination club or enter a club code.");
      return;
    }
    
    if (destinationClubId === selectedClub.id) {
      alert("Cannot copy players to the currently active club.");
      return;
    }
    
    try {
      if (targetCopyClubId) {
        const destClubFromList = clubs.find(c => c.id === destinationClubId);
        if (destClubFromList) {
           destClubName = destClubFromList.name;
        } else {
           const destClubSnap = await getDoc(doc(db, 'club', destinationClubId));
           if (destClubSnap.exists()) {
             destClubName = destClubSnap.data().name;
           }
        }
      }

      let successfulCopies = 0;
      for (const playerId of selectedPlayerIds) {
        const userRef = doc(db, 'users', playerId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const joinedClubs = userData.joinedClubs || {};
          let joinedClubsIds = userData.joinedClubsIds || [];
          
          if (!joinedClubsIds.includes(destinationClubId)) {
            joinedClubs[destinationClubId] = {
              clubId: destinationClubId,
              clubName: destClubName,
              status: "approved",
              role: "player",
              joinedAt: new Date().toISOString()
            };
            
            joinedClubsIds.push(destinationClubId);
            
            await updateDoc(userRef, {
              joinedClubs: joinedClubs,
              joinedClubsIds: joinedClubsIds
            });
            successfulCopies++;
          }
        }
      }
      
      alert(`Success! Copied ${successfulCopies} players to the destination club.`);
      setSelectedPlayerIds([]);
      setTargetCopyClubId('');
      setCustomCopyClubCode('');
      setManagePlayersTab('current');
    } catch (e) {
      console.error(e);
      alert("Error copying players. Check console.");
    }
  };

  // ============ ROLE FUNCTIONS ============
  
  const isUserAdmin = () => {
    // Super admin override
    if (typeof isSuperAdmin === 'function' && isSuperAdmin()) return true;

    // First check explicit club-scoped role
    if (selectedClub?.id && user?.joinedClubs?.[selectedClub.id]) {
      const clubMembership = user.joinedClubs[selectedClub.id];
      if (clubMembership.role === 'admin' || clubMembership.role === 'super-admin') {
        return true;
      }
    }

    // Check new standard: club document admins array
    if (selectedClub?.admins && selectedClub.admins.includes(user?.uid)) {
      return true;
    }

    // Fallback to legacy checks ONLY IF this is their primary legacy club
    const legacyClubId = user?.clubId || user?.clubInfo?.clubId;
    if (!selectedClub?.id || selectedClub.id === legacyClubId) {
      const userRole = user?.clubInfo?.role || user?.role;
      return userRole === 'admin' || userRole === 'super-admin';
    }

    return false;
  };

  // Check if current user has treasurer role
  const isTreasurer = () => {
    // Check explicit club-scoped treasurers array on the club document
    if (selectedClub?.treasurers && selectedClub.treasurers.includes(user?.uid)) {
      return true;
    }

    // Check the explicitly selected club's role map in joinedClubs
    if (selectedClub?.id && user?.joinedClubs?.[selectedClub.id]) {
      const clubRole = user.joinedClubs[selectedClub.id].role;
      const clubAdditional = user.joinedClubs[selectedClub.id].additionalRoles || [];
      if (clubRole === 'treasurer' || clubAdditional.includes('treasurer')) {
        return true;
      }
    }

    // Check ALL joined clubs (handles case where clubInfo is scoped to a different club)
    if (user?.joinedClubs) {
      for (const clubEntry of Object.values(user.joinedClubs)) {
        if (clubEntry.role === 'treasurer' || (clubEntry.additionalRoles || []).includes('treasurer')) {
          return true;
        }
      }
    }

    // Fallback to legacy clubInfo structure.
    // Only trust clubInfo.additionalRoles when clubInfo.clubId matches the selected club
    // (or no club is selected), to avoid false positives across clubs.
    const clubInfoClubId = user?.clubInfo?.clubId || user?.clubId;
    const clubMatches = !selectedClub?.id || clubInfoClubId === selectedClub.id;
    if (clubMatches) {
      const userRole = user?.clubInfo?.role;
      const additionalRoles = user?.clubInfo?.additionalRoles || [];
      const allRoles = user?.clubInfo?.roles || [];
      if (userRole === 'treasurer' || additionalRoles.includes('treasurer') || allRoles.includes('treasurer')) {
        return true;
      }
    }

    return false;
  };

  const canManageFinances = () => {
    return isTreasurer();
  };

  const getRoleBadge = () => {
    const roles = [];
    
    // Prioritize club-scoped role arrays
    if (selectedClub?.admins?.includes(user?.uid)) {
      roles.push('Admin');
    }
    if (selectedClub?.treasurers?.includes(user?.uid)) {
      roles.push('Treasurer');
    }
    if (selectedClub?.auditors?.includes(user?.uid)) {
      roles.push('Auditor');
    }

    // Prioritize club-scoped user object role map as fallback
    const clubRole = selectedClub?.id && user?.joinedClubs?.[selectedClub.id] 
      ? user.joinedClubs[selectedClub.id].role 
      : user?.clubInfo?.role;
      
    const clubAdditional = selectedClub?.id && user?.joinedClubs?.[selectedClub.id]
      ? user.joinedClubs[selectedClub.id].additionalRoles || []
      : user?.clubInfo?.additionalRoles || [];

    if (clubRole === 'admin' && !roles.includes('Admin')) roles.push('Admin');
    if (clubRole === 'treasurer' && !roles.includes('Treasurer')) roles.push('Treasurer');
    if (clubRole === 'super-admin' || isSuperAdmin()) roles.push('Super Admin');

    if (clubAdditional.includes('treasurer') && !roles.includes('Treasurer')) {
      roles.push('Treasurer');
    }
    if (clubAdditional.includes('auditor') && !roles.includes('Auditor')) {
      roles.push('Auditor');
    }

    return roles.join(' & ');
  };
  // ============ END TREASURER ROLE FUNCTIONS ============

  // Pull-to-refresh handler
  // ============ PERMISSION LOGIC ============
  const calculatePermissions = useCallback(() => {
    // DEBUG LOGGING
    console.group('🔐 calculatePermissions Debug');
    console.log('User Object:', user);
    console.log('ClubInfo:', user?.clubInfo);
    console.log('Role:', user?.clubInfo?.role);

    // Explicitly check role matches rather than using loose OR logic to prevent string truthiness blocks
    const globalRole = user?.role;
    const localRole = user?.clubInfo?.role;
    const additionalRoles = user?.clubInfo?.additionalRoles || [];
    const allRoles = user?.clubInfo?.roles || [];

    // Safety check for isSuperAdmin function
    const isSuperAdminFn = typeof isSuperAdmin === 'function' ? isSuperAdmin() : false;
    const isSuperAdminUser = globalRole === 'super-admin' || localRole === 'super-admin' || isSuperAdminFn;

    // Is legacy club match?
    const legacyClubId = user?.clubId || user?.clubInfo?.clubId;
    const isLegacyClubMatch = !selectedClub?.id || selectedClub.id === legacyClubId;
    
    // Explicit array checks from Club document (new standard)
    const isClubAdminArray = selectedClub?.admins?.includes(user?.uid) || false;
    const isClubTreasurerArray = selectedClub?.treasurers?.includes(user?.uid) || false;
    const isClubAuditorArray = selectedClub?.auditors?.includes(user?.uid) || false;

    // Evaluate dynamic Multi-Club map fallback
    const clubMapRole = selectedClub?.id && user?.joinedClubs?.[selectedClub.id]?.role;
    const clubMapAdditional = selectedClub?.id ? (user?.joinedClubs?.[selectedClub.id]?.additionalRoles || []) : [];

    const isAdminUser = 
      isSuperAdminUser ||
      isClubAdminArray || 
      clubMapRole === 'admin' || 
      (isLegacyClubMatch && (globalRole === 'admin' || localRole === 'admin'));

    const isTreasurerUser = 
      isClubTreasurerArray || 
      clubMapRole === 'treasurer' || 
      clubMapAdditional.includes('treasurer') ||
      (isLegacyClubMatch && (globalRole === 'treasurer' || localRole === 'treasurer' || additionalRoles.includes('treasurer') || allRoles.includes('treasurer')));

    const isAuditor = 
      isClubAuditorArray || 
      clubMapAdditional.includes('auditor') ||
      (isLegacyClubMatch && (additionalRoles.includes('auditor') || allRoles.includes('auditor')));

    // Check if user is captain or vice-captain in ANY team
    // We safeguard against teams being null/undefined
    const isCaptainOrVice = (teams || []).some(team => {
      const captains = team.captains || [];
      const viceCaptains = team.viceCaptains || [];
      const userId = user.uid;
      // Check if user ID is in these arrays. 
      return (Array.isArray(captains) && captains.includes(userId)) ||
        (Array.isArray(viceCaptains) && viceCaptains.includes(userId));
    });

    console.log('Permissions breakdown:', {
      isAdminUser,
      isTreasurerUser,
      isAuditor,
      isCaptainOrVice,
      teamsCount: teams?.length
    });
    console.groupEnd();

    return {
      canManageClub: isAdminUser, // Only admins can manage club settings
      canManageFinancials: (isAdminUser && !isSuperAdminUser) || isTreasurerUser, // Admins (not super) and treasurers
      canViewFinancials: (isAdminUser && !isSuperAdminUser) || isTreasurerUser || isAuditor, // Auditors can also view
      canManageTeams: (isAdminUser && !isSuperAdminUser) || isCaptainOrVice, // Captains can manage their teams
      canManageEvents: (isAdminUser && !isSuperAdminUser) || isCaptainOrVice, // Captains can create events
      isSuperAdmin: isSuperAdminUser,
      isAdmin: isAdminUser,
      isTreasurer: isTreasurerUser,
      isCaptain: isCaptainOrVice,
      isAuditor: isAuditor
    };
  }, [user, teams, isSuperAdmin]);

  const permissions = calculatePermissions();

  // Search Logic for Manage Players
  useEffect(() => {
    // Auto-expand sections when searching
    if (playerSearchTerm) {
      setIsPendingPlayersExpanded(true);
      setIsUnassignedPlayersExpanded(true);
      // Expand all teams that match search (by name or having matching players)
      const newExpanded = {};
      teams.forEach(t => newExpanded[t.id] = true);
      setExpandedTeamRosters(newExpanded);
    } else {
      // Default state when clearing search -> collapse all teams
      setExpandedTeamRosters({});
    }
  }, [playerSearchTerm, teams]);

  // Derived filtered lists
  const filteredPendingPlayers = useMemo(() => {
    let result = pendingPlayers;
    if (playerSearchTerm) {
      const lowerTerm = playerSearchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(lowerTerm) ||
        p.email.toLowerCase().includes(lowerTerm)
      );
    }
    return [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [pendingPlayers, playerSearchTerm]);

  const filteredUnassignedPlayers = useMemo(() => {
    let result = unassignedPlayers;
    if (playerSearchTerm) {
      const lowerTerm = playerSearchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(lowerTerm) ||
        p.email.toLowerCase().includes(lowerTerm)
      );
    }
    return [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [unassignedPlayers, playerSearchTerm]);

  const filteredTeamsForManagePlayers = useMemo(() => {
    if (!playerSearchTerm) return teams;
    const lowerTerm = playerSearchTerm.toLowerCase();
    return teams.filter(t =>
      t.name.toLowerCase().includes(lowerTerm) ||
      (t.players && t.players.some(p => p.name.toLowerCase().includes(lowerTerm)))
    );
  }, [teams, playerSearchTerm]);

  // Helper to check registration/payment status
  const getRegistrationStatusBadge = (player) => {
    // 1. Signed-up: No registration data
    if (!player.registrationStatus) {
      return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">Signed-up</span>;
    }

    // 2. Awaiting Registration: Submitted but not approved
    if (player.registrationStatus === 'pending') {
      return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">Awaiting Registration</span>;
    }

    // 3. Registered: Approved (Check balance)
    if (player.registrationStatus === 'approved') {
      // Calculate balance on the fly
      let totalOwed = 0;
      expenses.forEach(expense => {
        if (expense.splitAmong?.includes(player.id)) {
          totalOwed += (expense.amount / expense.splitAmong.length);
        }
      });

      let totalPaid = 0;
      walletTransactions.forEach(t => {
        if (t.userId === player.id && t.status === 'approved' && (t.type === 'payment' || t.type === 'waiver')) {
          // Payments are negative in wallet transactions usually? 
          // Wait, usually payments ADD credits (positive) or deduct debt?
          // In PaymentManagement: t.amount < 0 check implies payments are negative?
          // Let's check PaymentManagement logic:
          // const playerPaymentsList = walletTransactions.filter(t => ... t.amount < 0 ...)
          // totalPaid = playerPaymentsList.reduce((sum, t) => sum + Math.abs(t.amount), 0);
          // So yes, payments are negative.
          if (t.amount < 0) {
            totalPaid += Math.abs(t.amount);
          }
        }
      });

      const balance = totalOwed - totalPaid;

      if (balance <= 0.01) { // Paid
        return <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">Registered Approved</span>;
      } else { // Unpaid
        return <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Registered Pending</span>;
      }
    }

    return null;
  };

  const handlePullToRefresh = useCallback(async () => {
    console.log('🔄 Pull-to-refresh triggered for AdminDashboard');

    try {
      if (selectedClub) {
        // Reload all club data
        await loadClubData();
        console.log('✅ Club data refreshed');
      } else if (clubs.length > 0) {
        // Reload club counts for clubs list view
        await loadClubCounts();
        console.log('✅ Club counts refreshed');
      }
    } catch (error) {
      console.error('❌ Refresh failed:', error);
    }
    return Promise.resolve();
  }, [selectedClub, clubs]);

  // Load club counts when clubs are loaded
  useEffect(() => {
    if (clubs.length > 0) {
      loadClubCounts();
    }
  }, [clubs]);

  // NEW: Don't auto-switch to create-club view
  // Store unsubscribe functions for real-time listeners
  const [unsubscribers, setUnsubscribers] = useState([]);

  useEffect(() => {
    console.log('🔍 useEffect triggered - selectedClub:', selectedClub?.name);
    console.log('🔍 selectedClub ID:', selectedClub?.id);

    if (selectedClub) {
      console.log('✅ selectedClub exists, calling loadClubData for:', selectedClub.name);
      loadClubData();
    } else {
      console.log('⚠️ selectedClub is null/undefined');
      // Clean up listeners when club is deselected
      unsubscribers.forEach(unsub => unsub && unsub());
      setUnsubscribers([]);
    }

    // Cleanup on unmount or when selectedClub changes
    return () => {
      console.log('🧹 Cleanup: selectedClub changed or component unmounting');
      unsubscribers.forEach(unsub => unsub && unsub());
    };
  }, [selectedClub]);

  // Auto-select club for regular admins (non-super-admins) and skip clubs-list
  useEffect(() => {
    if (!loading && !isSuperAdmin()) {
      // Regular admin - auto select their club
      if (clubs.length > 0 && !selectedClub) {
        console.log('🔵 Auto-selecting club for regular admin');
        setSelectedClub(clubs[0]);
      }
      // Always redirect regular admins away from clubs-list
      if (view === 'clubs-list') {
        console.log('🔵 Redirecting regular admin to overview');
        setView('overview');
      }
    }
    // Super admin without URL view should go to clubs-list
    if (!loading && isSuperAdmin() && !searchParams.get('view')) {
      setView('clubs-list');
    }
  }, [loading, clubs, selectedClub, view, isSuperAdmin, searchParams]);



  // Prevent super admins from accessing club overview
  useEffect(() => {
    if (isSuperAdmin() && (view === 'overview' || view === 'club-detail' || view === 'whatsapp-messenger')) {
      console.log('🔵 Super admin tried to access club view, redirecting to clubs-list');
      setView('clubs-list');
      setSelectedClub(null);
    }
  }, [view, isSuperAdmin]);

  // Load ALL players when entering all-players view (for super admin)
  useEffect(() => {
    if (view === 'all-players') {
      console.log('🔵 Entering all-players view, loading ALL players');
      loadPlayers(null); // Explicitly pass null to load all players
    }
  }, [view]);

  // Load pending players for club admin when viewing overview
  useEffect(() => {
    const loadPendingPlayers = async () => {
      if (!selectedClub || !db || view !== 'overview') return;

      console.log('🔍 Loading pending players for overview, club:', selectedClub.id);

      try {
        const usersRef = collection(db, 'users');
        const qLegacy = query(
          usersRef,
          where('clubInfo.clubId', '==', selectedClub.id),
          where('clubInfo.status', '==', 'pending')
        );
        const qNew = query(
          usersRef,
          where('joinedClubsIds', 'array-contains', selectedClub.id)
        );

        const [snap1, snap2] = await Promise.all([getDocs(qLegacy), getDocs(qNew)]);
        const pending = [];
        const seenIds = new Set();

        const processUserDoc = (doc) => {
          if (seenIds.has(doc.id)) return;
          seenIds.add(doc.id);

          const data = doc.data();
          const clubInfo = data.joinedClubs?.[selectedClub.id] || data.clubInfo;

          if (data.role !== 'super-admin' && clubInfo?.status === 'pending') {
            pending.push({
              id: doc.id,
              name: data.name || data.displayName || 'Unknown',
              email: data.email,
              clubId: clubInfo?.clubId,
              clubName: clubInfo?.clubName,
              clubStatus: clubInfo?.status,
              role: clubInfo?.role || data.role,
              createdAt: data.createdAt,
              joinedAt: clubInfo?.joinedAt
            });
          }
        };

        snap1.forEach(processUserDoc);
        snap2.forEach(processUserDoc);

        console.log('✅ Found', pending.length, 'pending players for overview');
        setPendingPlayers(pending);
      } catch (error) {
        console.error('❌ Error loading pending players:', error);
      }
    };

    loadPendingPlayers();
  }, [selectedClub, view, db]);

  // Helper function to get initial donation based on ball type from club settings
  const getInitialDonation = (ballType) => {
    const fees = selectedClub?.initialDonationAmounts || {};
    if (ballType === 'tennis') {
      return fees.tennis ?? 125; // Default $125 for tennis
    }
    return fees.leather ?? 250; // Default $250 for leather (or both)
  };

  // Handle importing games from screenshot
  const handleImportGames = async (newGames, tournamentId = null) => {
    if (!newGames || newGames.length === 0) return;

    const targetTournament = tournamentId
      ? tournaments.find(t => t.id === tournamentId)
      : selectedTournament;

    try {
      console.log('📥 Importing', newGames.length, 'games for tournament:', targetTournament?.name);

      // Generate promises for all game creations
      const createPromises = newGames.map(game => {
        const gameData = {
          name: `${game.homeTeam} vs ${game.awayTeam}`,
          date: game.date,
          time: game.time || null,
          arrivalTime: null,
          venue: game.venue || null,
          tournamentId: targetTournament?.id || null,
          tournamentName: targetTournament?.name || null,
          homeTeam: game.homeTeam,
          awayTeam: game.awayTeam,
          homeTeamId: game.homeTeamId || null,
          awayTeamId: game.awayTeamId || null,
          opponent: game.awayTeam,
          clubId: selectedClub.id,
          playing11: [],
          createdAt: new Date().toISOString(),
          askMealPreference: false,
          // Inherit tournament settings
          maxSquadSize: targetTournament?.maxSquadSize || 11,
          allowSuperSubs: targetTournament?.allowSuperSubs || false,
          ballType: targetTournament?.ballType || 'leather',
          numberOfOvers: targetTournament?.numberOfOvers ? Number(targetTournament.numberOfOvers) : null,
          matchDuration: targetTournament?.matchDuration || ''
        };

        return addDoc(collection(db, 'games'), gameData);
      });

      await Promise.all(createPromises);

      loadGames();
      // Wait a bit for games to reload then notify
      setTimeout(() => alert(`Successfully imported ${newGames.length} games!`), 500);

    } catch (error) {
      console.error('Error importing games:', error);
      alert('Failed to import games. Check console for details.');
    }
  };

  const loadClubData = async () => {
    console.log('🚀 loadClubData started');

    try {
      // Clean up previous listeners
      unsubscribers.forEach(unsub => unsub && unsub());

      // Set up real-time listeners for teams, expenses, wallet transactions, payment submissions, and tournaments
      console.log('🔍 Setting up real-time listeners...');
      const teamsUnsub = loadTeams(true);
      const expensesUnsub = loadExpenses(true);
      const walletUnsub = loadWalletTransactions(true);
      const paymentSubsUnsub = loadPaymentSubmissions(true);
      const tournamentsUnsub = loadTournaments(true);

      // Store unsubscribe functions
      setUnsubscribers([teamsUnsub, expensesUnsub, walletUnsub, paymentSubsUnsub, tournamentsUnsub].filter(Boolean));
      console.log('✅ Real-time listeners set up');
    } catch (error) {
      console.error('❌ Error setting up real-time listeners:', error);
    }

    // Then load everything else in parallel (one-time fetches)
    try {
      console.log('🔍 About to load games, players, events...');
      const fetches = [
        loadGames(),
        loadEvents()
      ];
      // Don't overwrite the all-players list when loadClubData runs for a selected club
      if (view !== 'all-players') {
        fetches.push(loadPlayers());
      }
      await Promise.all(fetches);
      console.log('✅ loadClubData completed successfully');
    } catch (error) {
      console.error('❌ Error in loadClubData Promise.all:', error);
    }
  };

  const loadClubCounts = async () => {
    try {
      const counts = {};

      // Filter out any undefined clubs before processing
      const validClubs = clubs.filter(club => club && club.id);

      // Load counts for each club
      for (const club of validClubs) {
        // Count teams
        const teamsRef = collection(db, 'teams');
        const teamsQuery = query(teamsRef, where('clubId', '==', club.id));
        const teamsSnapshot = await getDocs(teamsQuery);

        // Count tournaments
        const tournamentsRef = collection(db, 'tournaments');
        const tournamentsQuery = query(tournamentsRef, where('clubId', '==', club.id));
        const tournamentsSnapshot = await getDocs(tournamentsQuery);

        counts[club.id] = {
          teams: teamsSnapshot.size,
          tournaments: tournamentsSnapshot.size
        };
      }

      setClubCounts(counts);
    } catch (error) {
      console.error('Error loading club counts:', error);
    }
  };

  const loadUnassignedPlayers = async (currentTeams = null) => {
    if (!selectedClub) return;

    try {
      console.log('🔵 Loading unassigned players for club:', selectedClub.id);

      // Get all users who registered with this club (including admins who might also play)
      const usersRef = collection(db, 'users');
      const qLegacy = query(
        usersRef,
        where('clubInfo.clubId', '==', selectedClub.id)
      );
      const qNew = query(
        usersRef,
        where('joinedClubsIds', 'array-contains', selectedClub.id)
      );

      const [snap1, snap2] = await Promise.all([getDocs(qLegacy), getDocs(qNew)]);

      const allClubPlayers = [];
      const seenIds = new Set();

      const processUserDoc = (doc) => {
        if (seenIds.has(doc.id)) return;
        seenIds.add(doc.id);

        const data = doc.data();
        const clubInfo = data.joinedClubs?.[selectedClub.id] || data.clubInfo;

        // Only include APPROVED players (exclude super-admins)
        if (data.role !== 'super-admin' && clubInfo?.status === 'approved') {
          allClubPlayers.push({
            id: doc.id,
            userId: doc.id,
            ...data,
            clubInfo: clubInfo
          });
        }
      };

      snap1.forEach(processUserDoc);
      snap2.forEach(processUserDoc);

      // For Cascadia Willow Club clubs, check registration form completion for each player
      const isCWCClub = selectedClub?.name && (
        selectedClub.name === 'Cascadia Willow Club' ||
        selectedClub.name === 'CWC'
      );
      if (isCWCClub && allClubPlayers.length > 0) {
        console.log('🔵 Cascadia Willow Club - Checking registration form status for all players');

        // Check registration forms for all players in parallel
        const regFormChecks = allClubPlayers.map(async (player) => {
          try {
            const regFormRef = doc(db, 'registrationForms', player.id);
            const regFormSnap = await getDoc(regFormRef);
            const data = regFormSnap.data();
            return {
              playerId: player.id,
              hasRegistrationForm: regFormSnap.exists(),
              formats: data?.formats || null
            };
          } catch (err) {
            // Permission errors are expected for some users/admins due to strict rules
            // Log as warning only if it's NOT a permission error
            if (err.code !== 'permission-denied') {
              console.warn('Warning checking registration form for:', player.id, err.code);
            }
            // Fallback: Assume form exists to avoid blocking UI features
            return { playerId: player.id, hasRegistrationForm: true, formats: null };
          }
        });

        const regFormResults = await Promise.all(regFormChecks);
        const regFormMap = {};
        const regFormatsMap = {};

        regFormResults.forEach(r => {
          regFormMap[r.playerId] = r.hasRegistrationForm;
          regFormatsMap[r.playerId] = r.formats;
        });

        console.log('🔍 Debug: Formats map:', regFormatsMap);

        // Add registration form status and formats to each player
        allClubPlayers.forEach(player => {
          player.hasRegistrationForm = regFormMap[player.id] ?? true;
          player.formats = regFormatsMap[player.id] || null;
          if (player.formats) console.log(`✅ Player ${player.name} has formats:`, player.formats);
          else console.log(`⚠️ Player ${player.name} has NO formats`);
        });
      }

      // Use passed teams or state teams
      const teamsToCheck = currentTeams || teams;

      // Get all players already assigned to teams
      const assignedPlayerIds = new Set();
      teamsToCheck.forEach(team => {
        team.players?.forEach(player => {
          assignedPlayerIds.add(player.userId || player.id);
        });
      });

      // Filter to only unassigned players
      const unassigned = allClubPlayers.filter(p => !assignedPlayerIds.has(p.id));

      console.log('✅ Found', unassigned.length, 'unassigned players');
      setUnassignedPlayers(unassigned);
    } catch (error) {
      console.error('❌ Error loading unassigned players:', error);
      setUnassignedPlayers([]);
    }
  };

  const loadGames = async () => {
    try {
      console.log('🔵 Loading games for club:', selectedClub.id);

      const gamesRef = collection(db, 'games');
      const q = query(gamesRef, where('clubId', '==', selectedClub.id));
      const snapshot = await getDocs(q);

      const gamesData = [];
      snapshot.forEach((doc) => {
        gamesData.push({ id: doc.id, ...doc.data() });
      });

      // Sort by date (ascending)
      gamesData.sort((a, b) => new Date(a.date) - new Date(b.date));

      console.log('✅ Found', gamesData.length, 'games');
      setGames(gamesData);
    } catch (err) {
      console.error('❌ Error loading games:', err);
      setGames([]);
    }
  };

  // Real-time listener for expenses (also works for manual refresh)
  const loadExpenses = (setupListener = false) => {
    if (!selectedClub?.id) return;

    const expensesRef = collection(db, 'expenses');
    const q = query(expensesRef, where('clubId', '==', selectedClub.id));

    if (setupListener) {
      console.log('🔵 Setting up real-time expenses listener for club:', selectedClub.id);
      // Return unsubscribe function for cleanup
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const expensesData = [];
        snapshot.forEach((doc) => {
          expensesData.push({ id: doc.id, ...doc.data() });
        });
        console.log('✅ Expenses updated (real-time):', expensesData.length);
        setExpenses(expensesData);
      }, (err) => {
        console.error('❌ Error in expenses listener:', err);
        setExpenses([]);
      });
      return unsubscribe;
    } else {
      // One-time fetch (for backwards compatibility, though listener will update automatically)
      console.log('🔵 Manual expenses refresh triggered');
    }
  };

  // Real-time listener for wallet transactions (also works for manual refresh)
  const loadWalletTransactions = (setupListener = false) => {
    if (!selectedClub?.id) return;

    const walletRef = collection(db, 'walletTransactions');
    const q = query(walletRef, where('clubId', '==', selectedClub.id));

    if (setupListener) {
      console.log('🔵 Setting up real-time wallet transactions listener for club:', selectedClub.id);
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const transactionsData = [];
        snapshot.forEach((doc) => {
          transactionsData.push({ id: doc.id, ...doc.data() });
        });
        console.log('✅ Wallet transactions updated (real-time):', transactionsData.length);
        setWalletTransactions(transactionsData);
      }, (err) => {
        console.error('❌ Error in wallet transactions listener:', err);
        setWalletTransactions([]);
      });
      return unsubscribe;
    } else {
      // One-time fetch (for backwards compatibility, though listener will update automatically)
      console.log('🔵 Manual wallet transactions refresh triggered');
    }
  };

  const loadPaymentSubmissions = (setupListener = false) => {
    if (!selectedClub?.id) return;

    const submissionsRef = collection(db, 'paymentSubmissions');
    const q = query(submissionsRef, where('clubId', '==', selectedClub.id));

    const processSubmissions = (snapshot) => {
      const submissionsData = [];
      snapshot.forEach((doc) => {
        submissionsData.push({ id: doc.id, ...doc.data() });
      });
      console.log('✅ Payment submissions updated:', submissionsData.length, setupListener ? '(real-time)' : '');
      setPaymentSubmissions(submissionsData);
    };

    if (setupListener) {
      console.log('🔵 Setting up real-time payment submissions listener');
      return onSnapshot(q, processSubmissions, (err) => {
        console.error('❌ Error in payment submissions listener:', err);
        setPaymentSubmissions([]);
      });
    } else {
      getDocs(q).then(processSubmissions).catch((err) => {
        console.error('❌ Error loading payment submissions:', err);
        setPaymentSubmissions([]);
      });
    }
  };

  const loadPayments = async () => {
    // Placeholder - payments are tracked via wallet transactions
    // This function exists for compatibility
    console.log('🔵 loadPayments called - using wallet transactions for payment tracking');
  };

  const loadTeams = (setupListener = false) => {
    if (!selectedClub) return null;

    const teamsRef = collection(db, 'teams');
    const q = query(teamsRef, where('clubId', '==', selectedClub.id));

    // Helper function to check if a player ID looks like a Firebase UID
    // Firebase UIDs are typically 28 characters, alphanumeric
    // Manual/external player IDs start with 'player_' or 'guest-'
    const isFirebaseUid = (id) => {
      if (!id) return false;
      // Skip validation for manual/external players
      if (id.startsWith('player_') || id.startsWith('guest-') || id.startsWith('guest_')) {
        return false;
      }
      // Firebase UIDs are typically 20-28 characters, alphanumeric
      return /^[a-zA-Z0-9]{20,28}$/.test(id);
    };

    const processTeams = async (snapshot) => {
      const teamsData = [];
      const allPlayerUserIds = new Set();

      // First pass: collect all player userIds that need validation (Firebase UIDs only)
      snapshot.forEach((docSnap) => {
        const teamData = docSnap.data();
        if (teamData.players && teamData.players.length > 0) {
          teamData.players.forEach(p => {
            const userId = typeof p === 'string' ? p : (p.userId || p.id);
            // Only validate Firebase UIDs, not manual/external player IDs
            if (userId && isFirebaseUid(userId)) {
              allPlayerUserIds.add(userId);
            }
          });
        }
      });

      // Validate which users still exist in Firebase and get their data
      const validUsersMap = new Map();
      if (allPlayerUserIds.size > 0) {
        const userIdsArray = Array.from(allPlayerUserIds);
        // Query users in batches of 10 (Firestore 'in' query limit)
        for (let i = 0; i < userIdsArray.length; i += 10) {
          const batch = userIdsArray.slice(i, i + 10);
          try {
            const usersRef = collection(db, 'users');
            const usersQuery = query(usersRef, where('__name__', 'in', batch));
            const usersSnapshot = await getDocs(usersQuery);
            usersSnapshot.forEach(doc => {
              validUsersMap.set(doc.id, doc.data());
            });
          } catch (err) {
            console.error('Error validating users batch:', err);
            // If validation fails, assume all are valid with empty data to avoid breaking the UI
            batch.forEach(id => validUsersMap.set(id, {}));
          }
        }
      }

      // Second pass: build teams with only valid players
      snapshot.forEach((docSnap) => {
        const teamData = docSnap.data();
        const team = { id: docSnap.id, ...teamData };

        if (teamData.players && teamData.players.length > 0) {
          const seenUserIds = new Set();
          const validPlayers = [];

          teamData.players.forEach(p => {
            const userId = typeof p === 'string' ? p : (p.userId || p.id);
            const isManualOrExternal = !isFirebaseUid(userId) || p.isExternal || p.isManual || p.isGuest;

            // Include if:
            // 1. It's a manual/external player (skip validation), OR
            // 2. It's a Firebase user that exists in the users collection
            // AND not a duplicate
            if (userId && !seenUserIds.has(userId)) {
              if (isManualOrExternal || validUsersMap.has(userId)) {
                seenUserIds.add(userId);
                if (typeof p === 'string') {
                  const userData = validUsersMap.get(userId) || {};
                  validPlayers.push({
                    id: userId,
                    userId: userId,
                    name: userData.name || userData.displayName || 'Unknown Player',
                    email: userData.email || '',
                    joinedAt: teamData.createdAt || new Date().toISOString()
                  });
                } else {
                  if (!p.name && !p.email) {
                    const userData = validUsersMap.get(userId);
                    if (userData) {
                      validPlayers.push({
                        ...p,
                        name: p.name || userData.name || userData.displayName || 'Unknown Player',
                        email: p.email || userData.email || ''
                      });
                    } else {
                      validPlayers.push(p);
                    }
                  } else {
                    validPlayers.push(p);
                  }
                }
              } else {
                console.log('⚠️ Removed deleted user from team display:', p.name || p.email || p);
              }
            } else if (seenUserIds.has(userId)) {
              console.log('⚠️ Duplicate player found:', p.name || p, 'in team:', teamData.name);
            }
          });

          team.players = validPlayers;
        }

        teamsData.push(team);
      });

      console.log('✅ Teams updated:', teamsData.length, setupListener ? '(real-time)' : '');
      setTeams(teamsData);

      // Reload unassigned players after loading teams, passing the fresh data
      loadUnassignedPlayers(teamsData);
    };

    if (setupListener) {
      console.log('🔵 Setting up real-time teams listener');
      return onSnapshot(q, processTeams, (error) => {
        console.error('❌ Error in teams listener:', error);
        setTeams([]);
      });
    } else {
      console.log('🔵 Loading teams (one-time)');
      getDocs(q).then(processTeams).catch((error) => {
        console.error('❌ Error loading teams:', error);
        setTeams([]);
      });
      return null;
    }
  };

  const loadTournaments = (setupListener = false) => {
    if (!selectedClub?.id) return;

    try {
      const tournamentsRef = collection(db, 'tournaments');
      const q = query(tournamentsRef, where('clubId', '==', selectedClub.id));

      if (setupListener) {
        console.log('🔵 Setting up real-time tournaments listener for club:', selectedClub.id);
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const tournamentsData = [];
          snapshot.forEach((doc) => {
            tournamentsData.push({ id: doc.id, ...doc.data() });
          });
          console.log('✅ Tournaments updated (real-time):', tournamentsData.length);
          setTournaments(tournamentsData);
        }, (err) => {
          console.error('❌ Error in tournaments listener:', err);
          setTournaments([]);
        });
        return unsubscribe;
      } else {
        console.log('🔵 Manual tournaments refresh triggered');
        getDocs(q).then((snapshot) => {
          const tournamentsData = [];
          snapshot.forEach((doc) => {
            tournamentsData.push({ id: doc.id, ...doc.data() });
          });
          console.log('✅ Found', tournamentsData.length, 'tournaments');
          setTournaments(tournamentsData);
        }).catch((err) => {
          console.error('❌ Error loading tournaments:', err);
          setTournaments([]);
        });
      }
    } catch (err) {
      console.error('❌ Error in loadTournaments:', err);
      setTournaments([]);
    }
  };

  const loadEvents = async () => {
    if (!selectedClub) return;

    try {
      console.log('🔵 Loading events for club:', selectedClub.id);

      const eventsRef = collection(db, 'events');
      const q = query(eventsRef, where('clubId', '==', selectedClub.id));
      const snapshot = await getDocs(q);

      const eventsData = [];
      snapshot.forEach((doc) => {
        eventsData.push({ id: doc.id, ...doc.data() });
      });

      console.log('✅ Found', eventsData.length, 'events');
      setEvents(eventsData);
    } catch (err) {
      console.error('❌ Error loading events:', err);
      setEvents([]);
    }
  };

  const loadPlayers = async (clubIdFilter) => {
    try {
      console.log('🔍 loadPlayers called with clubIdFilter:', clubIdFilter);
      console.log('🔍 selectedClub:', selectedClub);
      console.log('🔍 db exists:', !!db);

      // Safety check: ensure db is initialized
      if (!db) {
        console.error('❌ loadPlayers: db not initialized');
        setPlayers([]);
        setPendingPlayers([]);
        return;
      }

      // IMPORTANT: Only use selectedClub.id if clubIdFilter is undefined (not null)
      // clubIdFilter === null means "load ALL players" (super admin view)
      // clubIdFilter === undefined means "use selectedClub.id"
      const clubId = clubIdFilter === undefined ? selectedClub?.id : clubIdFilter;
      console.log('🔍 Resolved clubId:', clubId);

      // If clubIdFilter is undefined (default) and selectedClub is not set, don't load yet
      if (clubIdFilter === undefined && !selectedClub) {
        console.log('⚠️ Waiting for selectedClub to be set, skipping load');
        return;
      }

      if (clubId) {
        console.log('🔵 Loading players for club:', clubId);

        // Query Firestore for players in specific club
        const usersRef = collection(db, 'users');
        const qLegacy = query(usersRef, where('clubInfo.clubId', '==', clubId));
        const qNew = query(usersRef, where('joinedClubsIds', 'array-contains', clubId));

        const [snap1, snap2] = await Promise.all([getDocs(qLegacy), getDocs(qNew)]);

        const playersData = [];
        const pendingPlayersData = [];
        const seenIds = new Set();

        const processUserDoc = (doc) => {
          if (seenIds.has(doc.id)) return;
          seenIds.add(doc.id);

          const data = doc.data();
          const clubInfo = data.joinedClubs?.[clubId] || data.clubInfo;
          
          // Include both players and admins explicitly
          if (true) {
            const playerObj = {
              id: doc.id,
              name: data.name || data.displayName || 'Unknown',
              email: data.email,
              clubId: clubInfo?.clubId || data.clubId,
              clubName: clubInfo?.clubName || data.clubName,
              clubStatus: clubInfo?.status || 'approved', // Default to approved for existing users
              teamId: data.teamId || null,
              role: clubInfo?.role || data.role,
              additionalRoles: clubInfo?.additionalRoles || data.additionalRoles || [],
              createdAt: data.createdAt,
              joinedAt: clubInfo?.joinedAt,
              registrationStatus: data.registrationStatus, // Add this field
              phone: data.phoneNumber || data.phone || null
            };

            // Separate pending vs approved players
            if (clubInfo?.status === 'pending') {
              pendingPlayersData.push(playerObj);
            } else {
              playersData.push(playerObj);
            }
          }
        };

        snap1.forEach(processUserDoc);
        snap2.forEach(processUserDoc);

        console.log('✅ Found', playersData.length, 'approved users,', pendingPlayersData.length, 'pending');
        setPlayers(playersData);
        setPendingPlayers(pendingPlayersData);
      } else {
        // Load ALL players and admins (for super admin viewing all users)
        console.log('🔵 Loading ALL users (players & admins) for super admin');
        console.log('🔍 About to query users collection...');

        const usersRef = collection(db, 'users');
        console.log('🔍 usersRef created:', !!usersRef);

        const snapshot = await getDocs(usersRef);
        console.log('🔍 Snapshot received, size:', snapshot.size);

        const allPlayersData = []; // Combine all players for super admin

        snapshot.forEach((doc) => {
          const data = doc.data();
          console.log('🔍 Processing user:', data.email, 'role:', data.role);

          // Include both players and admins (exclude super-admin)
          if (data.role !== 'super-admin') {
            const playerObj = {
              id: doc.id,
              name: data.name || data.displayName || 'Unknown',
              email: data.email,
              clubId: data.clubInfo?.clubId || data.clubId,
              clubName: data.clubInfo?.clubName || data.clubName || 'No Club',
              clubStatus: data.clubInfo?.status || 'no-club',
              teamId: data.teamId || null,
              role: data.role,
              additionalRoles: data.additionalRoles || [],
              createdAt: data.createdAt,
              joinedAt: data.clubInfo?.joinedAt,
              joinedClubs: data.joinedClubs || {},
              phone: data.phoneNumber || data.phone || null
            };

            console.log('🔍 Adding player to list:', playerObj.email);
            allPlayersData.push(playerObj);
          } else {
            console.log('🔍 Skipping super-admin:', data.email);
          }
        });

        console.log('✅ Found', allPlayersData.length, 'total users across all clubs');
        console.log('🔍 Setting players state with:', allPlayersData);
        setPlayers(allPlayersData); // Show ALL players for super admin
        setPendingPlayers([]); // No separate pending section for "All Players" view
      }
    } catch (error) {
      console.error('❌ Error loading players:', error);
      setPlayers([]);
      setPendingPlayers([]);
    }
  };

  // Approve a pending player
  const handleApprovePlayer = async (player) => {
    try {
      console.log('🔵 Approving player:', player.name);

      const userRef = doc(db, 'users', player.id);
      
      const updateData = {};
      if (selectedClub?.id) {
        updateData[`joinedClubs.${selectedClub.id}.status`] = 'approved';
        updateData[`joinedClubs.${selectedClub.id}.role`] = 'player';
      }
      
      // Always persist the legacy structure update if they are native to this club
      if (player.clubId === selectedClub?.id || !player.clubId) {
        updateData['clubInfo.status'] = 'approved';
        updateData['clubInfo.role'] = 'player';
      }

      // Explicitly set top-level role to player IF it's not super-admin, 
      // preventing any accidental inherited top-level admin privileges from misconfigurations.
      if (player.role !== 'super-admin' && player.role !== 'admin') {
        updateData.role = 'player';
      }

      await updateDoc(userRef, updateData);

      console.log('✅ Player approved:', player.name);
      alert(`✅ ${player.name} has been approved and can now access the dashboard!`);

      // Reload players to update lists
      await loadPlayers(selectedClub?.id);
      // Also reload unassigned players so they appear in "Waiting for Team Assignment"
      await loadUnassignedPlayers();
    } catch (error) {
      console.error('❌ Error approving player:', error);
      alert('Error approving player. Please try again.');
    }
  };

  // Reject a pending player
  const handleRejectPlayer = async (player) => {
    if (!confirm(`Are you sure you want to reject ${player.name}'s request to join?`)) {
      return;
    }

    try {
      console.log('🔵 Rejecting player:', player.name);

      const userRef = doc(db, 'users', player.id);
      
      const updateData = {};
      if (selectedClub?.id) {
        updateData[`joinedClubs.${selectedClub.id}.status`] = 'rejected';
        updateData[`joinedClubs.${selectedClub.id}.rejectedAt`] = new Date().toISOString();
      }
      
      // Persist the legacy structure update if they are native to this club
      if (player.clubId === selectedClub?.id || !player.clubId) {
        updateData['clubInfo.status'] = 'rejected';
        updateData['clubInfo.rejectedAt'] = new Date().toISOString();
      }

      await updateDoc(userRef, updateData);

      console.log('✅ Player rejected:', player.name);
      alert(`${player.name}'s request has been rejected.`);

      // Reload players to update lists
      await loadPlayers(selectedClub?.id);
      // Also reload unassigned players to remove them from that list
      await loadUnassignedPlayers();
    } catch (error) {
      console.error('❌ Error rejecting player:', error);
      alert('Error rejecting player. Please try again.');
    }
  };

  // ============ TREASURER ROLE ASSIGNMENT FUNCTIONS ============
  const handleAssignTreasurerRole = async (playerId, playerName) => {
    if (!confirm(`Assign treasurer role to ${playerName}? They will be able to approve/deny all expenses and payments.`)) {
      return;
    }

    try {
      const playerRef = doc(db, 'users', playerId);
      const playerDoc = await getDoc(playerRef);

      if (playerDoc.exists()) {
        const currentData = playerDoc.data();
        const clubId = selectedClub?.id;

        // Add treasurer to legacy clubInfo.additionalRoles
        const currentRoles = currentData.clubInfo?.additionalRoles || [];
        if (!currentRoles.includes('treasurer')) {
          currentRoles.push('treasurer');
        }

        const updates = {
          'clubInfo.additionalRoles': currentRoles
        };

        // Also write to joinedClubs[clubId].additionalRoles so club-scoped
        // checks in isTreasurer() work when the user views a different club
        if (clubId && currentData.joinedClubs?.[clubId]) {
          const joinedAdditional = currentData.joinedClubs[clubId].additionalRoles || [];
          if (!joinedAdditional.includes('treasurer')) {
            joinedAdditional.push('treasurer');
          }
          updates[`joinedClubs.${clubId}.additionalRoles`] = joinedAdditional;
        }

        await updateDoc(playerRef, updates);

        console.log(`✅ Treasurer role assigned to ${playerName}`);
        alert(`✅ ${playerName} has been assigned the treasurer role and can now approve/deny expenses!`);
        await loadPlayers(selectedClub?.id);
      }
    } catch (error) {
      console.error('Error assigning treasurer role:', error);
      alert('Failed to assign treasurer role. Please try again.');
    }
  };

  const handleRemoveTreasurerRole = async (playerId, playerName) => {
    if (!confirm(`Remove treasurer role from ${playerName}? They will no longer be able to approve/deny expenses.`)) {
      return;
    }

    try {
      const playerRef = doc(db, 'users', playerId);
      const playerDoc = await getDoc(playerRef);

      if (playerDoc.exists()) {
        const currentData = playerDoc.data();

        // Remove treasurer from additional roles
        const currentRoles = currentData.clubInfo?.additionalRoles || [];
        const updatedRoles = currentRoles.filter(role => role !== 'treasurer');

        await updateDoc(playerRef, {
          'clubInfo.additionalRoles': updatedRoles
        });

        console.log(`✅ Treasurer role removed from ${playerName}`);
        alert(`✅ Treasurer role removed from ${playerName} successfully!`);
        await loadPlayers(selectedClub?.id);
      }
    } catch (error) {
      console.error('Error removing treasurer role:', error);
      alert('Failed to remove treasurer role. Please try again.');
    }
  };
  // ============ END TREASURER ROLE ASSIGNMENT ============

  // Delete player completely from club - removes all data
  const handleDeletePlayerFromClub = async () => {
    if (!playerToDelete || deleteConfirmEmail.toLowerCase() !== playerToDelete.email?.toLowerCase()) {
      alert('Please type the correct email to confirm deletion.');
      return;
    }

    setDeletingPlayer(true);
    const playerId = playerToDelete.id || playerToDelete.userId;
    const playerEmail = playerToDelete.email?.toLowerCase();
    const playerName = playerToDelete.name;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🗑️ DELETING PLAYER FROM CLUB');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('Player ID:', playerId);
    console.log('Player Email:', playerEmail);
    console.log('Player Name:', playerName);

    try {
      // 1. Remove player from all teams
      console.log('📍 Step 1: Removing from teams...');
      for (const team of teams) {
        if (team.players?.some(p =>
          p.id === playerId ||
          p.userId === playerId ||
          p.email?.toLowerCase() === playerEmail
        )) {
          const updatedPlayers = team.players.filter(p =>
            p.id !== playerId &&
            p.userId !== playerId &&
            p.email?.toLowerCase() !== playerEmail
          );
          const teamRef = doc(db, 'teams', team.id);
          await updateDoc(teamRef, { players: updatedPlayers });
          console.log(`  ✅ Removed from team: ${team.name}`);
        }
      }

      // 2. Handle expenses - delete if solo, redistribute if shared
      console.log('📍 Step 2: Handling expenses...');
      const playerExpenses = expenses.filter(e =>
        e.splitAmong?.includes(playerId) ||
        e.splitAmong?.includes(playerEmail) ||
        e.playerEmail?.toLowerCase() === playerEmail ||
        e.submittedBy === playerId
      );

      for (const expense of playerExpenses) {
        const expenseRef = doc(db, 'expenses', expense.id);

        // Check if player is the only one in splitAmong
        const otherPlayers = expense.splitAmong?.filter(id =>
          id !== playerId && id !== playerEmail
        ) || [];

        if (otherPlayers.length === 0) {
          // Delete expense entirely
          await deleteDoc(expenseRef);
          console.log(`  ✅ Deleted expense: ${expense.description} ($${expense.amount})`);
        } else {
          // Redistribute to remaining players
          const newAmountPerPlayer = expense.amount / otherPlayers.length;
          await updateDoc(expenseRef, {
            splitAmong: otherPlayers,
            amountPerPlayer: newAmountPerPlayer,
            updatedAt: new Date().toISOString(),
            updateReason: `Player ${playerName} removed from club - amount redistributed`
          });
          console.log(`  ✅ Redistributed expense: ${expense.description} - new amount per player: $${newAmountPerPlayer.toFixed(2)}`);
        }
      }

      // 3. Delete wallet transactions
      console.log('📍 Step 3: Deleting wallet transactions...');
      const walletRef = collection(db, 'walletTransactions');
      const walletQuery = query(walletRef, where('clubId', '==', selectedClub.id));
      const walletSnapshot = await getDocs(walletQuery);

      for (const walletDoc of walletSnapshot.docs) {
        const wallet = walletDoc.data();
        if (wallet.playerId === playerId ||
          wallet.playerEmail?.toLowerCase() === playerEmail ||
          wallet.submittedBy === playerId) {
          await deleteDoc(doc(db, 'walletTransactions', walletDoc.id));
          console.log(`  ✅ Deleted wallet transaction: $${wallet.amount}`);
        }
      }

      // 4. Delete payment submissions
      console.log('📍 Step 4: Deleting payment submissions...');
      const paymentsRef = collection(db, 'paymentSubmissions');
      const paymentsQuery = query(paymentsRef, where('clubId', '==', selectedClub.id));
      const paymentsSnapshot = await getDocs(paymentsQuery);

      for (const paymentDoc of paymentsSnapshot.docs) {
        const payment = paymentDoc.data();
        if (payment.playerId === playerId ||
          payment.playerEmail?.toLowerCase() === playerEmail ||
          payment.submittedBy === playerId) {
          await deleteDoc(doc(db, 'paymentSubmissions', paymentDoc.id));
          console.log(`  ✅ Deleted payment submission: $${payment.amount}`);
        }
      }

      // 5. Delete registration form
      console.log('📍 Step 5: Deleting registration form...');
      try {
        const regFormRef = doc(db, 'registrationForms', playerId);
        const regFormSnap = await getDoc(regFormRef);
        if (regFormSnap.exists()) {
          await deleteDoc(regFormRef);
          console.log('  ✅ Deleted registration form');
        }
      } catch (regErr) {
        console.log('  ⚠️ No registration form found or error:', regErr.message);
      }

      // 6. Delete notifications
      console.log('📍 Step 6: Deleting notifications...');
      try {
        const notificationsRef = collection(db, 'notifications');
        const notifQuery = query(notificationsRef, where('userId', '==', playerId));
        const notifSnapshot = await getDocs(notifQuery);

        let deletedNotifs = 0;
        for (const notifDoc of notifSnapshot.docs) {
          try {
            await deleteDoc(doc(db, 'notifications', notifDoc.id));
            deletedNotifs++;
          } catch (notifDelErr) {
            console.log(`  ⚠️ Could not delete notification ${notifDoc.id}:`, notifDelErr.message);
          }
        }
        console.log(`  ✅ Deleted ${deletedNotifs} of ${notifSnapshot.size} notifications`);
      } catch (notifErr) {
        console.log('  ⚠️ Could not query/delete notifications:', notifErr.message);
      }

      // 7. Delete waiver agreements
      console.log('📍 Step 7: Deleting waiver agreements...');
      try {
        const waiverRef = doc(db, 'userWaiverAgreements', playerId);
        const waiverSnap = await getDoc(waiverRef);
        if (waiverSnap.exists()) {
          await deleteDoc(waiverRef);
          console.log('  ✅ Deleted waiver agreement');
        } else {
          console.log('  ℹ️ No waiver agreement found');
        }
      } catch (waiverErr) {
        console.log('  ⚠️ Could not delete waiver agreement:', waiverErr.message);
      }

      // 8. Delete user document from users collection
      console.log('📍 Step 8: Deleting user document...');
      try {
        const userRef = doc(db, 'users', playerId);
        await deleteDoc(userRef);
        console.log('  ✅ Deleted user document');
      } catch (userDelErr) {
        console.error('  ⚠️ Could not delete user document:', userDelErr.message);
        // Don't throw - continue with cleanup even if user doc deletion fails
        // The user is effectively removed since they're no longer in any team
      }

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ PLAYER DELETION COMPLETE');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      alert(`✅ ${playerName} has been removed from ${selectedClub.name}.\n\nMost associated data has been deleted. Check console for any skipped items.`);

      // Reset state and reload data
      setShowDeletePlayerModal(false);
      setPlayerToDelete(null);
      setDeleteConfirmEmail('');

      // Reload all relevant data
      await loadPlayers(selectedClub.id);
      await loadUnassignedPlayers();
      await loadTeams();
      loadExpenses(false);
      loadWalletTransactions(false);

    } catch (error) {
      console.error('❌ Error deleting player:', error);
      alert(`Error deleting player: ${error.message}\n\nSome data may have been partially deleted. Please check the console for details.`);
    } finally {
      setDeletingPlayer(false);
    }
  };

  const handleCopyCode = () => {
    if (selectedClub) {
      navigator.clipboard.writeText(selectedClub.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCreateClub = async (name, location) => {
    console.log('🔵 AdminDashboard: Creating club:', { name, location });

    const result = await createClub(name, location);
    console.log('🔵 AdminDashboard: createClub result:', result);

    if (result.success && result.club) {
      console.log('✅ AdminDashboard: Club created successfully:', result.club);

      // Navigate back to clubs list
      setView('clubs-list');
      setSelectedClub(result.club);

      return result.club;
    } else {
      console.error('❌ AdminDashboard: Club creation failed:', result.error);
      // Don't throw - let ClubCreation component handle the error
      throw new Error(result.error);
    }
  };

  // INITIAL DONATION HELPER FUNCTIONS
  // These functions ensure correct initial donation amounts based on ball types

  /**
   * Get all teams a player belongs to
   * @param {string} playerId - The player's ID
   * @returns {Array} Array of team objects the player is on
   */
  const getPlayerTeams = (playerId, playerEmail = null) => {
    return teams.filter(team =>
      team.players && team.players.some(p =>
        p.id === playerId ||
        p.userId === playerId ||
        (playerEmail && p.email?.toLowerCase() === playerEmail?.toLowerCase())
      )
    );
  };

  /**
   * Calculate the CORRECT initial donation for a player based on ball types they play
   * CRITICAL BUSINESS LOGIC:
   * - Leather ball (or both): Use club's leather fee setting
   * - Tennis ball only: Use club's tennis fee setting
   * 
   * @param {string} playerId - The player's ID
   * @param {string} playerEmail - The player's email (for better matching)
   * @returns {number} The correct initial donation amount from club settings
   */
  const calculateCorrectInitialDonation = (playerId, playerEmail = null) => {
    const playerTeams = getPlayerTeams(playerId, playerEmail);

    if (playerTeams.length === 0) {
      console.warn('⚠️ Player not on any teams, defaulting to leather fee');
      return getInitialDonation('leather'); // Default to higher amount if no teams
    }

    // Get all ball types this player plays
    const ballTypes = playerTeams.map(t => t.ballType || 'leather'); // Default to leather if not set
    const hasLeather = ballTypes.includes('leather');
    const hasTennis = ballTypes.includes('tennis');

    // CRITICAL: If player plays ANY leather ball, use leather fee
    // Only if player plays ONLY tennis ball, use tennis fee
    const correctFee = hasLeather ? getInitialDonation('leather') : getInitialDonation('tennis');

    console.log('💰 Fee calculation for player:', playerId);
    console.log('  - Teams:', playerTeams.map(t => t.name).join(', '));
    console.log('  - Ball types:', ballTypes.join(', '));
    console.log('  - Has leather:', hasLeather);
    console.log('  - Has tennis:', hasTennis);
    console.log('  - Correct fee: $', correctFee);

    return correctFee;
  };

  /**
   * Get initial donation expense for a player
   * Checks by BOTH player ID and email to handle Firebase UID vs team player ID mismatches
   * @param {string} playerId - The player's ID (team player ID)
   * @param {string} playerEmail - The player's email (optional but recommended)
   * @param {string} userId - The player's Firebase userId (optional)
   * @returns {Object|null} The initial donation expense or null if not found
   */
  const getPlayerInitialDonation = (playerId, playerEmail = null, userId = null) => {
    return expenses.find(e => {
      // Accept if it is explicitly an Initial Donation OR a Registration Fee (that isn't an Additional Donation)
      const isValidType = e.isInitialDonation || (e.isRegistrationFee && e.type !== 'Additional Donation');
      if (!isValidType) return false;

      if (!e.splitAmong || e.splitAmong.length === 0) return false;

      // Check if player ID is in splitAmong
      if (e.splitAmong.includes(playerId)) return true;

      // Check if userId is in splitAmong (for users who login)
      if (userId && e.splitAmong.includes(userId)) return true;

      // ALSO check by email if provided (handles Firebase UID vs team player ID mismatch)
      if (playerEmail) {
        // Check if the expense creator email matches player email
        if (e.createdBy && e.createdBy.toLowerCase() === playerEmail.toLowerCase()) {
          return true;
        }
        // Check if playerEmail is stored in the expense
        if (e.playerEmail && e.playerEmail.toLowerCase() === playerEmail.toLowerCase()) {
          return true;
        }
      }

      return false;
    });
  };

  /**
   * Validate all initial donations and find mismatches
   * @returns {Array} Array of validation issues
   */
  const validateAllRegistrationFees = () => {
    const issues = [];

    // Get all unique players across all teams
    const allPlayers = [];
    const playerIds = new Set();

    teams.forEach(team => {
      if (team.players) {
        team.players.forEach(player => {
          if (!playerIds.has(player.id)) {
            playerIds.add(player.id);
            allPlayers.push(player);
          }
        });
      }
    });

    console.log('🔍 Validating initial donations for', allPlayers.length, 'players');

    allPlayers.forEach(player => {
      const correctFee = calculateCorrectInitialDonation(player.id, player.email);
      const actualFeeExpense = getPlayerInitialDonation(player.id, player.email, player.userId); // ✅ Pass userId too
      const playerTeams = getPlayerTeams(player.id);

      const ballTypes = playerTeams.map(t => t.ballType || 'leather');
      const hasLeather = ballTypes.includes('leather');
      const hasTennis = ballTypes.includes('tennis');

      let issueType = null;
      let description = '';

      if (!actualFeeExpense) {
        // Missing initial donation
        issueType = 'missing';
        description = 'No initial donation created';
      } else if (actualFeeExpense.amount !== correctFee) {
        // Wrong amount
        issueType = 'incorrect_amount';
        description = `Fee is $${actualFeeExpense.amount} but should be $${correctFee}`;
      } else {
        // Correct - no issue
        return;
      }

      issues.push({
        player,
        playerTeams,
        ballTypes,
        hasLeather,
        hasTennis,
        correctFee,
        actualFee: actualFeeExpense ? actualFeeExpense.amount : 0,
        actualFeeExpense,
        issueType,
        description
      });
    });

    console.log('⚠️ Found', issues.length, 'initial donation issues');
    return issues;
  };

  const handleUpdateGameStatus = async (gameId, newStatus) => {
    try {
      console.log('🔵 Updating status for game:', gameId, 'to:', newStatus);
      const gameRef = doc(db, 'games', gameId);

      await updateDoc(gameRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });

      // The onSnapshot listener (loadGames) will pick this up automatically
    } catch (error) {
      console.error('❌ Error updating game status:', error);
      alert('Failed to update game status.');
    }
  };

  const handleSaveMatchRecap = async (gameId, recapData) => {
    try {
      console.log('🔵 Saving match recap for game:', gameId);
      const gameRef = doc(db, 'games', gameId);
      await updateDoc(gameRef, recapData);
      // The onSnapshot listener (loadGames) will pick this up automatically
    } catch (error) {
      console.error('❌ Error saving match recap:', error);
      alert('Failed to save match recap. Please try again.');
    }
  };

  // All the other handlers remain the same...
  const handleCreateTeam = async () => {
    if (!teamName) return;

    // Check for duplicate team name
    const teamNameLower = teamName.trim().toLowerCase();
    const isDuplicate = teams.some(t => t.name.toLowerCase() === teamNameLower);

    if (isDuplicate) {
      alert('A team with this name already exists. Please choose a unique name.');
      return;
    }

    try {
      console.log('🔵 Creating team:', teamName, 'Ball Type:', teamBallType, 'Players:', teamSelectedPlayers.length);

      const selectedPlayerObjects = teamSelectedPlayers.map(pid => {
        const found = players.find(p => p.id === pid || p.userId === pid);
        return {
          id: pid,
          userId: pid,
          name: found?.name || 'Unknown Player',
          email: found?.email || '',
          joinedAt: new Date().toISOString()
        };
      });

      const team = {
        name: teamName,
        clubId: selectedClub.id,
        ballType: teamBallType,
        players: selectedPlayerObjects,
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'teams'), team);
      console.log('✅ Team created:', docRef.id);

      setTeamName('');
      setTeamBallType('leather');
      setTeamSelectedPlayers([]);
      alert('Team created successfully!');
      loadTeams();
      setView('manage-teams');
    } catch (error) {
      console.error('❌ Error creating team:', error);
      alert('Error creating team');
    }
  };

  const createNotification = async (type, message, gameId = null, targetTeamIds = null) => {
    try {
      console.log('🔵 Creating notifications for club:', selectedClub.id);

      // Get all players in the club (check both clubId and clubInfo.clubId)
      const usersRef = collection(db, 'users');

      // Query 1: Users with direct clubId
      const q1 = query(usersRef, where('clubId', '==', selectedClub.id));
      const snapshot1 = await getDocs(q1);

      // Query 2: Users with clubInfo.clubId
      const q2 = query(usersRef, where('clubInfo.clubId', '==', selectedClub.id));
      const snapshot2 = await getDocs(q2);

      // Query 3: Users with joinedClubsIds
      const q3 = query(usersRef, where('joinedClubsIds', 'array-contains', selectedClub.id));
      const snapshot3 = await getDocs(q3);

      // Combine and dedupe users
      const usersMap = new Map();
      snapshot1.forEach((userDoc) => {
        usersMap.set(userDoc.id, { id: userDoc.id, ...userDoc.data() });
      });
      snapshot2.forEach((userDoc) => {
        usersMap.set(userDoc.id, { id: userDoc.id, ...userDoc.data() });
      });
      snapshot3.forEach((userDoc) => {
        usersMap.set(userDoc.id, { id: userDoc.id, ...userDoc.data() });
      });

      console.log('📊 Found', usersMap.size, 'users in club');

      const notifications = [];
      usersMap.forEach((userData, oderId) => {
        // Filter by targetTeamIds if provided
        if (targetTeamIds && targetTeamIds.length > 0) {
          if (!targetTeamIds.includes(userData.teamId)) {
            return; // Skip this user
          }
        }

        console.log('  - User:', userData.email, 'Role:', userData.role);
        // Only notify players (not admins)
        if (userData.role === 'player') {
          notifications.push({
            userId: userData.id,
            clubId: selectedClub.id,
            type: type, // 'game_created', 'game_updated', 'event_created', etc.
            message: message,
            gameId: gameId,
            read: false,
            createdAt: new Date().toISOString()
          });
        }
      });

      // Batch create notifications
      for (const notification of notifications) {
        await addDoc(collection(db, 'notifications'), notification);
      }

      console.log(`✅ Created ${notifications.length} notifications`);
    } catch (error) {
      console.error('❌ Error creating notifications:', error);
    }
  };


  const handleCreateGame = async (gameData = null) => {
    // If gameData is provided (from AddGame component), use it
    // Otherwise, use the old approach for backward compatibility
    if (!gameData && (!gameName || !gameDate || !selectedTeam)) return;

    try {
      const game = gameData || {
        name: gameName,
        date: gameDate,
        time: gameTime || null,
        arrivalTime: arrivalTime || null,
        opponent: gameOpponent,
        venue: gameVenue,
        teamId: selectedTeam || homeTeam || null,  // Try both selectedTeam and homeTeam
        homeTeamId: homeTeam || selectedTeam || null,  // Store both for compatibility
        awayTeamId: awayTeam || null,
        tournamentId: selectedTournament || null,
        clubId: selectedClub.id,
        playing11: [],
        expenses: [],
        askMealPreference: askMealPreference,
        createdAt: new Date().toISOString()
      };

      console.log('🔵 Creating game:', game.name);
      console.log('🔵 Team IDs - teamId:', game.teamId, 'homeTeamId:', game.homeTeamId);
      const docRef = await addDoc(collection(db, 'games'), game);
      const gameWithId = { ...game, id: docRef.id };
      console.log('✅ Game created:', docRef.id);

      // Send notifications to team players only (not all club players)
      // For tournament games, use homeTeamId; for regular games, use teamId
      const notificationTeamId = game.homeTeamId || game.teamId;
      let notificationResult = { count: 0 }; // Initialize with default value

      if (notificationTeamId) {
        console.log('📧 Sending notifications for team:', notificationTeamId);
        notificationResult = await notifyTeamPlayersAboutNewGame(gameWithId, notificationTeamId);

        if (notificationResult.success) {
          console.log(`✅ Notified ${notificationResult.count} team players`);
        } else {
          console.error('⚠️ Failed to send some notifications:', notificationResult.error);
        }
      } else {
        console.log('⚠️ No team ID found for game, skipping notifications');
      }

      // Reset form
      setGameName('');
      setGameDate('');
      setGameTime('');
      setArrivalTime('');
      setGameOpponent('');
      setGameVenue('');
      setSelectedTeam('');
      setSelectedTournament('');
      setHomeTeam('');
      setAwayTeam('');
      setAskMealPreference(false);

      alert(`Game created successfully! ${notificationResult?.count || 0} team players notified.`);
      await loadGames();
      setView('games-list');
    } catch (error) {
      console.error('❌ Error creating game:', error);
      alert('Error creating game');
      throw error; // Re-throw for AddGame component to handle
    }
  };

  const handleAddPlayer = async () => {
    if (!selectedTeam || !playerName || !playerEmail) return;

    try {
      console.log('🔵 Adding player to team:', selectedTeam);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('INITIAL DONATION SMART CREATION - START');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      const teamRef = doc(db, 'teams', selectedTeam);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        alert('Team not found');
        return;
      }

      const teamData = teamSnap.data();
      const teamBallType = teamData.ballType || 'leather';

      console.log('📊 Team info:');
      console.log('  - Name:', teamData.name);
      console.log('  - Ball Type:', teamBallType);

      // CRITICAL: Check if player already exists in ANY team in this club
      let existingPlayer = null;
      let existingPlayerTeams = [];

      for (const team of teams) {
        if (team.players) {
          const foundPlayer = team.players.find(p =>
            p.email.toLowerCase() === playerEmail.toLowerCase()
          );
          if (foundPlayer) {
            existingPlayer = foundPlayer;
            existingPlayerTeams.push(team);
          }
        }
      }

      if (existingPlayer) {
        console.log('⚠️ EXISTING PLAYER DETECTED');
        console.log('  - Player ID:', existingPlayer.id);
        console.log('  - Player Name:', existingPlayer.name);
        console.log('  - Currently on teams:', existingPlayerTeams.map(t => t.name).join(', '));

        // Check if already on this specific team
        if (existingPlayerTeams.some(t => t.id === selectedTeam)) {
          alert(`${playerName} is already on the ${teamData.name} team!`);
          return;
        }

        // Add player to new team (reuse existing player ID)
        const players = teamData.players || [];
        players.push({
          ...existingPlayer,
          teamId: selectedTeam
        });

        await updateDoc(teamRef, { players });
        console.log('✅ Existing player added to new team');

        // Adding existing player - no fee creation logic needed
        alert(`✅ Player added to ${teamData.name}!`);

        // Notify about roster change
        notifyTeamRosterChange({
          teamId: selectedTeam,
          teamName: teamData.name,
          player: existingPlayer,
          action: 'added',
          clubId: selectedClub.id,
          currentUserInfo: user
        });


      } else {
        // NEW PLAYER
        console.log('✨ NEW PLAYER - Creating player record');

        const player = {
          id: generateId('player'),
          name: playerName,
          email: playerEmail,
          teamId: selectedTeam,
          joinedAt: new Date().toISOString(),
          isManual: true, // Flag to indicate this player was added manually (not registered)
          isExternal: true // External player - no Firebase account, needs manual payment tracking
        };

        console.log('  - Player ID:', player.id);
        console.log('  - Player Name:', player.name);
        console.log('  - Player Email:', player.email);
        console.log('  - Is External:', player.isExternal);

        const players = teamData.players || [];
        players.push(player);

        await updateDoc(teamRef, { players });
        console.log('✅ New player added to team');

        alert(`✅ Player added to ${teamData.name}!\n\n(No initial donation assigned per updated rules)`);

        // Notify about roster change
        notifyTeamRosterChange({
          teamId: selectedTeam,
          teamName: teamData.name,
          player: player,
          action: 'added',
          clubId: selectedClub.id,
          currentUserInfo: user
        });
      }

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('INITIAL DONATION SMART CREATION - END');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      setPlayerName('');
      setPlayerEmail('');
      loadTeams();
      await loadExpenses(); // Reload expenses to show new/updated fee
    } catch (error) {
      console.error('❌ Error adding player:', error);
      alert('Error adding player: ' + error.message);
    }
  };

  const handleSelectPlaying11 = async (gameId) => {
    try {
      console.log('🔵 Selecting playing squad for game:', gameId);

      const gameRef = doc(db, 'games', gameId);
      const gameSnap = await getDoc(gameRef);

      if (!gameSnap.exists()) {
        alert('Game not found');
        return;
      }

      const game = { id: gameSnap.id, ...gameSnap.data() };

      const explicitlyAvailableMap = new Map();

      // 0. Include all players from the game's teams if recognizable
      const possibleTeamIds = [game.teamId, game.homeTeamId, game.awayTeamId, ...((game.teamIds && Array.isArray(game.teamIds)) ? game.teamIds : [])].filter(Boolean);
      const possibleTeamNames = [game.teamName, game.homeTeamName, game.awayTeamName].filter(Boolean).map(n => n.toLowerCase());
      
      teams.forEach(t => {
        const matchesId = possibleTeamIds.includes(t.id);
        const matchesName = possibleTeamNames.includes((t.name || '').toLowerCase());
        
        if ((matchesId || matchesName) && t.players) {
          t.players.forEach(p => {
            const fullPlayerInfo = players.find(x => x.id === p.id || x.userId === p.id);
            if (fullPlayerInfo) {
              explicitlyAvailableMap.set(fullPlayerInfo.id, {
                id: fullPlayerInfo.id,
                userId: fullPlayerInfo.userId || fullPlayerInfo.id,
                name: fullPlayerInfo.name,
                email: fullPlayerInfo.email,
                registrationStatus: fullPlayerInfo.registrationStatus,
                isGuest: false
              });
            }
          });
        }
      });

      const gameAvailability = game.availability || {};

      // 1. Add all players who have a recorded availability response
      Object.entries(gameAvailability).forEach(([uid, resp]) => {
        if (!explicitlyAvailableMap.has(uid)) {
          const fullPlayerInfo = players.find(p => p.id === uid || p.userId === uid);
          explicitlyAvailableMap.set(uid, {
            id: fullPlayerInfo ? fullPlayerInfo.id : uid,
            userId: fullPlayerInfo ? (fullPlayerInfo.userId || fullPlayerInfo.id) : uid,
            name: fullPlayerInfo ? fullPlayerInfo.name : (resp.playerName || 'Unknown Player'),
            email: fullPlayerInfo ? fullPlayerInfo.email : 'External/Guest',
            registrationStatus: fullPlayerInfo ? fullPlayerInfo.registrationStatus : null,
            isGuest: false
          });
        }
      });

      // 2. Ensure anyone already in playing11 is included, even if they never voted
      (game.playing11 || []).forEach(uid => {
        if (!explicitlyAvailableMap.has(uid)) {
          const fullPlayerInfo = players.find(p => p.id === uid || p.userId === uid);
          if (fullPlayerInfo) {
            explicitlyAvailableMap.set(fullPlayerInfo.id, {
              id: fullPlayerInfo.id,
              userId: fullPlayerInfo.userId || fullPlayerInfo.id,
              name: fullPlayerInfo.name,
              email: fullPlayerInfo.email,
              registrationStatus: fullPlayerInfo.registrationStatus,
              isGuest: false
            });
          }
        }
      });

      setSelectedGameForPlayers(game);
      setAvailablePlayers(Array.from(explicitlyAvailableMap.values()));
      setView('select-playing11');
    } catch (error) {
      console.error('❌ Error selecting squad:', error);
      alert('Error loading game data');
    }
  };

  const handleSavePlaying11 = async ({ playing11, superSubs, guestPlayers, playerDuties, isPublish, notificationOption }) => {
    // Note: Validation is handled by SquadSelection component
    if (!selectedGameForPlayers) {
      alert('No game selected');
      return;
    }

    // Duty labels for notifications
    const DUTY_LABELS = {
      'ground_setup': '🏟️ Ground Setup',
      'refreshments': '🥤 Drinks & Snacks',
      'scoring': '📝 Scoring',
      'umpiring': '🧑‍⚖️ Umpiring',
      'first_aid': '🩹 First Aid',
      'equipment': '🎒 Equipment',
      'transport': '🚗 Transport',
      'photography': '📸 Photography'
    };

    try {
      console.log('🔵 Saving playing squad for game:', selectedGameForPlayers.id);
      console.log('  - Players:', playing11.length);
      console.log('  - Super subs:', superSubs?.length || 0);
      console.log('  - Guest players:', guestPlayers?.length || 0);
      console.log('  - Players with duties:', Object.keys(playerDuties || {}).length);

      const gameRef = doc(db, 'games', selectedGameForPlayers.id);
      const previousSquad = selectedGameForPlayers.playing11 || [];

      // Update game with playing11, superSubs, guestPlayers, and duties
      const updateData = {
        playing11,
        superSubs: superSubs || [],
        guestPlayers: guestPlayers || [],
        playerDuties: playerDuties || {}
      };

      const previousNotified = selectedGameForPlayers.notifiedSquad || [];

      let newlySelected = [];
      if (isPublish) {
        updateData.isSquadPublished = true;
        newlySelected = playing11.filter(playerId =>
          !previousNotified.includes(playerId) && !playerId.startsWith('guest')
        );
        updateData.notifiedSquad = Array.from(new Set([...previousNotified, ...newlySelected]));
      }

      await updateDoc(gameRef, updateData);

      // Format game date nicely
      const formattedDate = formatDate(selectedGameForPlayers.date, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });

      let totalNotified = 0;

      // Create notifications for newly selected REGISTERED players only when PUBLISHED
      if (isPublish) {
        const notificationResult = await processSquadPublishNotifications({
          game: selectedGameForPlayers,
          newSquad: playing11,
          previousSquad: previousSquad,
          superSubs: superSubs || [],
          newDuties: playerDuties || {},
          previousDuties: selectedGameForPlayers.playerDuties || {},
          notificationOption: notificationOption,
          currentUserName: user.name || user.email,
          clubId: selectedClub.id
        });

        if (notificationResult && notificationResult.success) {
          totalNotified = notificationResult.count;
          console.log(`✅ Processed admin squad notifications (Option: ${notificationOption}): ${totalNotified} sent`);
        }
        totalNotified += newlySelected.length;

        // Also notify players who got NEW duties assigned (even if already in squad and previously notified)
        const playersWithNewDuties = Object.keys(playerDuties || {}).filter(playerId => {
          const previousDuty = selectedGameForPlayers.playerDuties?.[playerId];
          const newDuty = playerDuties[playerId];
          // We don't skip newlySelected here if they got a duty, because previous logic didn't skip them strictly 
          // Wait, previous logic DID skip newlySelected: `!newlySelected.includes(playerId)`. We will keep that.
          return newDuty && newDuty !== previousDuty && !playerId.startsWith('guest') && !newlySelected.includes(playerId);
        });

        for (const playerId of playersWithNewDuties) {
          const dutyLabel = DUTY_LABELS[playerDuties[playerId]];
          if (dutyLabel) {
            const notificationRef = collection(db, 'notifications');
            await addDoc(notificationRef, {
              userId: playerId,
              type: 'duty_assigned',
              gameId: selectedGameForPlayers.id,
              gameName: selectedGameForPlayers.name,
              gameDate: formattedDate,
              gameVenue: selectedGameForPlayers.venue || '',
              message: `📋 You've been assigned: ${dutyLabel}`,
              assignedDuty: dutyLabel,
              read: false,
              createdAt: new Date().toISOString(),
              createdBy: user.name || user.email,
              clubId: selectedClub.id
            });
          }
        }
        totalNotified += playersWithNewDuties.length;
      }

      console.log('✅ Squad saved with super subs, guests, duties, and optional notifications');
      if (isPublish) {
        alert(`Squad published successfully! ${totalNotified} player${totalNotified !== 1 ? 's' : ''} notified.`);
      } else {
        alert(`Squad draft saved successfully!`);
      }

      await loadGames();
      setView('games-list');
    } catch (error) {
      console.error('❌ Error saving squad:', error);
      alert('Error saving squad');
    }
  };

  const handlePromoteToAdmin = async (player, targetClubId) => {
    // Prioritize implicitly passed targetClubId
    const clubId = targetClubId || player.clubId || player.clubInfo?.clubId;
    const targetClub = clubs.find(c => c.id === clubId);
    const clubName = targetClub?.name || player.clubName || 'this club';

    if (!clubId) {
      alert('Please select a club to make this player admin of.');
      return;
    }

    if (!confirm(`Promote ${player.name} to admin for ${clubName}?`)) {
      return;
    }

    try {
      console.log('🔵 Promoting player to admin:', player.email, 'for club:', clubId);

      // Update user's per-club role
      const userRef = doc(db, 'users', player.id);
      const updatePayload = {
        [`joinedClubs.${clubId}`]: {
          clubId: clubId,
          clubName: clubName,
          role: 'admin',
          status: 'approved',
          joinedAt: new Date().toISOString()
        },
        joinedClubsIds: arrayUnion(clubId),
        promotedAt: new Date().toISOString(),
        promotedBy: user.email
      };

      // Only set global role to 'admin' if this is the player's currently active club
      // Otherwise, it would incorrectly make them admin of their current (different) club too
      const playerActiveClubId = player.clubId || player.clubInfo?.clubId;
      if (clubId === playerActiveClubId) {
        updatePayload.role = 'admin';
      }

      await updateDoc(userRef, updatePayload);

      // Also add to club's admins array
      const clubRef = doc(db, 'club', clubId);
      await updateDoc(clubRef, {
        admins: arrayUnion(player.id)
      });
      console.log('✅ Added to club admins array');

      console.log('✅ Player promoted to admin (global + per-club)');
      alert(`${player.name} is now an admin for ${clubName}!`);

      // Reload players list
      await loadPlayers(null);
    } catch (error) {
      console.error('❌ Error promoting player:', error);
      alert('Error promoting player to admin');
    }
  };

  const handleDemoteAdmin = async (admin) => {
    // Prioritize explicitly passed clubId over legacy clubInfo
    const clubId = admin.clubId || admin.clubInfo?.clubId;
    const clubName = admin.clubName || 'their club';

    if (!confirm(`Demote ${admin.name} from admin of ${clubName}?`)) {
      return;
    }

    try {
      console.log('🔵 Demoting admin to player:', admin.email, 'for club:', clubId);

      const userRef = doc(db, 'users', admin.id);
      const userUpdatePayload = {
        additionalRoles: arrayRemove('admin'),
        demotedAt: new Date().toISOString(),
        demotedBy: user.email
      };

      // Set per-club role to player
      if (clubId) {
        userUpdatePayload[`joinedClubs.${clubId}.role`] = 'player';
      }

      // Check if user is admin of any OTHER club before resetting global role
      const userSnap = await getDoc(userRef);
      let isAdminOfOtherClub = false;
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const joinedClubs = userData.joinedClubs || {};
        for (const [jClubId, jClubData] of Object.entries(joinedClubs)) {
          if (jClubId !== clubId && jClubData.role === 'admin') {
            isAdminOfOtherClub = true;
            break;
          }
        }
        // Also check other club.admins arrays as fallback
        if (!isAdminOfOtherClub) {
          const clubsRef = collection(db, 'club');
          const allClubsSnap = await getDocs(clubsRef);
          allClubsSnap.forEach((clubDoc) => {
            if (clubDoc.id !== clubId) {
              const clubData = clubDoc.data();
              if (clubData.admins?.includes(admin.id)) {
                isAdminOfOtherClub = true;
              }
            }
          });
        }
      }

      if (!isAdminOfOtherClub) {
        userUpdatePayload.role = 'player';
        console.log('✅ User is not admin of any other club, resetting global role');
      } else {
        console.log('ℹ️ User is still admin of another club, keeping global role');
      }

      await updateDoc(userRef, userUpdatePayload);

      // Also remove from club's admins array
      if (clubId) {
        const clubRef = doc(db, 'club', clubId);
        await updateDoc(clubRef, {
          admins: arrayRemove(admin.id)
        });
        console.log('✅ Removed from club admins array');
      }

      console.log('✅ Admin demoted');
      alert(`${admin.name} is no longer admin of ${clubName}.`);

      // Reload players list
      await loadPlayers(null);
    } catch (error) {
      console.error('❌ Error demoting admin:', error);
      alert('Error demoting admin');
    }
  };

  // Edit Club states
  const [editingClub, setEditingClub] = useState(null);
  const [editClubName, setEditClubName] = useState('');
  const [editClubLocation, setEditClubLocation] = useState('');
  const [editClubIsArchived, setEditClubIsArchived] = useState(false);

  const handleEditClub = (club) => {
    setEditingClub(club);
    setEditClubName(club.name);
    setEditClubLocation(club.location);
    setEditClubIsArchived(club.isArchived || false);
  };

  const handleSaveClubEdit = async () => {
    if (!editClubName.trim() || !editClubLocation.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const clubRef = doc(db, 'club', editingClub.id);
      await updateDoc(clubRef, {
        name: editClubName.trim(),
        location: editClubLocation.trim(),
        isArchived: editClubIsArchived,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      await reloadClubs();

      alert(`✅ ${editClubName} has been updated successfully!`);
      setEditingClub(null);
    } catch (error) {
      console.error('Error updating club:', error);
      alert('Failed to update club. Please try again.');
    }
  };

  const handleApproveClub = async (club) => {
    if (!confirm(`Approve club: ${club.name}? This will grant the creator full access.`)) {
      return;
    }
    
    try {
      // 1. Update club status
      const result = await clubService.approveClub(club.id);
      if (!result.success) throw new Error(result.error);
      
      // 2. Update user status so they can access their dashboard
      const userId = club.adminId || club.createdBy;
      if (userId) {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let updates = {};
          if (userData.clubInfo && userData.clubInfo.status === 'pending') {
            updates['clubInfo.status'] = 'approved';
          }
          if (userData.joinedClubs && userData.joinedClubs[club.id] && userData.joinedClubs[club.id].status === 'pending') {
            updates[`joinedClubs.${club.id}.status`] = 'approved';
          }
          if (Object.keys(updates).length > 0) {
            await updateDoc(userRef, updates);
          }
        }
      }
      
      await reloadClubs();
      alert(`✅ ${club.name} has been approved successfully!`);
    } catch (error) {
      console.error('Error approving club:', error);
      alert('Failed to approve club. Please try again.');
    }
  };

  const handleDeleteClub = async (clubId) => {
    const clubToDelete = clubs.find(c => c.id === clubId);
    const clubName = clubToDelete?.name || 'this club';

    if (!confirm(`⚠️ DELETE CLUB: ${clubName}\n\nThis will permanently delete:\n• All teams\n• All games\n• All tournaments\n• All expenses\n• All notifications\n• All wallet transactions\n• All payments\n\nThis cannot be undone. Are you sure?`)) {
      return;
    }

    // Double confirmation for safety
    if (!confirm(`FINAL CONFIRMATION\n\nType "DELETE" mentally and click OK to permanently delete ${clubName} and ALL its data.`)) {
      return;
    }

    try {
      console.log('🔵 Starting cascade deletion for club:', clubId);

      let deletedCounts = {
        teams: 0,
        games: 0,
        tournaments: 0,
        expenses: 0,
        notifications: 0,
        walletTransactions: 0,
        payments: 0
      };

      // 1. Delete all TEAMS for this club
      console.log('🗑️ Deleting teams...');
      const teamsQuery = query(collection(db, 'teams'), where('clubId', '==', clubId));
      const teamsSnapshot = await getDocs(teamsQuery);
      for (const teamDoc of teamsSnapshot.docs) {
        await deleteDoc(doc(db, 'teams', teamDoc.id));
        deletedCounts.teams++;
      }
      console.log(`  ✅ Deleted ${deletedCounts.teams} teams`);

      // 2. Delete all GAMES for this club
      console.log('🗑️ Deleting games...');
      const gamesQuery = query(collection(db, 'games'), where('clubId', '==', clubId));
      const gamesSnapshot = await getDocs(gamesQuery);
      for (const gameDoc of gamesSnapshot.docs) {
        await deleteDoc(doc(db, 'games', gameDoc.id));
        deletedCounts.games++;
      }
      console.log(`  ✅ Deleted ${deletedCounts.games} games`);

      // 3. Delete all TOURNAMENTS for this club
      console.log('🗑️ Deleting tournaments...');
      const tournamentsQuery = query(collection(db, 'tournaments'), where('clubId', '==', clubId));
      const tournamentsSnapshot = await getDocs(tournamentsQuery);
      for (const tournamentDoc of tournamentsSnapshot.docs) {
        await deleteDoc(doc(db, 'tournaments', tournamentDoc.id));
        deletedCounts.tournaments++;
      }
      console.log(`  ✅ Deleted ${deletedCounts.tournaments} tournaments`);

      // 4. Delete all EXPENSES for this club
      console.log('🗑️ Deleting expenses...');
      const expensesQuery = query(collection(db, 'expenses'), where('clubId', '==', clubId));
      const expensesSnapshot = await getDocs(expensesQuery);
      for (const expenseDoc of expensesSnapshot.docs) {
        await deleteDoc(doc(db, 'expenses', expenseDoc.id));
        deletedCounts.expenses++;
      }
      console.log(`  ✅ Deleted ${deletedCounts.expenses} expenses`);

      // 5. Delete all NOTIFICATIONS for this club
      console.log('🗑️ Deleting notifications...');
      const notificationsQuery = query(collection(db, 'notifications'), where('clubId', '==', clubId));
      const notificationsSnapshot = await getDocs(notificationsQuery);
      for (const notificationDoc of notificationsSnapshot.docs) {
        await deleteDoc(doc(db, 'notifications', notificationDoc.id));
        deletedCounts.notifications++;
      }
      console.log(`  ✅ Deleted ${deletedCounts.notifications} notifications`);

      // 6. Delete all WALLET TRANSACTIONS for this club
      console.log('🗑️ Deleting wallet transactions...');
      const walletQuery = query(collection(db, 'walletTransactions'), where('clubId', '==', clubId));
      const walletSnapshot = await getDocs(walletQuery);
      for (const walletDoc of walletSnapshot.docs) {
        await deleteDoc(doc(db, 'walletTransactions', walletDoc.id));
        deletedCounts.walletTransactions++;
      }
      console.log(`  ✅ Deleted ${deletedCounts.walletTransactions} wallet transactions`);

      // 7. Delete all PAYMENTS for this club
      console.log('🗑️ Deleting payments...');
      const paymentsQuery = query(collection(db, 'payments'), where('clubId', '==', clubId));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      for (const paymentDoc of paymentsSnapshot.docs) {
        await deleteDoc(doc(db, 'payments', paymentDoc.id));
        deletedCounts.payments++;
      }
      console.log(`  ✅ Deleted ${deletedCounts.payments} payments`);

      // 8. Finally, delete the CLUB itself
      console.log('🗑️ Deleting club document...');
      const clubRef = doc(db, 'club', clubId);
      await deleteDoc(clubRef);

      console.log('✅ Club and all associated data deleted successfully');

      // Show summary
      alert(`✅ Club "${clubName}" deleted successfully!\n\nDeleted:\n• ${deletedCounts.teams} teams\n• ${deletedCounts.games} games\n• ${deletedCounts.tournaments} tournaments\n• ${deletedCounts.expenses} expenses\n• ${deletedCounts.notifications} notifications\n• ${deletedCounts.walletTransactions} wallet transactions\n• ${deletedCounts.payments} payments`);

      // Reload clubs
      window.location.reload();
    } catch (error) {
      console.error('❌ Error deleting club:', error);
      alert('Error deleting club: ' + error.message);
    }
  };

  const handleUpdatePlayerRole = async (teamId, playerId, newRole) => {
    try {
      console.log('🔵 Updating player role:', { teamId, playerId, newRole });

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        alert('Team not found');
        return;
      }

      const team = teamSnap.data();

      // Remove duplicate players (same userId)
      const seenUserIds = new Set();
      let dedupedPlayers = [];

      (team.players || []).forEach(p => {
        const userId = p.userId || p.id;
        if (!seenUserIds.has(userId)) {
          seenUserIds.add(userId);
          dedupedPlayers.push(p);
        }
      });

      const updatedPlayers = dedupedPlayers.map(p => {
        if (p.id === playerId) {
          return { ...p, teamRole: newRole === 'player' ? null : newRole };
        }
        // If setting a new captain, remove captain from others
        if (newRole === 'captain' && p.teamRole === 'captain') {
          return { ...p, teamRole: null };
        }
        // If setting a new vice-captain, remove vice-captain from others (Wait, teams can have multiple VCs?)
        // The original logic stripped it from others. Let's keep the original logic strictly:
        if (newRole === 'vice-captain' && p.teamRole === 'vice-captain') {
          return { ...p, teamRole: null };
        }
        return p;
      });

      // Synchronize root arrays for firestore.rules
      const newCaptains = updatedPlayers.filter(p => p.teamRole === 'captain').map(p => p.userId || p.id);
      const newViceCaptains = updatedPlayers.filter(p => p.teamRole === 'vice-captain').map(p => p.userId || p.id);

      await updateDoc(teamRef, { 
        players: updatedPlayers,
        captains: newCaptains,
        viceCaptains: newViceCaptains
      });

      console.log('✅ Player role updated');
      loadTeams();
    } catch (error) {
      console.error('❌ Error updating player role:', error);
      alert('Error updating player role');
    }
  };

  const handleRemovePlayer = async (teamId, playerId, playerName) => {
    // UPDATED MESSAGE: No longer warns about deleting donations
    if (!confirm(`Remove ${playerName} from the team?`)) {
      return;
    }

    try {
      console.log('🔵 Removing player from team:', { teamId, playerId });

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        alert('Team not found');
        return;
      }

      const team = teamSnap.data();
      const player = team.players?.find(p => p.id === playerId);

      // Remove player from team
      const updatedPlayers = (team.players || []).filter(p => p.id !== playerId);
      
      const newCaptains = updatedPlayers.filter(p => p.teamRole === 'captain').map(p => p.userId || p.id);
      const newViceCaptains = updatedPlayers.filter(p => p.teamRole === 'vice-captain').map(p => p.userId || p.id);
      
      await updateDoc(teamRef, { 
        players: updatedPlayers,
        captains: newCaptains,
        viceCaptains: newViceCaptains
      });

      // Update user document to remove teamId
      if (player && player.userId) {
        const userRef = doc(db, 'users', player.userId);
        await updateDoc(userRef, {
          teamId: deleteField()
        });
      }

      // CRITICAL CHANGE: We NO LONGER delete financial records here.
      // - Initial Donation expense remains (they still owe the club)
      // - Wallet transactions remain (history preserved)
      // - Payment records remain

      console.log('✅ Player removed from team (financial records preserved)');
      alert(`${playerName} has been removed from the team.`);

      // Notify about roster change
      // Get the team name since we only have teamId
      const teamName = team.name || 'Team';
      notifyTeamRosterChange({
        teamId: teamId,
        teamName: teamName,
        player: player || { id: playerId, name: playerName },
        action: 'removed',
        clubId: selectedClub.id,
        currentUserInfo: user
      });

      // Reload data to reflect changes
      loadTeams();
      // No need to reload expenses/wallet since they didn't change, 
      // but reloading maintains consistent state
    } catch (error) {
      console.error('❌ Error removing player:', error);
      alert('Error removing player from team');
    }
  };

  const handleConfirmBatchTeamAssignment = async (player) => {
    const teamIds = selectedTeamsForPlayer[player.id] || [];
    if (teamIds.length === 0) return;
    
    setIsAssigningPlayer(true);
    try {
      // Execute all assignments parallelly without auto-refreshing in the middle
      for (const id of teamIds) {
        await handleAssignPlayerToTeam(player, id, true);
      }
      
      // Cleanup locally
      setExpandedPlayerTeamAssign(prev => ({ ...prev, [player.id]: false }));
      setSelectedTeamsForPlayer(prev => ({ ...prev, [player.id]: [] }));
      
      // Trigger mass refresh at the very end
      loadTeams();
      await loadUnassignedPlayers();
      await loadExpenses();
      
      alert(`Successfully assigned ${player.name} to ${teamIds.length} team(s)!`);
    } catch (e) {
      console.error('Batch assignment failed', e);
      alert('Failed to batch assign player.');
    } finally {
      setIsAssigningPlayer(false);
    }
  };

  const handleAssignPlayerToTeam = async (player, teamId, skipReload = false) => {
    try {
      console.log('🔵 Assigning player to team:', { player: player.name, teamId });
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('INITIAL DONATION - MANAGE PLAYERS FLOW');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Check if player has completed registration form (for Cascadia Willow Club)
      const isCWCClub = selectedClub?.name && (
        selectedClub.name === 'Cascadia Willow Club' ||
        selectedClub.name === 'CWC'
      );

      if (isCWCClub) {
        console.log('🔵 Cascadia Willow Club - Checking registration form completion for:', player.name);
        try {
          const regFormRef = doc(db, 'registrationForms', player.id);
          const regFormSnap = await getDoc(regFormRef);

          if (!regFormSnap.exists()) {
            // Admin override - allow assigning with confirmation
            const confirmAssign = confirm(
              `⚠️ Registration Form Not Completed\n\n` +
              `${player.name} has not completed the Player Registration Form for ${selectedClub.name}.\n\n` +
              `This may be a pre-existing user from before the registration requirement was added.\n\n` +
              `Do you want to assign them to the team anyway?`
            );

            if (!confirmAssign) {
              console.log('⚠️ Admin cancelled assignment - registration form not completed');
              return;
            }
            console.log('⚠️ Admin override - assigning player without registration form');
          } else {
            console.log('✅ Player registration form verified');
          }
        } catch (regFormError) {
          console.error('⚠️ Error checking registration form:', regFormError);
          // Continue on error to avoid blocking legitimate assignments
        }
      }

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        alert('Team not found');
        return;
      }

      const team = teamSnap.data();
      const teamBallType = team.ballType || 'leather';

      console.log('📊 Team info:');
      console.log('  - Name:', team.name);
      console.log('  - Ball Type:', teamBallType);

      // Check if player already exists in team (by id which is Firebase UID)
      const playerExists = team.players?.some(p =>
        p.id === player.id || p.userId === player.id || p.email?.toLowerCase() === player.email?.toLowerCase()
      );

      if (playerExists) {
        alert(`${player.name} is already in ${team.name}!`);
        return;
      }

      // IMPORTANT: Use Firebase UID as the single source of truth for player ID
      // This ensures consistency across all parts of the app
      const newPlayer = {
        id: player.id, // Firebase UID - single source of truth
        name: player.name,
        email: player.email,
        teamId: teamId,
        joinedAt: new Date().toISOString()
      };

      const updatedPlayers = [...(team.players || []), newPlayer];
      await updateDoc(teamRef, { players: updatedPlayers });

      console.log('✅ Player added to team with ID:', player.id);



      // Send notification to player (Non-blocking)
      try {
        await addDoc(collection(db, 'notifications'), {
          userId: player.id,
          clubId: selectedClub.id,
          type: 'team_assigned',
          message: `You've been assigned to ${team.name}! Refresh to see your team.`,
          read: false,
          createdAt: new Date().toISOString()
        });
      } catch (notifError) {
        console.warn('⚠️ Could not send notification (likely network/duplicate), but assignment proceeded:', notifError);
        // Swallow error so assignment completes
      }

      console.log('✅ Player assigned to team');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      if (!skipReload) {
        // Reload teams and unassigned players
        loadTeams();
        await loadUnassignedPlayers();
        await loadExpenses(); // ✅ Reload expenses to show new fee
      }
    } catch (error) {
      console.error('❌ Error assigning player:', error);
      alert('Error assigning player to team');
    }
  };

  // DELETE HANDLERS
  const handleUpdateGame = async (gameId, gameData) => {
    try {
      console.log('🔵 Updating game:', gameId);

      const gameRef = doc(db, 'games', gameId);
      await updateDoc(gameRef, gameData);

      // Send notification to targeted players
      const targetTeamIds = [gameData.homeTeamId, gameData.awayTeamId].filter(id => id != null && id !== '');
      await createNotification(
        'game_updated',
        `Game updated: ${gameData.name} on ${formatDate(gameData.date)}`,
        gameId,
        targetTeamIds
      );

      console.log('✅ Game updated');
      alert('Game updated successfully!');
      await loadGames();
      setView('games-list');
      setEditingGame(null);
    } catch (error) {
      console.error('❌ Error updating game:', error);
      alert('Error updating game');
      throw error;
    }
  };

  const handleDeleteGame = async (gameId) => {
    try {
      console.log('🔵 Deleting game:', gameId);

      // Get game data before deleting for debugging
      const gameRef = doc(db, 'games', gameId);
      const gameSnap = await getDoc(gameRef);

      if (!gameSnap.exists()) {
        alert('Game not found');
        return;
      }

      const gameData = gameSnap.data();
      const gameName = gameData.name || 'A game';

      console.log('🔍 Game data:', {
        gameId,
        clubId: gameData.clubId,
        userClubId: user.clubInfo?.clubId,
        userRole: user.role
      });

      // Check for associated expenses
      const expensesRef = collection(db, 'expenses');
      const q = query(expensesRef, where('gameId', '==', gameId), where('clubId', '==', gameData.clubId));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        alert('There are open expenses associated with this game. Please remove or resolve them before deleting this game.');
        return;
      }

      // Delete the game
      const deletePromises = [deleteDoc(gameRef)];
      await Promise.all(deletePromises);

      // Send notification to targeted players
      const targetTeamIds = [gameData.homeTeamId, gameData.awayTeamId].filter(id => id != null && id !== '');
      await createNotification(
        'game_deleted',
        `Game deleted: ${gameName}`,
        null,
        targetTeamIds
      );

      console.log('✅ Game deleted');
      await loadGames();
      await loadExpenses();
    } catch (error) {
      console.error('❌ Error deleting game:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message
      });
      alert(`Error deleting game: ${error.message}`);
    }
  };

  const handleDeleteTournament = async (tournamentId) => {
    if (!confirm('Are you sure you want to delete this tournament? This cannot be undone.')) {
      return;
    }

    try {
      console.log('🔵 Deleting tournament:', tournamentId);

      const gamesInTournament = games.filter(g => g.tournamentId === tournamentId);

      // Check for any expenses linked to the tournament directly or its games
      const hasExpenses = expenses.some(e => 
        e.tournamentId === tournamentId || 
        (e.gameId && gamesInTournament.some(g => g.id === e.gameId))
      );

      if (hasExpenses) {
        alert('Cannot delete tournament: There are open expenses associated with this tournament or its games. Please remove or resolve them first.');
        return;
      }

      if (gamesInTournament.length > 0) {
        if (!confirm(`This tournament has ${gamesInTournament.length} game(s). Deleting the tournament will ALSO delete all associated games. Are you sure?`)) {
          return;
        }
      }

      const deletePromises = [];

      // Add tournament deletion to promises
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      deletePromises.push(deleteDoc(tournamentRef));

      // Add all associated games deletion to promises
      gamesInTournament.forEach(game => {
        const gameRef = doc(db, 'games', game.id);
        deletePromises.push(deleteDoc(gameRef));
      });

      await Promise.all(deletePromises);

      console.log('✅ Tournament and its games deleted');
      alert('Tournament and associated games deleted successfully!');
      
      // Reload both tournaments and games to reflect changes
      await loadTournaments();
      await loadGames();
    } catch (error) {
      console.error('❌ Error deleting tournament:', error);
      alert('Error deleting tournament');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Are you sure you want to delete this team? This cannot be undone.')) {
      return;
    }

    try {
      console.log('🔵 Deleting team:', teamId);

      // Check if there are games for this team
      const gamesForTeam = games.filter(g => g.teamId === teamId);
      if (gamesForTeam.length > 0) {
        alert(`Cannot delete team. It has ${gamesForTeam.length} game(s) associated with it. Delete the games first.`);
        return;
      }

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (teamSnap.exists()) {
        const team = teamSnap.data();

        // Remove teamId from all players in this team
        if (team.players && team.players.length > 0) {
          const updatePromises = team.players.map(player => {
            if (player.userId) {
              const userRef = doc(db, 'users', player.userId);
              return updateDoc(userRef, { teamId: null });
            }
            return Promise.resolve();
          });
          await Promise.all(updatePromises);
        }
      }

      await deleteDoc(doc(db, 'teams', teamId));

      console.log('✅ Team deleted');
      alert('Team deleted successfully!');
      loadTeams();
    } catch (error) {
      console.error('❌ Error deleting team:', error);
      alert('Error deleting team');
    }
  };

  // EVENT HANDLERS
  const handleCreateEvent = async (eventData) => {
    if (!selectedClub) return;

    try {
      console.log('🔵 Creating event:', eventData.name);

      const eventsRef = collection(db, 'events');
      const newEvent = {
        ...eventData,
        clubId: selectedClub.id,
        clubName: selectedClub.name,
        createdBy: user.uid,
        createdAt: new Date().toISOString()
      };

      await addDoc(eventsRef, newEvent);

      // Send notification to all club members if inviteAllMembers is checked
      if (eventData.inviteAllMembers !== false) {
        const eventDate = formatDate(eventData.date, {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
        const timeStr = eventData.time ? ` at ${eventData.time}` : '';
        const venueStr = eventData.venue ? ` - ${eventData.venue}` : '';

        await createNotification(
          'event_created',
          `🎉 New Event: ${eventData.name} on ${eventDate}${timeStr}${venueStr}. Please RSVP!`,
          null
        );
      }

      console.log('✅ Event created');
      alert('Event created successfully!' + (eventData.inviteAllMembers !== false ? ' Invitations sent to club members.' : ''));
      await loadEvents();
      setView('events-list');
    } catch (error) {
      console.error('❌ Error creating event:', error);
      alert('Error creating event');
    }
  };

  const handleUpdateEvent = async (eventData) => {
    if (!eventData?.id) return;

    try {
      console.log('🔵 Updating event:', eventData.id);

      const eventRef = doc(db, 'events', eventData.id);
      await updateDoc(eventRef, {
        ...eventData,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Event updated');
      alert('Event updated successfully!');
      await loadEvents();
      setView('events-list');
      setEditingEvent(null);
    } catch (error) {
      console.error('❌ Error updating event:', error);
      alert('Error updating event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      console.log('🔵 Deleting event:', eventId);

      const eventRef = doc(db, 'events', eventId);
      await deleteDoc(eventRef);

      console.log('✅ Event deleted');
      alert('Event deleted successfully!');
      await loadEvents();
    } catch (error) {
      console.error('❌ Error deleting event:', error);
      alert('Error deleting event');
    }
  };

  const handleSendEventReminder = async (event) => {
    try {
      console.log('🔵 Sending event reminder for:', event.name);

      await createNotification(
        'event_reminder',
        `Reminder: ${event.name} on ${formatDate(event.date)}. Please RSVP!`,
        null
      );

      alert('Reminder sent to all club members!');
    } catch (error) {
      console.error('❌ Error sending reminder:', error);
      alert('Error sending reminder');
    }
  };

  // EDIT HANDLERS
  const handleEditGame = async () => {
    if (!editingGame) return;

    try {
      console.log('🔵 Updating game:', editingGame.id);

      const gameRef = doc(db, 'games', editingGame.id);
      await updateDoc(gameRef, {
        name: gameName,
        date: gameDate,
        opponent: gameOpponent,
        venue: gameVenue,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Game updated');
      alert('Game updated successfully!');
      setEditingGame(null);
      setGameName('');
      setGameDate('');
      setGameOpponent('');
      setGameVenue('');
      await loadGames();
      setView('games-list');
    } catch (error) {
      console.error('❌ Error updating game:', error);
      alert('Error updating game');
    }
  };

  const handleEditTournament = async () => {
    if (!editingTournament) return;

    try {
      console.log('🔵 Updating tournament:', editingTournament.id);

      const tournamentRef = doc(db, 'tournaments', editingTournament.id);
      await updateDoc(tournamentRef, {
        name: tournamentName,
        startDate: tournamentStartDate,
        endDate: tournamentEndDate || null, // Optional end date
        ballType: tournamentBallType,
        maxSquadSize: tournamentMaxSquad,
        allowSuperSubs: tournamentAllowSuperSubs,
        updatedAt: new Date().toISOString()
      });

      // Propagate allowSuperSubs and maxSquadSize to all associated games in the tournament
      try {
        const gamesRef = collection(db, 'games');
        const q = query(gamesRef, where('tournamentId', '==', editingTournament.id));
        const snapshot = await getDocs(q);
        const updatePromises = [];
        snapshot.forEach((gameDoc) => {
          updatePromises.push(
            updateDoc(doc(db, 'games', gameDoc.id), {
              allowSuperSubs: tournamentAllowSuperSubs,
              maxSquadSize: tournamentMaxSquad
            })
          );
        });
        await Promise.all(updatePromises);
        console.log(`✅ Propagated squad settings to ${snapshot.size} games`);
      } catch (err) {
        console.error('❌ Error propagating squad settings to games:', err);
      }

      console.log('✅ Tournament updated');
      alert('Tournament updated successfully!');
      setEditingTournament(null);
      setTournamentName('');
      setTournamentStartDate('');
      setTournamentEndDate(''); // Clear end date
      setTournamentBallType('leather');
      setTournamentMaxSquad(11);
      setTournamentAllowSuperSubs(false);
      await loadTournaments();
      setView('manage-tournaments');
    } catch (error) {
      console.error('❌ Error updating tournament:', error);
      alert('Error updating tournament');
    }
  };

  const handleEditTeam = async () => {
    if (!editingTeam) return;

    try {
      console.log('🔵 Updating team:', editingTeam.id);

      const teamRef = doc(db, 'teams', editingTeam.id);
      await updateDoc(teamRef, {
        name: teamName,
        ballType: teamBallType, // NEW: update ball type
        whatsappGroupLink: teamWhatsAppLink || null,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Team updated');
      alert('Team updated successfully!');
      setEditingTeam(null);
      setTeamName('');
      setTeamWhatsAppLink('');
      setTeamBallType('leather'); // Reset to default
      loadTeams();
      setView('manage-teams');
    } catch (error) {
      console.error('❌ Error updating team:', error);
      alert('Error updating team');
    }
  };

  // REMINDER FUNCTIONS
  const handleSendGameReminder = async (gameId) => {
    try {
      const game = games.find(g => g.id === gameId);
      if (!game || !game.playing11 || game.playing11.length === 0) {
        alert('Please select a squad before sending reminders');
        return;
      }

      const result = await sendGameReminder(gameId);
      if (result.success) {
        alert(`Game reminder sent to ${result.count} players: ${result.players.join(', ')}`);
      } else {
        alert('Error sending reminders');
      }
    } catch (error) {
      console.error('❌ Error sending game reminder:', error);
      alert('Error sending reminder');
    }
  };

  const handleSendAvailabilityReminder = async (gameId) => {
    try {
      const result = await sendAvailabilityReminder(gameId);
      if (result.success) {
        if (result.count === 0) {
          alert('All players have already provided their availability!');
        } else {
          alert(`Availability reminder sent to ${result.count} players: ${result.players.join(', ')}`);
        }
      } else {
        alert('Error sending availability reminders');
      }
    } catch (error) {
      console.error('❌ Error sending availability reminder:', error);
      alert('Error sending reminder');
    }
  };

  const handleViewAvailability = (game) => {
    setViewingAvailability(game);
  };

  // Handle marking a player as late arrival for a game
  const handleMarkLateArrival = async (gameId, playerId, isLate) => {
    try {
      console.log('🔵 Marking late arrival:', { gameId, playerId, isLate });

      const gameRef = doc(db, 'games', gameId);
      const gameSnap = await getDoc(gameRef);

      if (!gameSnap.exists()) {
        alert('Game not found');
        return;
      }

      const currentLateArrivals = gameSnap.data().lateArrivals || {};

      if (isLate) {
        currentLateArrivals[playerId] = true;
      } else {
        delete currentLateArrivals[playerId];
      }

      await updateDoc(gameRef, {
        lateArrivals: currentLateArrivals
      });

      // Update local state
      setGames(prev => prev.map(g =>
        g.id === gameId
          ? { ...g, lateArrivals: currentLateArrivals }
          : g
      ));

      console.log('✅ Late arrival updated');
    } catch (error) {
      console.error('❌ Error marking late arrival:', error);
      alert('Error updating late arrival status');
    }
  };

  // Handle assigning a duty to a player for a game
  const handleAssignDuty = async (gameId, dutyId, playerId) => {
    try {
      console.log('🔵 Assigning duty:', { gameId, dutyId, playerId });

      const gameRef = doc(db, 'games', gameId);
      const gameSnap = await getDoc(gameRef);

      if (!gameSnap.exists()) {
        alert('Game not found');
        return;
      }

      const currentDuties = gameSnap.data().playerDuties || {};

      // First, remove this duty from any player who currently has it
      Object.keys(currentDuties).forEach(pid => {
        if (currentDuties[pid] === dutyId) {
          delete currentDuties[pid];
        }
      });

      // If a player is selected, assign the duty to them
      if (playerId) {
        currentDuties[playerId] = dutyId;
      }

      await updateDoc(gameRef, {
        playerDuties: currentDuties
      });

      // Update local state
      setGames(prev => prev.map(g =>
        g.id === gameId
          ? { ...g, playerDuties: currentDuties }
          : g
      ));

      console.log('✅ Duty assigned');
    } catch (error) {
      console.error('❌ Error assigning duty:', error);
      alert('Error assigning duty');
    }
  };

  const handleCheckUpcomingGames = async () => {
    if (!selectedClub) return;

    try {
      const result = await checkAndSendUpcomingGameReminders(selectedClub.id);
      if (result.success) {
        if (result.reminders.length === 0) {
          alert('No upcoming games requiring reminders at this time.');
        } else {
          const message = result.reminders.map(r =>
            `${r.gameName}: ${r.count} reminders sent`
          ).join('\n');
          alert(`Reminders sent:\n${message}`);
        }
      } else {
        alert('Error checking upcoming games');
      }
    } catch (error) {
      console.error('❌ Error checking upcoming games:', error);
      alert('Error checking games');
    }
  };



  const handleAddExpense = async (expenseData) => {
    if (!selectedClub) return;

    try {
      console.log('🔵 Adding expense with new flexible structure');
      console.log('🔵 Received expenseData:', expenseData);

      // Extract fields from expenseData
      const {
        type,
        amount,
        date,
        gameId,
        tournamentId,
        receipt,
        receiptName,
        // New flexible fields
        paidBy, // 'player' or 'club'
        splitType, // 'squad', 'tournament', or 'club'
        addedByPlayerId,
        addedByPlayerName,
        reimbursementStatus,
        // Squad split data
        squadSize,
        superSubCount,
        regularPlayerCount,
        perPlayerCost,
        perSuperSubCost,
        // Legacy compatibility
        isDividable,
        isTournamentFee,
        clubCovered,
        settled
      } = expenseData;

      // Validate required fields
      if (!type || !amount || !date) {
        alert('Missing required fields: type, amount, or date');
        return;
      }

      console.log('🔵 Extracted amount:', amount, 'type:', typeof amount);
      console.log('🔵 Split type:', splitType, 'Paid by:', paidBy);

      // Get tournament name if tournamentId provided
      let tournamentName = null;
      if (tournamentId) {
        const tournament = tournaments.find(t => t.id === tournamentId);
        tournamentName = tournament?.name || null;
      } else if (gameId) {
        // If no tournamentId but has gameId, get tournament from game
        const game = games.find(g => g.id === gameId);
        if (game?.tournamentId) {
          const tournament = tournaments.find(t => t.id === game.tournamentId);
          tournamentName = tournament?.name || null;
        }
      }

      // Get game name if gameId provided
      let gameName = null;
      let splitAmong = null;
      let splitAmongDetails = null;
      let amountPerPlayer = null;

      if (gameId) {
        const game = games.find(g => g.id === gameId);
        gameName = game?.name || null;

        // For dividable expenses (Match Fee, Lunch, Snacks, etc.), split among playing11
        if (isDividable && game?.playing11 && game.playing11.length > 0) {
          splitAmong = [...game.playing11]; // Array of player IDs

          // Calculate per-player amount considering super subs pay half
          const superSubs = game.superSubs || [];
          const regularPlayers = game.playing11.filter(id => !superSubs.includes(id));
          const effectiveShares = regularPlayers.length + (superSubs.length / 2);
          const costPerShare = Number(amount) / effectiveShares;

          // Build detailed breakdown
          splitAmongDetails = game.playing11.map(playerId => {
            const isSuperSub = superSubs.includes(playerId);
            const playerAmount = isSuperSub ? costPerShare / 2 : costPerShare;

            // Find player name
            let playerName = 'Unknown';
            for (const team of teams) {
              if (team.players) {
                const player = team.players.find(p => p.id === playerId || p.oderId === playerId || p.userId === playerId);
                if (player) {
                  playerName = player.name;
                  break;
                }
              }
            }

            return {
              oderId: playerId,
              name: playerName,
              amount: Math.round(playerAmount * 100) / 100,
              isSuperSub
            };
          });

          amountPerPlayer = costPerShare; // Regular player amount

          console.log('🔵 Split among', splitAmong.length, 'players');
          console.log('🔵 Regular players:', regularPlayers.length, '| Super subs:', superSubs.length);
        }
      }

      // Add expense to Firestore
      const expensesRef = collection(db, 'expenses');
      const expenseDoc = await addDoc(expensesRef, {
        type,
        amount: Number(amount), // Ensure it's a number
        date,
        gameId: gameId || null,
        gameName: gameName,
        tournamentId: tournamentId || (gameId ? games.find(g => g.id === gameId)?.tournamentId : null),
        tournamentName: tournamentName,
        receipt: receipt || null,
        receiptName: receiptName || null,

        // New flexible fields
        paidBy: paidBy || 'player', // 'player' or 'club'
        splitType: splitType || 'squad', // 'squad', 'tournament', or 'club'
        addedByPlayerId: addedByPlayerId || null,
        addedByPlayerName: addedByPlayerName || null,
        reimbursementStatus: reimbursementStatus || null,

        // Squad split data (when splitType === 'squad')
        squadSize: squadSize || null,
        superSubCount: superSubCount || null,
        regularPlayerCount: regularPlayerCount || null,
        perPlayerCost: perPlayerCost || null,
        perSuperSubCost: perSuperSubCost || null,
        splitAmong: splitAmong,
        splitAmongDetails: splitAmongDetails,
        amountPerPlayer: amountPerPlayer,

        // Legacy compatibility fields
        isDividable: isDividable || false,
        isTournamentFee: isTournamentFee || false,
        clubCovered: clubCovered || false,
        settled: isTournamentFee ? false : null,

        // Metadata
        clubId: selectedClub.id,
        createdAt: new Date().toISOString(),
        createdBy: user.email,
        createdByName: user.displayName || user.email.split('@')[0],

        // Approval logic:
        // - Club-paid expenses: auto-approved (no reimbursement needed)
        // - Club expenses (splitType='club'): auto-approved (not split)
        // - Player-paid expenses: pending approval (for reimbursement)
        approvalStatus: (paidBy === 'club' || splitType === 'club') ? 'approved' : 'pending',
        description: type
      });

      console.log('✅ Expense added:', expenseDoc.id);

      // Send notifications if expense is approved and split among players
      if ((paidBy === 'club' || splitType === 'club') && splitAmong && splitAmong.length > 0) {
        console.log('📧 Sending expense notifications to affected players');

        const savedExpense = {
          id: expenseDoc.id,
          description: type,
          amount: Number(amount),
          clubId: selectedClub.id,
          gameId: gameId,
          gameName: gameName
        };

        // Get game data if available
        let gameData = null;
        if (gameId) {
          gameData = games.find(g => g.id === gameId);
        }

        // Send notifications to affected players
        const notificationResult = await notifyPlayersAboutExpenseSplit(
          savedExpense,
          gameData,
          splitAmong
        );

        if (notificationResult.success) {
          console.log(`✅ Notified ${notificationResult.count} players about expense`);
        }
      }

      await loadExpenses();
      setView('expense-report');
    } catch (error) {
      console.error('❌ Error adding expense:', error);
      alert('Failed to add expense: ' + error.message);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense? This action cannot be undone.')) {
      return;
    }

    try {
      console.log('🔵 Deleting expense:', expenseId);

      // Get the expense to check if it was approved
      const expenseRef = doc(db, 'expenses', expenseId);
      const expenseSnap = await getDoc(expenseRef);

      if (!expenseSnap.exists()) {
        alert('Expense not found');
        return;
      }

      const expense = { id: expenseSnap.id, ...expenseSnap.data() };

      // If expense was approved, we need to delete the wallet transaction
      // Relaxed check: just look for transactions linked to this expenseId
      if (expense.approvalStatus === 'approved') {
        console.log('🔵 Removing wallet transaction for deleted approved expense');

        // Find and delete the wallet transaction for this expense
        const walletRef = collection(db, 'walletTransactions');
        // Query by expenseId and clubId primarily
        const q = query(
          walletRef,
          where('expenseId', '==', expenseId),
          where('clubId', '==', selectedClub.id)
        );
        const walletSnapshot = await getDocs(q);

        const deletePromises = [];
        walletSnapshot.forEach((docSnap) => {
          console.log(`  - Deleting wallet transaction: ${docSnap.id}`);
          deletePromises.push(deleteDoc(doc(db, 'walletTransactions', docSnap.id)));
        });
        await Promise.all(deletePromises);

        console.log(`✅ Deleted ${walletSnapshot.size} wallet transaction(s)`);
      }

      // Delete any notifications related to this expense
      // Also relaxed: just matching expenseId
      console.log('🔵 Deleting related notifications for expense:', expenseId);
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('expenseId', '==', expenseId),
        where('clubId', '==', selectedClub.id)
      );
      const notificationSnapshot = await getDocs(q);

      const deletePromises = [];
      notificationSnapshot.forEach((docSnap) => {
        console.log(`  - Deleting notification: ${docSnap.id}`);
        deletePromises.push(deleteDoc(doc(db, 'notifications', docSnap.id)));
      });
      await Promise.all(deletePromises);

      console.log(`✅ Deleted ${notificationSnapshot.size} related notifications`);

      // Send notification to player about deletion
      if (expense.addedByPlayerId) {
        const notificationsRef = collection(db, 'notifications');
        await addDoc(notificationsRef, {
          userId: expense.addedByPlayerId,
          clubId: selectedClub.id,
          type: 'expense_deleted',
          message: `Your expense "${expense.description}" (${formatCurrency(expense.amount)}) was deleted by admin${expense.approvalStatus === 'approved' ? ' and removed from your wallet' : ''}`,
          read: false,
          createdAt: new Date().toISOString()
        });

        console.log('✅ Notification sent to player');
      }

      // Delete the expense
      await deleteDoc(expenseRef);

      console.log('✅ Expense deleted');
      alert('Expense deleted successfully!' + (expense.approvalStatus === 'approved' ? ' Wallet transaction reversed.' : ''));
      await loadExpenses();
    } catch (error) {
      console.error('❌ Error deleting expense:', error);
      console.error('Error details:', error.message);
      console.error('Error code:', error.code);
      alert(`Error deleting expense: ${error.message}`);
    }
  };

  // Handle settling a tournament fee - split among all players who played in tournament games
  const handleSettleTournamentFee = async (expense) => {
    if (!expense.tournamentId) {
      alert('This expense is not linked to a tournament');
      return;
    }

    try {
      console.log('🔵 Settling tournament fee:', expense.id);
      console.log('  - Tournament ID:', expense.tournamentId);

      // Find all games in this tournament
      const tournamentGames = games.filter(g => g.tournamentId === expense.tournamentId);
      console.log('  - Tournament games:', tournamentGames.length);

      if (tournamentGames.length === 0) {
        alert('No games found for this tournament. Add games before settling.');
        return;
      }

      // Check that all games have playing11 selected
      const gamesWithoutSquad = tournamentGames.filter(g => !g.playing11 || g.playing11.length === 0);
      if (gamesWithoutSquad.length > 0) {
        alert(`${gamesWithoutSquad.length} game(s) don't have a playing squad selected yet.\n\nPlease select playing 11/12 for all games before settling.`);
        return;
      }

      // Calculate fee per game
      const feePerGame = expense.amount / tournamentGames.length;
      console.log('  - Fee per game:', feePerGame);

      // Calculate each player's share based on games they played
      const playerShares = {}; // { oderId: { oderId, totalOwed, gamesPlayed, name } }

      tournamentGames.forEach(game => {
        const squadSize = game.playing11.length;
        const feePerPlayerThisGame = feePerGame / squadSize;

        game.playing11.forEach(oderId => {
          if (!playerShares[oderId]) {
            // Find player name
            let playerName = 'Unknown';
            for (const team of teams) {
              if (team.players) {
                const player = team.players.find(p => p.id === oderId || p.oderId === oderId || p.userId === oderId);
                if (player) {
                  playerName = player.name;
                  break;
                }
              }
            }

            playerShares[oderId] = {
              oderId,
              name: playerName,
              totalOwed: 0,
              gamesPlayed: 0,
              gameDetails: []
            };
          }

          playerShares[oderId].totalOwed += feePerPlayerThisGame;
          playerShares[oderId].gamesPlayed += 1;
          playerShares[oderId].gameDetails.push({
            gameId: game.id,
            gameName: game.name,
            amount: feePerPlayerThisGame
          });
        });
      });

      const playerList = Object.values(playerShares);
      console.log('  - Players with shares:', playerList.length);

      // Build confirmation message
      const totalCalculated = playerList.reduce((sum, p) => sum + p.totalOwed, 0);
      const playerSummary = playerList
        .sort((a, b) => b.gamesPlayed - a.gamesPlayed)
        .slice(0, 10)
        .map(p => `  ${p.name}: ${p.gamesPlayed} game(s) = ${formatCurrency(p.totalOwed)}`)
        .join('\n');

      const confirmMsg = `Settle Tournament Fee:\n\n` +
        `Total Amount: ${formatCurrency(expense.amount)}\n` +
        `Games: ${tournamentGames.length}\n` +
        `Fee per Game: ${formatCurrency(feePerGame)}\n` +
        `Unique Players: ${playerList.length}\n\n` +
        `Player Breakdown (top 10):\n${playerSummary}` +
        `${playerList.length > 10 ? `\n  ...and ${playerList.length - 10} more` : ''}\n\n` +
        `Calculated Total: ${formatCurrency(totalCalculated)}\n\n` +
        `This will update each player's balance due. Continue?`;

      if (!confirm(confirmMsg)) {
        return;
      }

      // Prepare splitAmong with amounts for each player
      const splitAmongWithAmounts = playerList.map(p => ({
        oderId: p.oderId,
        name: p.name,
        amount: Math.round(p.totalOwed * 100) / 100, // Round to 2 decimal places
        gamesPlayed: p.gamesPlayed,
        // Flag external players for payment tracking
        isExternal: p.oderId.startsWith('guest-') ||
          p.oderId.startsWith('guest_') ||
          p.oderId.startsWith('player_')
      }));

      // Update the expense with settlement info
      // PlayerDashboard will read splitAmongDetails to calculate player balances
      const expenseRef = doc(db, 'expenses', expense.id);
      await updateDoc(expenseRef, {
        settled: true,
        settledAt: new Date().toISOString(),
        settledBy: user.email,
        splitAmong: playerList.map(p => p.oderId), // Array of player IDs for backward compatibility
        splitAmongDetails: splitAmongWithAmounts, // Detailed breakdown with amounts
        feePerGame: feePerGame,
        gamesCount: tournamentGames.length,
        playersCount: playerList.length
      });

      // CRITICAL: Also mark the tournament itself as settled so the player dashboard
      // unlocks PART 2 game expenses for this tournament (tournament.isSettled gate).
      const tournamentRef = doc(db, 'tournaments', expense.tournamentId);
      await updateDoc(tournamentRef, {
        isSettled: true,
        settledAt: new Date().toISOString(),
        settledBy: user.email
      });

      // Count external vs regular players for the message
      const externalPlayers = playerList.filter(p =>
        p.oderId.startsWith('guest-') || p.oderId.startsWith('guest_') || p.oderId.startsWith('player_')
      );

      console.log('✅ Tournament fee settled');
      let successMsg = `Tournament fee settled successfully!\n\n${playerList.length} players across ${tournamentGames.length} games.`;
      successMsg += `\n\nEach player's balance has been updated based on games played.`;
      if (externalPlayers.length > 0) {
        successMsg += `\n\n⚠️ ${externalPlayers.length} external player(s) - track their payments in Expense Report.`;
      }
      alert(successMsg);

      // Reload expenses to reflect changes
      await loadExpenses();
    } catch (error) {
      console.error('❌ Error settling tournament fee:', error);
      alert(`Error settling tournament fee: ${error.message}`);
    }
  };

  // Mark a tournament fee as club covered (not split among players)
  const handleMarkClubCovered = async (expense) => {
    if (!expense.tournamentId) {
      alert('This expense is not linked to a tournament');
      return;
    }

    const confirmMsg = `Mark as Club Covered?\n\n` +
      `Amount: ${formatCurrency(expense.amount)}\n\n` +
      `This expense will NOT be split among players.\n` +
      `The club will cover this cost entirely.\n\n` +
      `Continue?`;

    if (!confirm(confirmMsg)) {
      return;
    }

    try {
      console.log('🔵 Marking tournament fee as club covered:', expense.id);

      const expenseRef = doc(db, 'expenses', expense.id);
      await updateDoc(expenseRef, {
        clubCovered: true,
        clubCoveredAt: new Date().toISOString(),
        clubCoveredBy: user.email,
        // Clear any split data
        splitAmong: null,
        splitAmongDetails: null,
        settled: false
      });

      console.log('✅ Tournament fee marked as club covered');
      alert('Expense marked as club covered. It will not affect player balances.');

      await loadExpenses();
    } catch (error) {
      console.error('❌ Error marking expense as club covered:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Mark an external player's expense share as paid
  const handleMarkExternalPlayerPaid = async (expenseId, playerId, playerName, amount, paymentMethod) => {
    try {
      console.log('🔵 Marking external player payment:', { expenseId, playerId, playerName, amount, paymentMethod });

      const expenseRef = doc(db, 'expenses', expenseId);
      const expenseSnap = await getDoc(expenseRef);

      if (!expenseSnap.exists()) {
        alert('Expense not found');
        return;
      }

      const expenseData = expenseSnap.data();
      const externalPayments = expenseData.externalPayments || {};

      // Add or update payment record for this player
      externalPayments[playerId] = {
        playerId,
        playerName,
        amount,
        paymentMethod,
        paidAt: new Date().toISOString(),
        markedBy: user.email,
        markedByName: user.displayName || user.email.split('@')[0]
      };

      await updateDoc(expenseRef, {
        externalPayments
      });

      console.log('✅ External player payment marked');
      alert(`${playerName}'s payment of ${formatCurrency(amount)} via ${paymentMethod} has been recorded.`);

      await loadExpenses();
    } catch (error) {
      console.error('❌ Error marking external player payment:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Unmark an external player's payment (if marked by mistake)
  const handleUnmarkExternalPlayerPaid = async (expenseId, playerId, playerName) => {
    if (!confirm(`Remove payment record for ${playerName}?`)) return;

    try {
      console.log('🔵 Unmarking external player payment:', { expenseId, playerId });

      const expenseRef = doc(db, 'expenses', expenseId);
      const expenseSnap = await getDoc(expenseRef);

      if (!expenseSnap.exists()) {
        alert('Expense not found');
        return;
      }

      const expenseData = expenseSnap.data();
      const externalPayments = { ...(expenseData.externalPayments || {}) };

      delete externalPayments[playerId];

      await updateDoc(expenseRef, {
        externalPayments
      });

      console.log('✅ External player payment unmarked');
      await loadExpenses();
    } catch (error) {
      console.error('❌ Error unmarking external player payment:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleApproveExpense = async (expense, note = '') => {
    // Check treasurer permission first
    if (!isTreasurer()) {
      alert('Only treasurers can approve expenses. Please contact your club treasurer.');
      return;
    }

    // Prevent admin from approving their own expense (Creator or Beneficiary)
    if (expense.createdBy === user.email || (expense.addedByPlayerId && expense.addedByPlayerId === user.uid)) {
      alert('You cannot approve your own expense. Another treasurer must approve it.');
      return;
    }

    try {
      console.log('💰 TREASURER Approving expense:', expense.id);
      console.log('Approved by:', user.displayName, '(', getRoleBadge(), ')');

      const expenseRef = doc(db, 'expenses', expense.id);
      await updateDoc(expenseRef, {
        approvalStatus: 'approved',
        approvedBy: user.email,
        approvedByName: user.displayName || user.email.split('@')[0], // Add name for reports
        approvedByRole: getRoleBadge(), // Track the approver's role
        approvedAt: new Date().toISOString(),
        approvalNote: note // Save approval note
      });

      // Create wallet transaction for the player
      if (expense.addedByPlayerId && expense.amount) {
        // Get tournament info from game
        const game = games.find(g => g.id === expense.gameId);
        const tournamentId = game?.tournamentId || null;
        const tournamentName = tournamentId ? tournaments.find(t => t.id === tournamentId)?.name : null;

        const walletRef = collection(db, 'walletTransactions');
        await addDoc(walletRef, {
          userId: expense.addedByPlayerId,
          clubId: selectedClub.id,
          type: 'expense_reimbursement',
          amount: expense.amount,
          description: `Reimbursement: ${expense.description}`,
          expenseId: expense.id,
          gameId: expense.gameId,
          gameName: expense.gameName,
          tournamentId: tournamentId,  // NEW
          tournamentName: tournamentName,  // NEW
          status: 'approved',
          createdAt: new Date().toISOString(),
          approvedBy: user.email
        });
        console.log('✅ Wallet transaction created for player');
      }

      // Send notification to player
      if (expense.addedByPlayerId) {
        const notificationsRef = collection(db, 'notifications');
        await addDoc(notificationsRef, {
          userId: expense.addedByPlayerId,
          clubId: selectedClub.id,
          type: 'expense_approved',
          message: `Your expense "${expense.description}" (${formatCurrency(expense.amount)}) has been approved and added to your wallet`,
          gameDate: expense.gameName || 'N/A',
          expenseId: expense.id,
          read: false,
          createdAt: new Date().toISOString()
        });
      }

      console.log('✅ Expense approved');
      alert('Expense approved successfully! Amount added to player wallet.');
      await loadExpenses();
      await loadWalletTransactions(); // Reload wallet transactions too
    } catch (error) {
      console.error('❌ Error approving expense:', error);
      alert('Error approving expense');
    }
  };

  const handleDenyExpense = async (expense, note = '') => {
    // Check treasurer permission first
    if (!isTreasurer()) {
      alert('Only treasurers can deny expenses. Please contact your club treasurer.');
      return;
    }

    // Prevent admin from denying their own expense (Creator or Beneficiary)
    if (expense.createdBy === user.email || (expense.addedByPlayerId && expense.addedByPlayerId === user.uid)) {
      alert('You cannot deny your own expense. Another treasurer must review it.');
      return;
    }

    // Use provided note or prompt if not provided
    let reason = note;
    if (!reason) {
      reason = prompt('Please provide a reason for denying this expense:');
    }

    if (!reason) return;

    try {
      console.log('❌ TREASURER Denying expense:', expense.id);
      console.log('Denied by:', user.displayName, '(', getRoleBadge(), ')');

      // If expense was previously approved, reverse the wallet transaction
      if (expense.approvalStatus === 'approved' && expense.addedByPlayerId && expense.amount) {
        console.log('🔵 Reversing wallet transaction for previously approved expense');

        // Create a reversal wallet transaction (negative amount)
        const walletRef = collection(db, 'walletTransactions');
        await addDoc(walletRef, {
          userId: expense.addedByPlayerId,
          clubId: selectedClub.id,
          type: 'expense_reversal',
          amount: -expense.amount, // Negative to deduct from wallet
          description: `Expense denied: ${expense.description}`,
          expenseId: expense.id,
          gameId: expense.gameId || null,
          gameName: expense.gameName || null,
          status: 'denied',
          createdAt: new Date().toISOString(),
          deniedBy: user.email,
          denialReason: reason
        });

        console.log('✅ Wallet transaction reversed');
      }

      // Update expense status to denied
      const expenseRef = doc(db, 'expenses', expense.id);
      await updateDoc(expenseRef, {
        approvalStatus: 'denied',
        deniedBy: user.email,
        deniedByName: user.displayName || user.email.split('@')[0], // Add name for reports
        deniedByRole: getRoleBadge(), // Track the denier's role
        deniedAt: new Date().toISOString(),
        rejectionReason: reason
      });

      // Send notification to player
      if (expense.addedByPlayerId) {
        const notificationsRef = collection(db, 'notifications');
        await addDoc(notificationsRef, {
          userId: expense.addedByPlayerId,
          clubId: selectedClub.id,
          type: 'expense_denied',
          message: `Your expense "${expense.description}" (${formatCurrency(expense.amount)}) has been denied${expense.approvalStatus === 'approved' ? ' and removed from your wallet' : ''}`,
          reason: reason,
          gameDate: expense.gameName || 'N/A',
          expenseId: expense.id,
          read: false,
          createdAt: new Date().toISOString()
        });
      }

      console.log('✅ Expense denied');
      alert('Expense denied' + (expense.approvalStatus === 'approved' ? '. Wallet transaction reversed.' : ''));
      await loadExpenses();
      await loadWalletTransactions(); // Reload wallet transactions too
    } catch (error) {
      console.error('❌ Error denying expense:', error);
      alert('Error denying expense');
    }
  };

  // Mark an admin-paid expense as reimbursed
  const handleMarkReimbursed = async (expense) => {
    if (!expense.paidBy || expense.paidBy !== 'admin') {
      alert('This expense was not paid by an admin');
      return;
    }

    if (expense.reimbursementStatus === 'reimbursed') {
      alert('This expense has already been reimbursed');
      return;
    }

    const confirmReimburse = confirm(
      `Mark this expense as reimbursed?\n\n` +
      `Expense: ${expense.description}\n` +
      `Amount: ${formatCurrency(expense.amount)}\n` +
      `Paid by: ${expense.paidByAdminName || 'Admin'}\n\n` +
      `This indicates the club has reimbursed the admin.`
    );

    if (!confirmReimburse) return;

    try {
      console.log('🔵 Marking expense as reimbursed:', expense.id);

      const expenseRef = doc(db, 'expenses', expense.id);
      await updateDoc(expenseRef, {
        reimbursementStatus: 'reimbursed',
        reimbursedAt: new Date().toISOString(),
        reimbursedBy: user.email,
        reimbursedByName: user.displayName || user.email.split('@')[0]
      });

      console.log('✅ Expense marked as reimbursed');
      alert(`✅ Expense marked as reimbursed.\n\n${expense.paidByAdminName || 'Admin'} has been reimbursed ${formatCurrency(expense.amount)}.`);
      await loadExpenses();
    } catch (error) {
      console.error('❌ Error marking expense as reimbursed:', error);
      alert('Error marking expense as reimbursed');
    }
  };

  const handleApprovePaymentSubmission = async (submission, note = null, receivedDate = null) => {
    // Check treasurer permission first
    if (!isTreasurer()) {
      alert('Only treasurers can approve payment submissions. Please contact your club treasurer.');
      return;
    }

    // Prevent self-approval only for non-treasurers.
    // Treasurers can approve their own payments since their financial oversight
    // role is separate from their player role.
    if (!isTreasurer() && (submission.playerId === user.uid || submission.playerEmail === user.email)) {
      alert('You cannot approve your own payment submission. Please ask your club treasurer to approve it.');
      return;
    }

    if (submission.status === 'approved') {
      alert('This payment has already been approved.');
      return;
    }

    try {
      console.log('💳 TREASURER Approving payment submission:', submission.id);
      console.log('Approved by:', user.displayName, '(', getRoleBadge(), ')');

      // Use provided received date or fallback to now (UTC ISO String but date part matters)
      // Ideally we want to store the date part explicitly or a full timestamp
      const effectiveDate = receivedDate ? new Date(receivedDate).toISOString() : new Date().toISOString();

      // 1. Update submission status
      const submissionRef = doc(db, 'paymentSubmissions', submission.id);
      await updateDoc(submissionRef, {
        status: 'approved',
        approvedBy: user.email,
        approvedByName: user.displayName || user.email.split('@')[0],
        approvedByRole: getRoleBadge(), // Track the approver's role
        approvedAt: new Date().toISOString(),
        fundsReceivedAt: effectiveDate, // Store the specific received date
        treasurerNote: note // Save the note
      });

      // 2. Create wallet transaction as SINGLE SOURCE OF TRUTH for payments
      // No more separate 'payments' collection - everything in walletTransactions
      const walletRef = collection(db, 'walletTransactions');
      await addDoc(walletRef, {
        userId: submission.playerId,
        clubId: submission.clubId,
        type: 'payment',
        amount: -submission.amount, // Negative = reduces what player owes
        description: `Payment via ${submission.paymentMethod}${note ? ` - ${note}` : ''}`,
        playerName: submission.playerName,
        playerEmail: submission.playerEmail,
        paymentMethod: submission.paymentMethod,
        paymentDate: submission.paymentDate,
        receivedDate: effectiveDate, // Store received date in wallet txn too
        transactionId: submission.transactionId || null,
        receiptURL: submission.receiptURL,
        notes: note || submission.notes || null, // Prioritize treasurer note or fall back to user note
        treasurerNote: note, // Explicitly save treasurer note field
        status: 'approved',
        createdAt: new Date().toISOString(),
        approvedBy: user.email,
        approvedByName: user.displayName || user.email.split('@')[0],
        submissionId: submission.id,
        isDonation: submission.isDonation || false, // Track if this was a donation
        // Preserve tournament scope from the original submission so the
        // Tournament Players tab can correctly filter this payment
        tournamentId: submission.tournamentId || null,
        tournamentName: submission.tournamentName || null
      });

      // 3. Send notification to player
      const notificationsRef = collection(db, 'notifications');
      await addDoc(notificationsRef, {
        userId: submission.playerId,
        clubId: submission.clubId,
        type: 'payment_approved',
        message: `Your payment of ${formatCurrency(submission.amount)} has been approved${note ? `. Note: ${note}` : ''}`,
        paymentMethod: submission.paymentMethod,
        amount: submission.amount,
        read: false,
        createdAt: new Date().toISOString()
      });

      // 4. Remove/delete payment_submitted notifications for all other admins
      try {
        const adminNotificationsQuery = query(
          notificationsRef,
          where('type', '==', 'payment_submitted'),
          where('paymentId', '==', submission.id)
        );
        const adminNotificationsSnap = await getDocs(adminNotificationsQuery);

        for (const notifDoc of adminNotificationsSnap.docs) {
          await deleteDoc(doc(db, 'notifications', notifDoc.id));
        }
        console.log('✅ Removed payment notifications for other admins');
      } catch (notifError) {
        console.error('Error removing admin notifications:', notifError);
      }

      console.log('✅ Payment submission approved');
      alert('Payment approved successfully! Player balance updated.');

      // Reload data
      await loadPaymentSubmissions();
      await loadWalletTransactions();
      await loadPayments();

    } catch (error) {
      console.error('❌ Error approving payment submission:', error);
      alert('Error approving payment: ' + error.message);
    }
  };

  const handleDenyPaymentSubmission = async (submission, providedReason) => {
    const reason = providedReason || prompt('Please provide a reason for denying this payment:');
    if (!reason) return;

    try {
      console.log('🔵 Denying payment submission:', submission.id);

      // Update submission status
      const submissionRef = doc(db, 'paymentSubmissions', submission.id);
      await updateDoc(submissionRef, {
        status: 'denied',
        deniedBy: user.email,
        deniedByName: user.displayName || user.email.split('@')[0],
        deniedAt: new Date().toISOString(),
        denialReason: reason
      });

      // Send notification to player
      const notificationsRef = collection(db, 'notifications');
      await addDoc(notificationsRef, {
        userId: submission.playerId,
        clubId: submission.clubId,
        type: 'payment_denied',
        message: `Your payment submission of ${formatCurrency(submission.amount)} was denied`,
        reason: reason,
        amount: submission.amount,
        read: false,
        createdAt: new Date().toISOString()
      });

      // Remove/delete payment_submitted notifications for all other admins
      try {
        const adminNotificationsQuery = query(
          notificationsRef,
          where('type', '==', 'payment_submitted'),
          where('paymentId', '==', submission.id)
        );
        const adminNotificationsSnap = await getDocs(adminNotificationsQuery);

        for (const notifDoc of adminNotificationsSnap.docs) {
          await deleteDoc(doc(db, 'notifications', notifDoc.id));
        }
        console.log('✅ Removed payment notifications for other admins');
      } catch (notifError) {
        console.error('Error removing admin notifications:', notifError);
      }

      console.log('✅ Payment submission denied');
      alert('Payment denied. Player has been notified.');

      // Reload data
      await loadPaymentSubmissions();

    } catch (error) {
      console.error('❌ Error denying payment submission:', error);
      alert('Error denying payment. Please try again.');
    }
  };

  const handleDeleteWalletTransaction = async (transactionId, userId, amount) => {
    // Check treasurer permission first
    if (!isTreasurer() && !isSuperAdmin() && user?.clubInfo?.role !== 'admin') {
      alert('Only administrators or treasurers can delete wallet transactions.');
      return;
    }

    try {
      console.log('🔴 Deleting wallet transaction:', transactionId);
      
      const transactionRef = doc(db, 'walletTransactions', transactionId);
      await deleteDoc(transactionRef);
      
      console.log('✅ Wallet transaction deleted successfully');
      alert(`Payment record of ${formatCurrency(Math.abs(amount))} deleted successfully.`);
      
      // Reload Data
      await loadWalletTransactions();
      await loadPayments();
      
    } catch (error) {
      console.error('❌ Error deleting wallet transaction:', error);
      alert('Error deleting wallet transaction: ' + error.message);
    }
  };

  const handleEditExpense = async () => {
    // Special validation for initial donations vs regular expenses
    const isInitialDonation = editingExpense?.isInitialDonation;

    if (isInitialDonation) {
      // Initial Donation validation - only description and amount required
      if (!expenseDescription || !expenseAmount) {
        alert('Please fill in description and amount');
        return;
      }
    } else {
      // Regular expense validation - needs game
      if (!editingExpense || !expenseDescription || !expenseAmount || !expenseGameId) {
        alert('Please fill in all required fields');
        return;
      }
    }

    try {
      console.log('🔵 Updating expense:', editingExpense.id);
      console.log('  - Is initial donation:', isInitialDonation);
      setUploadingReceipt(true);

      let receiptURL = editingExpense.receiptURL;

      // If user wants to remove old receipt
      if (removeOldReceipt) {
        receiptURL = null;
      }

      // Upload new receipt if provided
      if (expenseReceipt) {
        console.log('🔵 Uploading new receipt...');
        const receiptRef = ref(storage, `receipts/${selectedClub.id}/${Date.now()}_${expenseReceipt.name}`);
        await uploadBytes(receiptRef, expenseReceipt);
        receiptURL = await getDownloadURL(receiptRef);
        console.log('✅ New receipt uploaded:', receiptURL);
      }

      if (isInitialDonation) {
        // INITIAL DONATION UPDATE
        console.log('💰 Updating initial donation');
        console.log('  - New amount: $', expenseAmount);

        const expenseRef = doc(db, 'expenses', editingExpense.id);
        await updateDoc(expenseRef, {
          description: expenseDescription,
          amount: parseFloat(expenseAmount),
          amountPerPlayer: parseFloat(expenseAmount), // For reg fees, total = per player
          receiptURL: receiptURL,
          updatedAt: new Date().toISOString(),
          updatedBy: user.email,
          manuallyEdited: true, // Flag to indicate admin manually changed this
          manualEditReason: 'Admin manual correction'
        });

        console.log('✅ Initial Donation updated manually by admin');
        alert(`✅ Initial Donation updated to $${expenseAmount}\n\nNote: This was a manual edit. The smart system will not auto-update this fee unless the player joins a different ball type team.`);

      } else {
        // REGULAR EXPENSE UPDATE
        // Check if receipt is required for this expense type
        const receiptNotRequired = ['umpiring fee', 'umpiring assignment'];
        const requiresReceipt = !receiptNotRequired.some(type => expenseDescription.toLowerCase().startsWith(type));
        if (requiresReceipt && !receiptURL) {
          alert('Receipt is required for this expense type. Please upload a receipt.');
          setUploadingReceipt(false);
          return;
        }

        // Get game details
        const gameRef = doc(db, 'games', expenseGameId);
        const gameSnap = await getDoc(gameRef);

        if (!gameSnap.exists()) {
          alert('Game not found');
          setUploadingReceipt(false);
          return;
        }

        const game = gameSnap.data();
        const playing11 = game.playing11 || [];
        const amountPerPlayer = playing11.length > 0 ? parseFloat(expenseAmount) / playing11.length : 0;

        // Update expense
        const expenseRef = doc(db, 'expenses', editingExpense.id);
        await updateDoc(expenseRef, {
          description: expenseDescription,
          amount: parseFloat(expenseAmount),
          amountPerPlayer: amountPerPlayer,
          gameId: expenseGameId,
          gameName: game.name,
          splitAmong: playing11,
          receiptURL: receiptURL,
          updatedAt: new Date().toISOString(),
          updatedBy: user.email
        });

        console.log('✅ Regular expense updated');
        alert('Expense updated successfully!');
      }

      // Reset form
      setEditingExpense(null);
      setExpenseDescription('');
      setExpenseAmount('');
      setExpenseGameId('');
      setExpenseReceipt(null);
      setRemoveOldReceipt(false);
      setUploadingReceipt(false);

      await loadExpenses();
      setView('expense-report');
    } catch (error) {
      console.error('❌ Error updating expense:', error);
      alert('Error updating expense: ' + error.message);
      setUploadingReceipt(false);
    }
  };

  const renderView = () => {
    // Show Tournament Detail View if a tournament is selected for viewing
    if (viewingTournamentDetail) {
      return (
        <TournamentDetailView
          tournament={viewingTournamentDetail}
          games={games}
          expenses={expenses}
          teams={teams}
          walletTransactions={walletTransactions}
          players={players}
          isTreasurer={isTreasurer()}
          isAdmin={isUserAdmin()}
          currentUserId={user?.uid}
          currentUserEmail={user?.email}
          onBack={() => setViewingTournamentDetail(null)}
          onEditGame={(game) => {
            setEditingGame(game);
            setViewingTournamentDetail(null);
            setView('edit-game');
          }}
          onSelectSquad={(game) => {
            setSelectedGameForPlayers(game);
            setViewingTournamentDetail(null);
            setView('squad-selection');
          }}
          onImportGames={handleImportGames}
        />
      );
    }

    switch (view) {
      case 'team-migration':
        return <TeamMigrationTool clubs={clubs} onBack={() => setView(isSuperAdmin() ? 'clubs-list' : 'overview')} />;

      case 'tournament-migration':
        return <TournamentMigrationTool clubs={clubs} currentClubId={selectedClub?.id || '3dyhYJTLsoTrhCd31cnC'} onBack={() => setView(isSuperAdmin() ? 'clubs-list' : 'overview')} />;

      case 'clubs-list':
        const handleFixBadges = async () => {
          if (!confirm('Run manual fix for ALL payment approval badges? This will scan all users and update payment records.')) return;

          try {
            console.log('🔄 Starting badge fix...');

            // 1. Get all users to build a map of Email -> Role Data
            const usersSnap = await getDocs(collection(db, 'users'));
            const userMap = {};
            usersSnap.forEach(doc => {
              const u = doc.data();
              if (u.email) {
                userMap[u.email.toLowerCase()] = u;
              }
            });
            console.log(`✅ Loaded ${Object.keys(userMap).length} users`);

            // 2. Get all approved payment submissions
            const paymentsQuery = query(collection(db, 'paymentSubmissions'), where('status', '==', 'approved'));
            const paymentsSnap = await getDocs(paymentsQuery);
            console.log(`✅ Found ${paymentsSnap.size} approved payments`);

            let updatedCount = 0;

            // 3. Iterate and fix
            for (const docSnapshot of paymentsSnap.docs) {
              const payment = docSnapshot.data();
              const approverEmail = payment.approvedBy?.toLowerCase();

              if (approverEmail && userMap[approverEmail]) {
                const approver = userMap[approverEmail];

                // Reconstruct badge logic
                const roles = [];
                if (approver.role === 'admin') roles.push('Admin');
                if (approver.role === 'treasurer') roles.push('Treasurer');
                if (approver.role === 'super-admin') roles.push('Super Admin'); // Only if actually super-admin

                const additionalRoles = approver.additionalRoles || [];
                if (additionalRoles.includes('treasurer') && !roles.includes('Treasurer')) {
                  roles.push('Treasurer');
                }

                const correctBadge = roles.join(' & ');

                // Compare and update if different
                if (payment.approvedByRole !== correctBadge) {
                  console.log(`✏️ Fixing badge for payment ${docSnapshot.id} (Approver: ${approverEmail})`);
                  console.log(`   Old: "${payment.approvedByRole}" -> New: "${correctBadge}"`);

                  await updateDoc(doc(db, 'paymentSubmissions', docSnapshot.id), {
                    approvedByRole: correctBadge
                  });
                  updatedCount++;
                }
              }
            }

            console.log(`🎉 Finished! Updated ${updatedCount} records.`);
            alert(`Badge fix complete! Updated ${updatedCount} records.`);

          } catch (error) {
            console.error('❌ Error fixing badges:', error);
            alert('Error running fix script: ' + error.message);
          }
        };

        const handleFixDuplicateDonations = async () => {
          if (!confirm('Run duplicate fee cleanup? This will safely remove any redundant $250 Initial Donation fees.')) return;

          try {
            console.log('🔄 Starting duplicate donation cleanup...');

            // Get all initial donations using expenseType (catches old documents missing the boolean flag)
            const expensesQuery = query(
              collection(db, 'expenses'),
              where('expenseType', '==', 'Initial Donation')
            );
            const expensesSnap = await getDocs(expensesQuery);
            
            // Map by player ID
            const playerDonations = {};
            let deleteCount = 0;

            expensesSnap.forEach(docSnap => {
              const expense = docSnap.data();
              // Find the primary player this is split among
              const playerUid = expense.splitAmong && expense.splitAmong.length > 0 ? expense.splitAmong[0] : expense.addedByPlayerId;
              
              if (playerUid) {
                if (!playerDonations[playerUid]) {
                  // First one found, mark as keeper
                  playerDonations[playerUid] = { id: docSnap.id };
                } else {
                  // Duplicate found! We can delete it.
                  console.log(`🗑️ Deleting duplicate donation ${docSnap.id} for player ${playerUid}`);
                  deleteDoc(doc(db, 'expenses', docSnap.id));
                  deleteCount++;
                }
              }
            });

            console.log(`🎉 Finished cleanup! Deleted ${deleteCount} duplicate fees.`);
            alert(`Cleanup complete! Deleted ${deleteCount} redundant initial donations.`);
            
          } catch (error) {
            console.error('❌ Error cleaning up donations:', error);
            alert('Error running cleanup script: ' + error.message);
          }
        };

        return (
          <div className="max-w-6xl mx-auto">
            {/* Temporary Fix Button for Super Admins */}
            {isSuperAdmin() && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
                <div>
                  <h3 className="text-red-800 font-bold">⚠️ Data Maintenance</h3>
                  <p className="text-red-600 text-sm">Run this script to fix historical "Super Admin" badges on payment records.</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleFixBadges}
                    className="bg-red-600 text-white px-4 py-2 rounded shadow hover:bg-red-700 font-bold"
                  >
                    🛠️ FIX BADGES
                  </button>
                  <button
                    onClick={handleFixDuplicateDonations}
                    className="bg-orange-600 text-white px-4 py-2 rounded shadow hover:bg-orange-700 font-bold"
                  >
                    🧹 CLEAN DUPLICATE FEES
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Registered Clubs</h2>
                <p className="text-gray-600 mt-1">
                  {isSuperAdmin() ? 'Manage all clubs as super admin' : 'Clubs you administer'}
                </p>
              </div>
              <div className="flex gap-3">
                {isSuperAdmin() && (
                  <>
                    <button
                      onClick={() => setView('feedback-management')}
                      className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                    >
                      <MessageCircle size={20} />
                      View Feedback
                    </button>
                    <button
                      onClick={() => {
                        loadPlayers(null); // Load all players
                        setView('all-players');
                      }}
                      className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      <Users size={20} />
                      View All Players
                    </button>
                    <button
                      onClick={() => setView('team-migration')}
                      className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                    >
                      <Database size={20} />
                      Team Migration
                    </button>
                    <button
                      onClick={() => setView('tournament-migration')}
                      className="flex items-center gap-2 bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
                    >
                      <Trophy size={20} />
                      Tournament Migration
                    </button>
                    <button
                      onClick={() => setView('create-club')}
                      className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                    >
                      <Plus size={20} />
                      Create New Club
                    </button>
                  </>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              </div>
            ) : clubs.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Users size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Clubs Yet</h3>
                <p className="text-gray-600 mb-6">
                  {isSuperAdmin()
                    ? 'Create your first club to get started'
                    : 'You haven\'t been assigned to any clubs yet'}
                </p>
                {isSuperAdmin() && (
                  <button
                    onClick={() => setView('create-club')}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700"
                  >
                    Create Your First Club
                  </button>
                )}
              </div>
            ) : (
              <div>
                {/* Pending Clubs Section */}
                {isSuperAdmin() && clubs.some(c => c && c.id && c.name && c.status === 'pending') && (
                  <div className="mb-12">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Clock className="text-yellow-600" /> Pending Approvals
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {clubs.filter(c => c && c.id && c.name && c.status === 'pending').map(club => (
                        <div
                          key={club.id}
                          className="bg-yellow-50 border border-yellow-200 rounded-xl shadow-sm p-6"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-1">{club.name}</h3>
                              <p className="text-gray-600 text-sm">{club.location}</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApproveClub(club)}
                                className="bg-green-600 text-white hover:bg-green-700 px-3 py-1 rounded text-sm font-semibold shadow flex items-center gap-1"
                              >
                                <CheckCircle size={16} />
                                Approve
                              </button>
                              <button
                                onClick={() => handleDeleteClub(club.id)}
                                className="bg-red-100 text-red-600 hover:bg-red-200 px-3 py-1 rounded text-sm font-semibold flex items-center gap-1"
                              >
                                <X size={16} />
                                Reject
                              </button>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Admin Email:</span>
                              <span className="font-semibold truncate max-w-[150px]" title={club.adminEmail || 'Unknown'}>
                                {club.adminEmail || 'Admin user'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Created:</span>
                              <span className="font-semibold text-gray-800">
                                {club.createdAt ? new Date(club.createdAt).toLocaleDateString() : 'Unknown'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Active Clubs Section */}
                <div>
                  {isSuperAdmin() && clubs.some(c => c && c.id && c.name && c.status === 'pending') && (
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <CheckCircle className="text-green-600" /> Active Clubs
                    </h3>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {clubs.filter(club => club && club.id && club.name && club.status !== 'pending').map(club => (
                      <div
                        key={club.id}
                        className={`bg-white rounded-xl shadow-sm transition-shadow border-2 border-transparent ${isSuperAdmin()
                          ? ''
                          : 'cursor-pointer hover:shadow-md hover:border-green-500'
                          }`}
                        onClick={() => {
                          if (!isSuperAdmin()) {
                            setSelectedClub(club);
                            setView('overview');
                          }
                        }}
                      >
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-1">{club.name}</h3>
                              <p className="text-gray-600 text-sm">{club.location}</p>
                            </div>
                            {isSuperAdmin() ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClub(club);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                                  title="Edit Club"
                                >
                                  <Settings size={18} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClub(club.id);
                                  }}
                                  className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                                  title="Delete Club"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            ) : (
                              <ChevronRight className="text-gray-400" size={24} />
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Club Code:</span>
                              <span className="font-mono font-semibold text-green-600">{club.code}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Teams:</span>
                              <span className="font-semibold">{clubCounts[club.id]?.teams || 0}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Tournaments:</span>
                              <span className="font-semibold">{clubCounts[club.id]?.tournaments || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Edit Club Modal */}
            {editingClub && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Edit Club</h3>
                    <button
                      onClick={() => setEditingClub(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Club Name *
                      </label>
                      <input
                        type="text"
                        value={editClubName}
                        onChange={(e) => setEditClubName(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter club name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location *
                      </label>
                      <input
                        type="text"
                        value={editClubLocation}
                        onChange={(e) => setEditClubLocation(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter location"
                      />
                    </div>

                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                      <input
                        type="checkbox"
                        id="archiveClub"
                        checked={editClubIsArchived}
                        onChange={(e) => setEditClubIsArchived(e.target.checked)}
                        className="w-4 h-4 text-red-600 border-gray-300 rounded cursor-pointer ring-offset-2 ring-red-500"
                      />
                      <label htmlFor="archiveClub" className="text-sm font-bold text-red-700 cursor-pointer">
                        Archive Club (Read-Only Mode)
                      </label>
                    </div>

                    <p className="text-xs text-gray-500 px-1 leading-relaxed">
                      Archiving locks all RSVPs, payments, and expenses for standard players, freezing the club entirely as an immutable timeline.
                    </p>

                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={handleSaveClubEdit}
                        className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => setEditingClub(null)}
                        className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'player-role-management':
        return selectedClub && (
          <PlayerRoleManagement
            currentUser={user}
            selectedClub={selectedClub}
            onBack={() => setView('overview')}
          />
        );

      case 'club-detail':
        return (
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => setView('clubs-list')}
              className="text-gray-600 hover:text-gray-900 mb-6 flex items-center gap-2"
            >
              ← Back to Clubs
            </button>

            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{selectedClub.name}</h2>
                  <p className="text-gray-600 mt-1">{selectedClub.location}</p>
                </div>
                <button
                  onClick={handleCopyCode}
                  className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  {copied ? '✓ Copied!' : `Code: ${selectedClub.code}`}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <button
                  onClick={() => setView('overview')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-colors"
                >
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{teams.length}</div>
                    <div className="text-gray-600 mt-2">Teams</div>
                  </div>
                </button>

                <button
                  onClick={() => setView('club-players')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{players.length}</div>
                    <div className="text-gray-600 mt-2">Players</div>
                  </div>
                </button>

                <button
                  onClick={() => setView('registration-fee-validation')}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-colors"
                >
                  <div className="text-center">
                    {(() => {
                      const issues = validateAllRegistrationFees();
                      return (
                        <>
                          <div className={`text-3xl font-bold ${issues.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {issues.length > 0 ? '⚠️' : '✅'}
                          </div>
                          <div className="text-gray-900 font-semibold mt-2">Initial Donation Validation</div>
                          {issues.length > 0 && (
                            <div className="text-xs text-orange-600 mt-1">{issues.length} issue{issues.length !== 1 ? 's' : ''}</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </button>

                {isSuperAdmin() && (
                  <button
                    onClick={() => setView('admin-management')}
                    className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-colors"
                  >
                    <div className="text-center">
                      <Shield size={32} className="mx-auto text-purple-600 mb-2" />
                      <div className="text-gray-900 font-semibold">Manage Admins</div>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      case 'club-settings':
        // Only accessible by club admins for their club
        if (!selectedClub && !isSuperAdmin()) {
          setView('overview');
          return null;
        }

        return (
          <ClubSettings
            club={selectedClub}
            onBack={() => setView('overview')}
            onClubUpdated={(updatedClub) => {
              updateClub(updatedClub);
              setSelectedClub(updatedClub);
            }}
            onClubDeleted={(clubId) => {
              // Refresh clubs list or redirect
              window.location.reload();
            }}
            isSuperAdmin={isSuperAdmin()}
          />
        );

      case 'create-club':
        // Only super admins can create clubs
        if (!isSuperAdmin()) {
          setView('overview');
          return null;
        }

        return (
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => setView('clubs-list')}
              className="text-gray-600 hover:text-gray-900 mb-6"
            >
              ← Back to Clubs
            </button>

            <ClubCreation
              onCreateClub={handleCreateClub}
              onCancel={() => setView('clubs-list')}
            />
          </div>
        );

      case 'financial-reports':
        // Only accessible by club admins for their club (Super Admins restricted)
        if (!selectedClub || isSuperAdmin()) {
          return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 h-[50vh]">
              <div className="bg-white p-6 rounded-xl shadow-md text-center max-w-md">
                <span className="text-4xl mb-4 block">🚫</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Access Restricted</h3>
                <p className="text-gray-600 mb-4">
                  Financial reports are restricted to Club Admins and Treasurers.
                  Super Admins cannot view club finances.
                </p>
                <button
                  onClick={() => setView('overview')}
                  className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-700 font-semibold"
                >
                  Back to Overview
                </button>
              </div>
            </div>
          );
        }

        return (
          <div>
            <button
              onClick={() => setView('financial-reports-hub')}
              className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
            >
              ← Back to Reports Hub
            </button>

            <FinancialReports
              expenses={expenses}
              walletTransactions={walletTransactions}
              players={players}
              games={games}
              teams={teams}
              selectedClub={selectedClub}
              clubName={selectedClub.name}
              tournaments={tournaments}
              user={user} // Pass user for role checking
              canApprove={isTreasurer()} // Pass treasurer permission
              userRole={getRoleBadge()} // Pass user's role badge
              paymentSubmissions={paymentSubmissions}
              onUpdateExpense={async (expenseId, updates) => {
                try {
                  const expenseRef = doc(db, 'expenses', expenseId);
                  await updateDoc(expenseRef, {
                    ...updates,
                    updatedAt: new Date().toISOString(),
                    updatedBy: user.email
                  });
                  await loadExpenses();
                  return true;
                } catch (error) {
                  console.error('Error updating expense:', error);
                  return false;
                }
              }}
            />
          </div>
        );

      case 'overview':
        // ================= SUPER ADMIN RESTRICTED VIEW =================
        if (isSuperAdmin()) {
          return selectedClub && (
            <div className="max-w-4xl mx-auto">
              <button
                onClick={() => setView('clubs-list')}
                className="text-gray-600 mb-6 hover:text-gray-800 flex items-center gap-2"
              >
                ← Back to Clubs
              </button>

              <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <span className="text-3xl">🛡️</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h2>
                    <p className="text-gray-600">Managing: {selectedClub.name}</p>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-8">
                  <p className="text-blue-800 text-sm">
                    <strong>Note:</strong> Super Admin access is restricted to administrative tasks only.
                    You cannot view internal club data like finances, teams, or events.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setView('admin-management')}
                    className="flex flex-col items-center p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:shadow-md transition-all group"
                  >
                    <div className="p-3 bg-blue-100 rounded-full mb-3 group-hover:bg-blue-200 transition-colors">
                      <Users size={32} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Manage Admins</h3>
                    <p className="text-sm text-center text-gray-500 mt-1">Promote users to Club Admin</p>
                  </button>

                  <button
                    onClick={() => setView('club-settings')}
                    className="flex flex-col items-center p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-500 hover:shadow-md transition-all group"
                  >
                    <div className="p-3 bg-gray-100 rounded-full mb-3 group-hover:bg-gray-200 transition-colors">
                      <Settings size={32} className="text-gray-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Club Settings</h3>
                    <p className="text-sm text-center text-gray-500 mt-1">Configure basic club details</p>
                  </button>

                  <button
                    onClick={() => setView('team-migration')}
                    className="flex flex-col items-center p-6 bg-white border-2 border-indigo-200 rounded-xl hover:border-indigo-500 hover:shadow-md transition-all group"
                  >
                    <div className="p-3 bg-indigo-100 rounded-full mb-3 group-hover:bg-indigo-200 transition-colors">
                      <Database size={32} className="text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Deploy Team Migrations</h3>
                    <p className="text-sm text-center text-gray-500 mt-1">System duplicate teams cross-clubs.</p>
                  </button>

                  <button
                    onClick={() => setView('tournament-migration')}
                    className="flex flex-col items-center p-6 bg-white border-2 border-purple-200 rounded-xl hover:border-purple-500 hover:shadow-md transition-all group"
                  >
                    <div className="p-3 bg-purple-100 rounded-full mb-3 group-hover:bg-purple-200 transition-colors">
                      <Trophy size={32} className="text-purple-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Deploy Tournament Migrations</h3>
                    <p className="text-sm text-center text-gray-500 mt-1">Duplicate tournaments, games, & finances cross-clubs.</p>
                  </button>
                </div>
              </div>
            </div>
          );
        }

        // ================= STANDARD ADMIN VIEW =================
        const pendingExpenses = expenses.filter(e => e.approvalStatus === 'pending');
        return selectedClub && (
          <div className="max-w-6xl mx-auto">
            {/* Pending Player Approval Alert */}
            {pendingPlayers.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    <span className="font-semibold text-orange-800">
                      🔔 {pendingPlayers.length} player{pendingPlayers.length > 1 ? 's' : ''} awaiting approval
                    </span>
                  </div>
                  <button
                    onClick={() => setView('manage-players')}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-semibold"
                  >
                    Review Requests
                  </button>
                </div>
              </div>
            )}

            {/* Pending Expenses Alert */}
            {pendingExpenses.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold text-yellow-800">
                      {pendingExpenses.length} expense{pendingExpenses.length > 1 ? 's' : ''} pending approval
                    </span>
                  </div>
                  <button
                    onClick={() => setSearchParams({ view: 'manage-expenses', review: 'true' })}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-semibold"
                  >
                    Review Expenses
                  </button>
                </div>
              </div>
            )}

            {/* Pending Payment Submissions Alert */}
            {paymentSubmissions.filter(p => p.status === 'pending').length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="font-semibold text-blue-800">
                      💰 {paymentSubmissions.filter(p => p.status === 'pending').length} payment{paymentSubmissions.filter(p => p.status === 'pending').length > 1 ? 's' : ''} awaiting approval
                    </span>
                  </div>
                  <button
                    onClick={() => setSearchParams({ view: 'payment-management', review: 'true' })}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
                  >
                    Review Payments
                  </button>
                </div>
              </div>
            )}

            <OverviewView
              club={selectedClub}
              teams={teams}
              tournaments={tournaments}
              games={games}
              expenses={expenses}
              walletTransactions={walletTransactions}
              events={events}
              players={players}
              copied={copied}
              onCopyCode={handleCopyCode}
              onNavigate={setView}
              onSendReminders={handleCheckUpcomingGames}
              permissions={permissions}
            />
          </div>
        );

      case 'admin-management':
        return selectedClub && (
          <AdminManagement
            club={selectedClub}
            onBack={() => setView('club-detail')}
          />
        );

      case 'manage-expenses':
        return selectedClub && (
          <ExpenseManagement
            games={games}
            tournaments={tournaments}
            teams={teams}
            expenses={expenses} // Passing expenses for the list view
            players={[...players, ...pendingPlayers]}
            onAddExpense={handleAddExpense}
            onApproveExpense={handleApproveExpense}
            onDenyExpense={handleDenyExpense}
            onDeleteExpense={handleDeleteExpense}
            onSettleTournamentFee={handleSettleTournamentFee}
            onBack={() => setView('overview')}
            currentUser={user}
            isTreasurer={isTreasurer()} // Pass permissions
            clubId={selectedClub.id}
            clubName={selectedClub.name}
          />
        );

      case 'edit-expense':
        return (
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => {
                setView('expense-report');
                setEditingExpense(null);
                setExpenseDescription('');
                setExpenseAmount('');
                setExpenseGameId('');
                setExpenseReceipt(null);
                setRemoveOldReceipt(false);
              }}
              className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
            >
              ← Back to Expense Report
            </button>

            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Edit Expense</h2>

              <div className="space-y-4">
                {/* Expense Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expense Description *
                  </label>
                  {editingExpense?.isInitialDonation ? (
                    // Initial donations are locked to the label
                    <input
                      type="text"
                      value={getRegistrationFeeLabel(selectedClub)}
                      disabled
                      className="w-full px-4 py-3 border rounded-lg bg-gray-100 text-gray-700"
                    />
                  ) : (
                    <select
                      value={expenseDescription}
                      onChange={(e) => setExpenseDescription(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-white"
                    >
                      <option value="">Select expense type...</option>
                      <option value="Ground Rental">Ground Rental</option>
                      <option value="Lunch">Lunch</option>
                      <option value="Snacks & Drinks">Snacks & Drinks</option>
                      <option value="Umpiring Fee">Umpiring Fee</option>
                      <option value="Umpiring Assignment">Umpiring Assignment</option>
                      <option value="Tournament Fee">Tournament Fee</option>
                      <option value="Game Balls">Game Balls</option>
                      <option value="Team Kit Essentials">Team Kit Essentials</option>
                      <option value="Practice">Practice</option>
                      <option value="Other">Other</option>
                    </select>
                  )}
                  {editingExpense?.isInitialDonation && (
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Initial donations cannot change type. You can only edit the amount.
                    </p>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseAmount}
                    onChange={(e) => setExpenseAmount(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="0.00"
                  />
                  {editingExpense?.isInitialDonation && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Typical amounts: $250 (Leather ball) or $125 (Tennis ball only)
                    </p>
                  )}
                </div>

                {/* Game Selection - Only show for non-initial donations */}
                {!editingExpense?.isInitialDonation && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Game *
                    </label>
                    <select
                      value={expenseGameId}
                      onChange={(e) => setExpenseGameId(e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Choose a game...</option>
                      {games.map(game => {
                        const tournament = tournaments.find(t => t.id === game.tournamentId);
                        const team = teams.find(t => t.id === (game.homeTeamId || game.teamId));
                        return (
                          <option key={game.id} value={game.id}>
                            {game.name} - {game.date}
                            {tournament && ` (${tournament.name})`}
                            {team && ` - ${team.name}`}
                          </option>
                        );
                      })}
                    </select>
                    {expenseGameId && (() => {
                      const selectedGame = games.find(g => g.id === expenseGameId);
                      const playing11Count = selectedGame?.playing11?.length || 0;
                      const perPlayer = expenseAmount && playing11Count > 0
                        ? (parseFloat(expenseAmount) / playing11Count).toFixed(2)
                        : '0.00';

                      return (
                        <p className="text-sm text-gray-600 mt-2">
                          {playing11Count > 0
                            ? `Will be split among ${playing11Count} players (${formatCurrency(perPlayer)} per player)`
                            : 'No players selected for this game yet'}
                        </p>
                      );
                    })()}
                  </div>
                )}

                {/* Initial Donation Info */}
                {editingExpense?.isInitialDonation && (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📋 Initial Donation Edit</h4>
                    <p className="text-sm text-blue-800 mb-2">
                      You are editing a initial donation. This expense is not tied to any game.
                    </p>
                    <p className="text-xs text-blue-700">
                      💡 The smart system will auto-update this fee if the player joins a different ball type team, unless you want to override it permanently.
                    </p>
                  </div>
                )}

                {/* Current Receipt */}
                {editingExpense?.receiptURL && !removeOldReceipt && (
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Current Receipt</p>
                        <a
                          href={editingExpense.receiptURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          View current receipt
                        </a>
                      </div>
                      <button
                        onClick={() => setRemoveOldReceipt(true)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Remove Receipt
                      </button>
                    </div>
                  </div>
                )}

                {/* Upload New Receipt */}
                {(!editingExpense?.receiptURL || removeOldReceipt) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload {editingExpense?.receiptURL ? 'New ' : ''}Receipt (Optional)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setExpenseReceipt(e.target.files[0])}
                        className="hidden"
                        id="receipt-upload-edit"
                      />
                      <label htmlFor="receipt-upload-edit" className="cursor-pointer">
                        <div className="text-gray-600">
                          {expenseReceipt ? (
                            <div>
                              <p className="font-medium text-green-600">✓ {expenseReceipt.name}</p>
                              <p className="text-sm mt-1">Click to change file</p>
                            </div>
                          ) : (
                            <div>
                              <p className="font-medium">Click to upload receipt</p>
                              <p className="text-sm mt-1">PNG, JPG, or PDF (max 5MB)</p>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={handleEditExpense}
                  disabled={uploadingReceipt || !expenseDescription || !expenseAmount || !expenseGameId}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${uploadingReceipt || !expenseDescription || !expenseAmount || !expenseGameId
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                    }`}
                >
                  {uploadingReceipt ? 'Updating...' : 'Update Expense'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'expense-report':
        return selectedClub && (
          <ExpenseReport
            expenses={expenses}
            games={games}
            tournaments={tournaments}
            teams={teams}
            players={players}
            currentUserEmail={user.email}
            clubName={selectedClub.name}
            onDeleteExpense={handleDeleteExpense}
            onApproveExpense={handleApproveExpense}
            onDenyExpense={handleDenyExpense}
            onMarkReimbursed={handleMarkReimbursed}
            onSettleTournamentFee={handleSettleTournamentFee}
            onMarkClubCovered={handleMarkClubCovered}
            onMarkExternalPlayerPaid={handleMarkExternalPlayerPaid}
            onUnmarkExternalPlayerPaid={handleUnmarkExternalPlayerPaid}
            canApprove={isTreasurer()} // Pass treasurer permission
            userRole={getRoleBadge()} // Pass user's role badge
            onEditExpense={(expense) => {
              setEditingExpense(expense);
              setExpenseDescription(expense.description);
              setExpenseAmount(expense.amount.toString());
              setExpenseGameId(expense.gameId);
              setExpenseReceipt(null);
              setRemoveOldReceipt(false);
              setView('edit-expense');
            }}
            onBack={() => setView('overview')}
          />
        );

      case 'payment-report':
        return (
          <PaymentReport
            paymentSubmissions={paymentSubmissions}
            walletTransactions={walletTransactions}
            players={players}
            onBack={() => setView('financial-reports-hub')}
            isTreasurer={permissions.isTreasurer || permissions.canManageFinancials}
            isAdmin={permissions.isAdmin || permissions.canManageClub}
          />
        );

      case 'add-event':
        return (
          <AddEvent
            players={players}
            teams={teams}
            onBack={() => {
              setView('events-list');
              setAddEventInitialValues(null);
            }}
            initialValues={addEventInitialValues}
            onSave={handleCreateEvent}
          />
        );

      case 'revenue-report':
        return selectedClub && (
          <RevenueReport
            expenses={expenses}
            games={games}
            tournaments={tournaments}
            teams={teams}
            payments={payments}
            onBack={() => setView('overview')}
          />
        );

      case 'financial-dashboard':
        return (
          <FinancialDashboard
            expenses={expenses}
            walletTransactions={walletTransactions}
            players={players}
            paymentSubmissions={paymentSubmissions}
            isTreasurer={isTreasurer()}
            onNavigate={setView}
            onApproveExpense={handleApproveExpense}
            onDenyExpense={handleDenyExpense}
            onApprovePayment={handleApprovePaymentSubmission}
            onDenyPayment={handleDenyPaymentSubmission}
            currentUser={user}
            canApprove={isTreasurer()} // Pass treasurer permission
            userRole={getRoleBadge()} // Pass user's role badge
            clubName={selectedClub.name}
          />
        );

      case 'expenses-report':
        return selectedClub && (
          <ExpenseReportNew
            expenses={expenses}
            games={games}
            tournaments={tournaments}
            clubName={selectedClub.name}
            onBack={() => setView('financial-dashboard')}
            onApprove={handleApproveExpense}
            onDeny={handleDenyExpense}
            onDelete={handleDeleteExpense}
            onEdit={(expense) => {
              setEditingExpense(expense);
              setExpenseDescription(expense.description);
              setExpenseAmount(expense.amount.toString());
              setExpenseGameId(expense.gameId);
              setExpenseReceipt(null);
              setRemoveOldReceipt(false);
              setView('edit-expense');
            }}
          />
        );

      case 'receivables-report':
        return selectedClub && (
          <ReceivablesReport
            charges={[]} // Will need to load playerCharges
            games={games}
            tournaments={tournaments}
            onBack={() => setView('financial-dashboard')}
            onMarkPaid={(charge) => {
              // Navigate to record payment with pre-selected charge
              setView('record-payment');
            }}
          />
        );

      case 'record-payment':
        return selectedClub && (
          <RecordPayment
            clubId={selectedClub.id}
            user={user}
            onBack={() => setView('overview')}
            onSuccess={() => {
              // Reload data and go back
              setView('overview');
            }}
          />
        );

      case 'payment-management':
        return selectedClub && (
          <PaymentManagement
            clubId={selectedClub.id}
            expenses={expenses}
            walletTransactions={walletTransactions}
            teams={teams}
            players={players}
            games={games}
            tournaments={tournaments}
            paymentSubmissions={paymentSubmissions}
            onApprovePayment={handleApprovePaymentSubmission}
            onDenyPayment={handleDenyPaymentSubmission}
            onBack={() => setView('overview')}
            isTreasurer={isTreasurer()}
            isAdmin={isUserAdmin()}
            currentUserId={user.uid}
            user={user}
            clubName={selectedClub.name}
            onDeleteWalletTransaction={handleDeleteWalletTransaction}
          />
        );

      case 'registration-report':
        return selectedClub && (
          <RegistrationReport
            players={players}
            expenses={expenses}
            walletTransactions={walletTransactions}
            paymentSubmissions={paymentSubmissions}
            clubName={selectedClub.name}
            onBack={() => setView('financial-reports-hub')}
          />
        );

      case 'players-report':
        return selectedClub && (
          <PlayersReport
            players={players}
            clubName={selectedClub.name}
            onBack={() => setView('financial-reports-hub')}
          />
        );

      case 'participation-report':
        return selectedClub && (
          <PlayerParticipationReport
            players={players}
            games={games}
            tournaments={tournaments}
            onBack={() => setView('overview')}
          />
        );

      case 'financial-reports-hub':
        return (
          <div className="max-w-6xl mx-auto">
            <button onClick={() => setView('overview')} className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2">
              ← Back to Overview
            </button>
            <div className="mb-8">
              <h2 className="text-3xl font-bold mb-2">Financial Reports</h2>
              <p className="text-gray-600">Access detailed financial reports and documents</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Payment Report */}
              <button
                onClick={() => setView('payment-report')}
                className="group bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 hover:shadow-md text-left transition-all border-2 border-teal-200 hover:border-teal-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                    <BarChart3 className="text-teal-600" size={24} />
                  </div>
                  <ChevronRight className="text-teal-300 group-hover:text-teal-600 transition-colors" size={20} />
                </div>
                <h3 className="font-bold text-lg mb-1 text-teal-900 group-hover:text-teal-700 transition-colors">Payments Report</h3>
                <p className="text-sm text-teal-700/80">Status of all payments and filter by status</p>
              </button>

              {/* Registration Report */}
              <button
                onClick={() => setView('registration-report')}
                className="group bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-6 hover:shadow-md text-left transition-all border-2 border-indigo-200 hover:border-indigo-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                    <Users className="text-indigo-600" size={24} />
                  </div>
                  <ChevronRight className="text-indigo-300 group-hover:text-indigo-600 transition-colors" size={20} />
                </div>
                <h3 className="font-bold text-lg mb-1 text-indigo-900 group-hover:text-indigo-700 transition-colors">Registration Report</h3>
                <p className="text-sm text-indigo-700/80">Manage player formats & registration status</p>
              </button>

              {/* Document Report (formerly Document Center) */}
              <button
                onClick={() => setView('receipt-management')}
                className="group bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 hover:shadow-md text-left transition-all border-2 border-purple-200 hover:border-purple-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                    <Receipt className="text-purple-600" size={24} />
                  </div>
                  <ChevronRight className="text-purple-300 group-hover:text-purple-600 transition-colors" size={20} />
                </div>
                <h3 className="font-bold text-lg mb-1 text-purple-900 group-hover:text-purple-700 transition-colors">Document Report</h3>
                <p className="text-sm text-purple-700/80">View receipts & financial documents</p>
              </button>

              {/* Players Report */}
              <button
                onClick={() => setView('players-report')}
                className="group bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 hover:shadow-md text-left transition-all border-2 border-amber-200 hover:border-amber-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                    <Users className="text-amber-600" size={24} />
                  </div>
                  <ChevronRight className="text-amber-300 group-hover:text-amber-600 transition-colors" size={20} />
                </div>
                <h3 className="font-bold text-lg mb-1 text-amber-900 group-hover:text-amber-700 transition-colors">Players Report</h3>
                <p className="text-sm text-amber-700/80">View player contact information</p>
              </button>

              {/* Financial Summary & Balances */}
              {permissions.isAdmin && (
                <button
                  onClick={() => setView('financial-reports')}
                  className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 hover:shadow-md text-left transition-all border-2 border-blue-200 hover:border-blue-300"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                      <BarChart3 className="text-blue-600" size={24} />
                    </div>
                    <ChevronRight className="text-blue-300 group-hover:text-blue-600 transition-colors" size={20} />
                  </div>
                  <h3 className="font-bold text-lg mb-1 text-blue-900 group-hover:text-blue-700 transition-colors">Financial Reports & Balances</h3>
                  <p className="text-sm text-blue-700/80">View Player Balances, Expense Breakdowns, Audit logs & more</p>
                </button>
              )}
            </div>
          </div>
        );

      case 'sync-schedule':
        return selectedClub && (
          <SyncSchedule
             selectedClub={selectedClub}
             teams={teams}
             tournaments={tournaments}
             games={games}
             onBack={() => setView('overview')}
          />
        );

      case 'manage-teams':
        return (
          <div className="max-w-6xl mx-auto">
            <button onClick={() => setView('overview')} className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2">
              ← Back to Overview
            </button>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Manage Teams</h2>
                <button
                  onClick={() => setView('create-team')}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
                >
                  + Add Team
                </button>
              </div>
              {teams.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>No teams yet. Create one to get started!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {teams.map(team => (
                    <div key={team.id} className="border rounded-lg p-4 flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-lg">{team.name}</h3>
                          <span className={`text-xs px-2 py-1 rounded font-semibold ${team.ballType === 'tennis'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                            }`}>
                            {team.ballType === 'tennis' ? '🎾 Tennis Ball' : '🏏 Leather Ball'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{team.players?.length || 0} players</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingTeam(team);
                            setTeamName(team.name);
                            setTeamWhatsAppLink(team.whatsappGroupLink || '');
                            setTeamBallType(team.ballType || 'leather'); // Load existing ball type or default
                            setView('edit-team');
                          }}
                          className="text-blue-600 hover:text-blue-800 px-4 py-2 border border-blue-600 rounded-lg"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTeam(team.id)}
                          className="text-red-600 hover:text-red-800 px-4 py-2"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'manage-tournaments':
        return (
          <div className="max-w-6xl mx-auto">
            <button onClick={() => setView('overview')} className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2">
              ← Back to Overview
            </button>
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">Manage Tournaments</h2>
                <button
                  onClick={() => setView('create-tournament')}
                  className="bg-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:bg-purple-700 text-sm sm:text-base"
                >
                  + Add Tournament
                </button>
              </div>
              {tournaments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>No tournaments yet. Create one to get started!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {tournaments.map(tournament => {
                    // Count games in this tournament
                    const tournamentGames = games.filter(g => g.tournamentId === tournament.id);
                    const completedGames = tournamentGames.filter(g => new Date(g.date) < new Date()).length;

                    return (
                      <div key={tournament.id} className="border-2 rounded-xl p-4 sm:p-5 hover:border-purple-300 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                                <Trophy className="text-purple-600" size={20} />
                              </div>
                              <div className="min-w-0">
                                <h3 className="font-bold text-base sm:text-lg">{tournament.name}</h3>
                                <p className="text-sm text-gray-600">
                                  {tournament.startDate ? (
                                    <>
                                      {formatDate(tournament.startDate)}
                                      <span className="text-gray-400 mx-2">to</span>
                                      {tournament.endDate && (
                                        <span className="font-semibold text-gray-900">
                                          {formatDate(tournament.endDate)}
                                        </span>
                                      )}</>
                                  ) : 'No date set'}
                                </p>
                              </div>
                            </div>
                            {/* Quick Stats */}
                            <div className="flex flex-wrap gap-3 sm:gap-4 mt-3 text-sm">
                              <span className="text-gray-600">
                                <span className="font-semibold text-gray-900">{tournamentGames.length}</span> games
                              </span>
                              <span className="text-gray-600">
                                <span className="font-semibold text-green-600">{completedGames}</span> completed
                              </span>
                              <span className="text-gray-600">
                                <span className="font-semibold text-blue-600">{tournamentGames.length - completedGames}</span> upcoming
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => setViewingTournamentDetail(tournament)}
                              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold text-sm flex items-center justify-center gap-2"
                            >
                              <ChevronRight size={16} />
                              View
                            </button>
                            <button
                              onClick={() => {
                                setEditingTournament(tournament);
                                setTournamentName(tournament.name);
                                setTournamentStartDate(tournament.startDate || '');
                                setTournamentEndDate(tournament.endDate || '');
                                setTournamentBallType(tournament.ballType || 'leather');
                                setTournamentMaxSquad(tournament.maxSquadSize || 11);
                                setTournamentAllowSuperSubs(tournament.allowSuperSubs || false);
                                setView('edit-tournament');
                              }}
                              className="px-3 sm:px-4 py-2 text-blue-600 hover:text-blue-800 border border-blue-600 rounded-lg text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTournament(tournament.id)}
                              className="px-3 sm:px-4 py-2 text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      case 'create-team': {
        // Players registered for the selected format: either they're on a team with this ballType,
        // or they have no teams at all (newly joined, format-agnostic).
        const eligiblePlayers = [...players]
          .filter(p => {
            const playerTeams = teams.filter(t => 
              t.players?.some(tp => (typeof tp === 'string' ? (tp === p.id || tp === p.userId) : (tp.id === p.id || tp.userId === p.id || tp.id === p.userId || tp.userId === p.userId)))
            );
            if (playerTeams.length === 0) return true; // No teams yet — show for all formats
            return playerTeams.some(t => (t.ballType || 'leather') === teamBallType);
          })
          .sort((a, b) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
            return nameA.localeCompare(nameB);
          });

        const togglePlayer = (playerId) => {
          setTeamSelectedPlayers(prev =>
            prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
          );
        };

        return (
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm p-8">
            <button
              onClick={() => {
                setView('manage-teams');
                setTeamName('');
                setTeamBallType('leather');
                setTeamSelectedPlayers([]);
              }}
              className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-1"
            >
              ← Back
            </button>
            <h2 className="text-2xl font-bold mb-6">Create Team</h2>
            <div className="space-y-5">

              {/* Team Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Team Name *</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
                  placeholder="e.g. CWC First XI"
                />
              </div>

              {/* Format */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Format *</label>
                <select
                  value={teamBallType}
                  onChange={(e) => { setTeamBallType(e.target.value); setTeamSelectedPlayers([]); }}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900"
                >
                  <option value="leather">🏏 Leather Ball</option>
                  <option value="tennis">🎾 Tennis Ball</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Only players registered for this format will appear below.</p>
              </div>

              {/* Player Multi-Select */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-gray-700">
                    Players
                    {teamSelectedPlayers.length > 0 && (
                      <span className="ml-2 text-blue-600 font-normal">({teamSelectedPlayers.length} selected)</span>
                    )}
                  </label>
                  {teamSelectedPlayers.length > 0 && (
                    <button
                      onClick={() => setTeamSelectedPlayers([])}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {eligiblePlayers.length === 0 ? (
                  <p className="text-sm text-gray-400 italic py-4 text-center border rounded-lg">
                    No players registered for this format yet.
                  </p>
                ) : (
                  <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                    {eligiblePlayers.map(player => {
                      const pid = player.userId || player.id;
                      const selected = teamSelectedPlayers.includes(pid);
                      const fullName = `${player.firstName || ''} ${player.lastName || ''}`.trim() || player.email;
                      return (
                        <button
                          key={player.id}
                          onClick={() => togglePlayer(pid)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            selected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}>
                            {selected && (
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className={`font-medium text-sm ${selected ? 'text-blue-800' : 'text-gray-800'}`}>{fullName}</p>
                            {player.email && <p className="text-xs text-gray-400 truncate">{player.email}</p>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                onClick={handleCreateTeam}
                disabled={!teamName.trim()}
                className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                  teamName.trim()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Create Team
              </button>
            </div>
          </div>
        );
      }

      case 'edit-team':
        return (
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm p-8">
            <button onClick={() => {
              setView('manage-teams');
              setEditingTeam(null);
              setTeamName('');
              setTeamWhatsAppLink('');
              setTeamBallType('leather');
            }} className="text-gray-600 mb-4">← Back</button>
            <h2 className="text-2xl font-bold mb-6">Edit Team</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Name *
                </label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Team Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ball Type *
                </label>
                <select
                  value={teamBallType}
                  onChange={(e) => setTeamBallType(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="leather">🏏 Leather Ball</option>
                  <option value="tennis">🎾 Tennis Ball</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  This helps organize teams by cricket format
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Group Link (Optional)
                </label>
                <input
                  type="url"
                  value={teamWhatsAppLink}
                  onChange={(e) => setTeamWhatsAppLink(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="https://chat.whatsapp.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Get from WhatsApp: Group → Group info → Invite via link → Copy link
                </p>
                {teamWhatsAppLink && !teamWhatsAppLink.startsWith('https://chat.whatsapp.com/') && (
                  <p className="text-xs text-red-600 mt-1">
                    ⚠️ WhatsApp links should start with "https://chat.whatsapp.com/"
                  </p>
                )}
              </div>

              <button
                onClick={handleEditTeam}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        );

      case 'create-tournament':
        return (
          <CreateTournament
            selectedClub={selectedClub}
            teams={teams}
            onBack={() => setView('manage-tournaments')}
            onSuccess={() => {
              loadTournaments();
              setView('manage-tournaments');
            }}
          />
        );

      case 'edit-tournament':
        return (
          <EditTournament
            tournament={editingTournament}
            selectedClub={selectedClub}
            teams={teams}
            onBack={() => {
              setView('manage-tournaments');
              setEditingTournament(null);
            }}
            onSuccess={() => {
              loadTournaments();
              setEditingTournament(null);
              setView('manage-tournaments');
            }}
          />
        );

      // Note: 'create-game' case removed - use 'add-game' which uses the AddGame component

      case 'manage-players':
        return (
          <div className="max-w-6xl mx-auto">
            <button onClick={() => setView('overview')} className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2">
              ← Back to Overview
            </button>



            <div className="mb-6">
              <h1 className="text-3xl font-bold mb-2">Manage Players</h1>
              <p className="text-gray-600 mb-4">Assign registered players to teams or add new players manually</p>
            </div>

              {/* Tabs for Managing Players Vs Copying */}
              <div className="flex space-x-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
                <button
                  onClick={() => setManagePlayersTab('current')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    managePlayersTab === 'current'
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Current Roster
                </button>
                <button
                  onClick={() => setManagePlayersTab('copy')}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    managePlayersTab === 'copy'
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Copy Players to Club
                </button>
              </div>

              {managePlayersTab === 'copy' ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 animate-in fade-in slide-in-from-bottom-2">
                  <h2 className="text-xl font-bold text-slate-800 mb-4">Copy Players to Another Club</h2>
                  
                  {/* Destination Selection */}
                  <div className="mb-6 grid grid-cols-1 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                     <div>
                       <label className="block text-sm font-bold text-slate-700 mb-2">Destination Club Code</label>
                       <input type="text" value={customCopyClubCode} onChange={(e) => setCustomCopyClubCode(e.target.value)} placeholder="Enter 6-digit Club Code" className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-medium" />
                     </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 pb-4 border-b border-slate-200 gap-4">
                     <div>
                       <p className="font-bold text-slate-800 text-lg mb-2">Select Players ({selectedPlayerIds.length} selected)</p>
                       <div className="flex gap-2">
                         <button 
                           onClick={() => setSelectedPlayerIds(players.filter(p => !playerSearchTerm || p.name.toLowerCase().includes(playerSearchTerm.toLowerCase()) || p.email?.toLowerCase().includes(playerSearchTerm.toLowerCase())).map(p => p.id))}
                           className="text-sm px-3 py-1 bg-slate-100 text-slate-700 font-semibold rounded hover:bg-slate-200 transition-colors"
                         >
                           Select All
                         </button>
                         <button 
                           onClick={() => setSelectedPlayerIds([])}
                           className="text-sm px-3 py-1 bg-slate-100 text-slate-700 font-semibold rounded hover:bg-slate-200 transition-colors"
                         >
                           Deselect All
                         </button>
                       </div>
                     </div>
                     <button onClick={handleExecuteCopyPlayers} disabled={selectedPlayerIds.length === 0} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-bold transition-all shadow-sm">
                       Execute Copy
                     </button>
                  </div>
                  
                  {/* List of approved players */}
                  <div className="max-h-[500px] overflow-y-auto space-y-2 pr-2">
                    {players
                      .filter(p => (!playerSearchTerm || p.name.toLowerCase().includes(playerSearchTerm.toLowerCase()) || p.email?.toLowerCase().includes(playerSearchTerm.toLowerCase())))
                      .map(p => (
                       <label key={p.id} className="flex items-center gap-4 p-3 bg-white border border-slate-200 rounded-xl hover:bg-blue-50 hover:border-blue-200 cursor-pointer transition-all">
                          <input type="checkbox" checked={selectedPlayerIds.includes(p.id)} onChange={() => handleSelectPlayerToCopy(p.id)} className="w-5 h-5 text-blue-600 rounded border-gray-300 cursor-pointer" />
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center font-bold text-blue-700 border border-blue-200 shadow-sm">{p.name.charAt(0).toUpperCase()}</div>
                          <div>
                            <p className="font-bold text-slate-800">{p.name}</p>
                            <p className="text-xs font-medium text-slate-500">{p.email}</p>
                          </div>
                          {p.teamName && (
                            <span className="ml-auto text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{p.teamName}</span>
                          )}
                       </label>
                    ))}
                    {players.length === 0 && (
                      <p className="text-center text-slate-500 py-8 font-medium">No active players available to copy.</p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Search Bar */}
                  <div className="relative mb-6">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="text-gray-400" size={20} />
                    </div>
                    <input
                      type="text"
                      placeholder="Search players by name or email..."
                      value={playerSearchTerm}
                      onChange={(e) => setPlayerSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm font-medium"
                    />
                  </div>

            {/* Pending Player Approvals - TOP PRIORITY */}
            {(pendingPlayers.length > 0 || (playerSearchTerm && filteredPendingPlayers.length > 0)) && (
              <div className="bg-white border-2 border-orange-200 rounded-xl shadow-sm mb-6 overflow-hidden">
                <button
                  onClick={() => setIsPendingPlayersExpanded(!isPendingPlayersExpanded)}
                  className="w-full bg-gradient-to-r from-orange-50 to-red-50 p-4 flex items-center justify-between hover:bg-orange-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-500 text-white p-2 rounded-full">
                      <Clock size={20} />
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        {pendingPlayers.length} Request{pendingPlayers.length !== 1 ? 's' : ''} Awaiting Approval
                        {playerSearchTerm && filteredPendingPlayers.length < pendingPlayers.length && (
                          <span className="text-sm font-normal text-gray-600 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                            {filteredPendingPlayers.length} matches
                          </span>
                        )}
                      </h2>
                    </div>
                  </div>
                  <ChevronDown className={`text-orange-600 transition-transform ${isPendingPlayersExpanded ? 'rotate-180' : ''}`} size={24} />
                </button>

                {isPendingPlayersExpanded && (
                  <div className="p-6 space-y-3 bg-white">
                    {filteredPendingPlayers.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No pending requests match your search.</p>
                    ) : (
                      filteredPendingPlayers.map(player => (
                        <div key={player.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 bg-white rounded-lg border border-orange-100 hover:border-orange-300 transition-all shadow-sm">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-lg sm:text-xl flex-shrink-0">
                                {player.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-bold text-base sm:text-lg">{player.name}</span>
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-semibold">
                                    Pending
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600">{player.email}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                  📅 Requested: {player.joinedAt ? formatDate(player.joinedAt) : 'Unknown'}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleApprovePlayer(player)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold flex items-center gap-1"
                            >
                              ✅ Approve
                            </button>
                            <button
                              onClick={() => handleRejectPlayer(player)}
                              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-semibold flex items-center gap-1"
                            >
                              ❌ Reject
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Unassigned Players Section - PRIORITY */}
            {(unassignedPlayers.length > 0 || (playerSearchTerm && filteredUnassignedPlayers.length > 0)) && (
              <div className="bg-white border-2 border-yellow-200 rounded-xl shadow-sm mb-6 overflow-hidden">
                <button
                  onClick={() => setIsUnassignedPlayersExpanded(!isUnassignedPlayersExpanded)}
                  className="w-full bg-gradient-to-r from-yellow-50 to-orange-50 p-4 flex items-center justify-between hover:bg-yellow-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-yellow-500 text-white p-2 rounded-full">
                      <Users size={20} />
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        {unassignedPlayers.length} Unassigned Player{unassignedPlayers.length !== 1 ? 's' : ''}
                        {playerSearchTerm && filteredUnassignedPlayers.length < unassignedPlayers.length && (
                          <span className="text-sm font-normal text-gray-600 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                            {filteredUnassignedPlayers.length} matches
                          </span>
                        )}
                      </h2>
                    </div>
                  </div>
                  <ChevronDown className={`text-yellow-600 transition-transform ${isUnassignedPlayersExpanded ? 'rotate-180' : ''}`} size={24} />
                </button>

                {isUnassignedPlayersExpanded && (
                  <div className="p-6 space-y-3 bg-white">
                    {filteredUnassignedPlayers.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No unassigned players match your search.</p>
                    ) : (
                      filteredUnassignedPlayers.map(player => (
                        <div key={player.id} className="mb-2">
                          <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 bg-white rounded-lg border ${player.hasRegistrationForm === false ? 'border-red-300 bg-red-50' : 'border-yellow-200 hover:border-yellow-400'} transition-all shadow-sm`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg sm:text-xl flex-shrink-0">
                                  {player.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-base sm:text-lg">{player.name}</span>
                                    {player.role === 'admin' && (
                                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-semibold">
                                        Admin
                                      </span>
                                    )}
                                    {player.hasRegistrationForm === false && (
                                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-semibold flex items-center gap-1">
                                        ⚠️ No Registration Form
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-600">{player.email}</div>
                                  {player.hasRegistrationForm === false ? (
                                    <div className="text-xs text-red-600 mt-1 font-medium">
                                      ⚠️ Player must complete registration form before team assignment
                                    </div>
                                  ) : (
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                      <span className="text-xs text-gray-500">
                                        ✓ Registered: {player.clubInfo?.joinedAt ? formatDate(player.clubInfo.joinedAt) : formatDate(player.createdAt)}
                                      </span>
                                      {getRegistrationStatusBadge(player)}
                                      {player.formats?.leather && (
                                        <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium border border-amber-200">
                                          Leather
                                        </span>
                                      )}
                                      {player.formats?.tennis && (
                                        <span className="text-[10px] bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium border border-green-200">
                                          Tennis
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                              <button
                                onClick={() => setExpandedPlayerTeamAssign(prev => ({...prev, [player.id]: !prev[player.id]}))}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors w-full sm:w-auto ${player.hasRegistrationForm === false ? 'bg-red-100 text-red-500 cursor-not-allowed border border-red-200' : expandedPlayerTeamAssign[player.id] ? 'bg-blue-100 text-blue-700 border border-blue-200 shadow-inner' : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 shadow-sm'}`}
                                disabled={player.hasRegistrationForm === false || isAssigningPlayer}
                              >
                                {expandedPlayerTeamAssign[player.id] ? 'Cancel Assignment' : 'Assign to Team(s)'}
                              </button>
                            </div>
                          </div>
                          
                          {/* Multi Select Teams Inline Interface */}
                          {expandedPlayerTeamAssign[player.id] && (
                            <div className="mx-4 p-5 bg-gradient-to-b from-blue-50/80 to-slate-50 border border-blue-100 rounded-b-xl border-t-0 -mt-2 animate-in slide-in-from-top-2 shadow-inner">
                              <p className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2 border-b border-blue-100 pb-2">
                                <span className="bg-blue-600 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">📋</span>
                                Select teams to assign <span className="text-blue-700">{player.name}</span> to:
                              </p>
                              
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-5 mt-4">
                                {teams.length === 0 ? (
                                  <p className="text-gray-500 italic text-sm py-2">No teams created yet.</p>
                                ) : (
                                  teams.map(t => {
                                    const isSelected = selectedTeamsForPlayer[player.id]?.includes(t.id);
                                    return (
                                      <label key={t.id} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'bg-white border-blue-500 shadow-sm transform scale-[1.02]' : 'bg-white/60 border-gray-200 hover:border-blue-300 hover:bg-white'}`}>
                                        <input 
                                          type="checkbox" 
                                          checked={isSelected || false} 
                                          onChange={(e) => {
                                            setSelectedTeamsForPlayer(prev => {
                                              const prevTeams = prev[player.id] || [];
                                              if (e.target.checked) return { ...prev, [player.id]: [...prevTeams, t.id] };
                                              else return { ...prev, [player.id]: prevTeams.filter(id => id !== t.id) };
                                            });
                                          }} 
                                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer" 
                                        />
                                        <span className={`font-semibold text-sm ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>{t.name}</span>
                                      </label>
                                    );
                                  })
                                )}
                              </div>
                              
                              <div className="flex justify-end gap-3 pt-3 border-t border-slate-200/60">
                                <button 
                                  onClick={() => {
                                    setExpandedPlayerTeamAssign(prev => ({...prev, [player.id]: false}));
                                    setSelectedTeamsForPlayer(prev => ({ ...prev, [player.id]: [] }));
                                  }} 
                                  className="px-5 py-2 text-sm font-bold text-slate-600 hover:bg-slate-200 hover:text-slate-800 rounded-lg transition-colors"
                                  disabled={isAssigningPlayer}
                                >
                                  Cancel
                                </button>
                                <button 
                                  onClick={() => handleConfirmBatchTeamAssignment(player)} 
                                  disabled={!(selectedTeamsForPlayer[player.id]?.length > 0) || isAssigningPlayer} 
                                  className="px-6 py-2 text-sm font-extrabold text-white bg-blue-600 hover:bg-blue-700 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all focus:ring-4 focus:ring-blue-100 flex items-center gap-2"
                                >
                                  {isAssigningPlayer ? (
                                    <>
                                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                      Assigning...
                                    </>
                                  ) : (
                                    <>
                                      Confirm Assignment
                                      <span className="bg-blue-700 px-1.5 py-0.5 rounded text-[10px]">
                                        {selectedTeamsForPlayer[player.id]?.length || 0}
                                      </span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Teams & Players Overview */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
                <h3 className="text-xl sm:text-2xl font-bold">Teams & Rosters</h3>
                <div className="text-sm text-gray-600">
                  {teams.length} team{teams.length !== 1 ? 's' : ''} • {teams.reduce((sum, t) => sum + (t.players?.length || 0), 0)} total players
                </div>
              </div>

              {filteredTeamsForManagePlayers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-2">{playerSearchTerm ? 'No teams match your search.' : 'No teams yet.'}</p>
                  {!playerSearchTerm && (
                    <button
                      onClick={() => setView('create-team')}
                      className="text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      Create your first team →
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTeamsForManagePlayers.map(team => (
                    <div key={team.id} className="border-2 rounded-lg hover:border-blue-300 transition-colors bg-white overflow-hidden">
                      {/* Team Header - Clickable to expand/collapse */}
                      <div
                        className="p-4 flex justify-between items-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                        onClick={() => setExpandedTeamRosters(prev => ({
                          ...prev,
                          [team.id]: !prev[team.id]
                        }))}
                      >
                        <div className="flex items-center gap-3">
                          <ChevronDown
                            size={20}
                            className={`text-gray-500 transition-transform ${expandedTeamRosters[team.id] ? 'rotate-180' : ''}`}
                          />
                          <div>
                            <h4 className="font-bold text-lg sm:text-xl flex items-center gap-2">
                              {team.name}
                              {playerSearchTerm && (
                                <span className="text-xs font-normal text-gray-500 bg-white px-2 py-0.5 rounded border">
                                  {team.players?.filter(p => p.name.toLowerCase().includes(playerSearchTerm.toLowerCase())).length || 0} matches
                                </span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-500">{team.players?.length || 0} player{(team.players?.length || 0) !== 1 ? 's' : ''}</p>
                          </div>
                        </div>

                        {/* Add Player Button - prevented from triggering collapse */}
                        {team.players && team.players.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedAddPlayerTeams(prev => ({
                                ...prev,
                                [team.id]: !prev[team.id]
                              }));
                              // Also expand the roster if adding a player
                              if (!expandedAddPlayerTeams[team.id]) {
                                setExpandedTeamRosters(prev => ({ ...prev, [team.id]: true }));
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg font-semibold text-sm flex items-center gap-1 transition-colors ${expandedAddPlayerTeams[team.id]
                              ? 'bg-gray-200 text-gray-700'
                              : 'bg-green-600 text-white hover:bg-green-700'
                              }`}
                          >
                            {expandedAddPlayerTeams[team.id] ? (
                              <>
                                <X size={16} />
                                <span className="hidden sm:inline">Close</span>
                              </>
                            ) : (
                              <>
                                <Plus size={16} />
                                <span className="hidden sm:inline">Add Player</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Expanded Roster Content */}
                      {expandedTeamRosters[team.id] && (
                        <div className="p-4 border-t border-gray-200">
                          {/* Add Player Panel - expandable for teams with existing players */}
                          {team.players && team.players.length > 0 && expandedAddPlayerTeams[team.id] && (
                            <div className="mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Users className="text-green-600" size={18} />
                                <span className="font-semibold text-green-900">Add more players to {team.name}</span>
                              </div>

                              {(() => {
                                // Get players not already in this team
                                const currentTeamPlayerIds = new Set(
                                  team.players?.map(p => p.userId || p.id) || []
                                );

                                const availablePlayers = [];
                                const seenIds = new Set();

                                // Add unassigned players
                                unassignedPlayers.forEach(p => {
                                  if (!currentTeamPlayerIds.has(p.id) && !seenIds.has(p.id)) {
                                    seenIds.add(p.id);
                                    availablePlayers.push({ ...p, source: 'unassigned' });
                                  }
                                });

                                // Add players from other teams
                                teams.forEach(t => {
                                  if (t.id !== team.id && t.players) {
                                    t.players.forEach(p => {
                                      const playerId = p.userId || p.id;
                                      if (!currentTeamPlayerIds.has(playerId) && !seenIds.has(playerId)) {
                                        seenIds.add(playerId);
                                        availablePlayers.push({
                                          ...p,
                                          id: playerId,
                                          source: 'other-team',
                                          currentTeam: t.name
                                        });
                                      }
                                    });
                                  }
                                });

                                // Filter available players by local search term if active
                                const search = teamPlayerSearchTerm[team.id]?.toLowerCase() || '';
                                let filteredAvailable = search
                                  ? availablePlayers.filter(p => p.name.toLowerCase().includes(search) || p.email?.toLowerCase().includes(search))
                                  : availablePlayers;

                                // Sort players alphabetically by name
                                filteredAvailable.sort((a, b) => {
                                  return (a.name || '').localeCompare(b.name || '');
                                });

                                if (availablePlayers.length === 0) {
                                  return (
                                    <div className="text-center py-3 text-gray-500 text-sm">
                                      All available players are already in this team.
                                    </div>
                                  );
                                }

                                return (
                                  <>
                                    <div className="mb-3">
                                      <input
                                        type="text"
                                        placeholder="Search available players by name..."
                                        value={teamPlayerSearchTerm[team.id] || ''}
                                        onChange={(e) => setTeamPlayerSearchTerm(prev => ({...prev, [team.id]: e.target.value}))}
                                        className="w-full px-3 py-2 border rounded-lg text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500"
                                      />
                                    </div>
                                    
                                    {filteredAvailable.length === 0 ? (
                                      <div className="text-center py-3 text-gray-500 text-sm border border-dashed rounded-lg bg-gray-50">
                                        No players match "{teamPlayerSearchTerm[team.id]}"
                                      </div>
                                    ) : (
                                      <>
                                        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                                          {filteredAvailable.map(player => (
                                        <div
                                          key={player.id}
                                          className={`flex justify-between items-center p-2 rounded-lg border transition-colors ${player.hasRegistrationForm === false
                                            ? 'bg-red-50 border-red-200'
                                            : 'bg-white border-gray-200 hover:border-green-400'
                                            }`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                              {player.name?.charAt(0).toUpperCase() || '?'}
                                            </div>
                                            <div>
                                              <div className="font-medium text-gray-900 text-sm flex items-center gap-1">
                                                {player.name}
                                                {player.hasRegistrationForm === false && (
                                                  <span className="text-xs text-red-600">⚠️</span>
                                                )}
                                              </div>
                                              <div className="text-xs text-gray-500 flex items-center gap-2">
                                                {player.hasRegistrationForm === false ? (
                                                  <span className="text-red-600">No registration form</span>
                                                ) : player.source === 'other-team' ? (
                                                  <span className="text-green-600">In {player.currentTeam}</span>
                                                ) : player.source === 'unassigned' ? (
                                                  <span className="text-orange-600">Unassigned</span>
                                                ) : null}
                                                <span>•</span>
                                                <span>{player.clubInfo?.joinedAt ? formatDate(player.clubInfo.joinedAt) : (player.createdAt ? formatDate(player.createdAt) : 'Unknown')}</span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-center p-1">
                                            <input
                                              type="checkbox"
                                              checked={selectedPlayersToAssign[team.id]?.includes(player.id) || false}
                                              onChange={(e) => {
                                                setSelectedPlayersToAssign(prev => {
                                                  const teamSelected = prev[team.id] || [];
                                                  if (e.target.checked) {
                                                    return { ...prev, [team.id]: [...teamSelected, player.id] };
                                                  } else {
                                                    return { ...prev, [team.id]: teamSelected.filter(id => id !== player.id) };
                                                  }
                                                });
                                              }}
                                              disabled={player.hasRegistrationForm === false || isAssigningMulti}
                                              className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                              title={player.hasRegistrationForm === false ? 'Player must complete registration form first' : 'Select to add to team'}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  
                                  <div className="mt-3 flex justify-end">
                                    <button
                                      onClick={async () => {
                                        const selectedIds = selectedPlayersToAssign[team.id] || [];
                                        if (selectedIds.length === 0) return;
                                        setIsAssigningMulti(true);
                                        
                                        try {
                                          const playersToAdd = availablePlayers.filter(p => selectedIds.includes(p.id));
                                          for (const p of playersToAdd) {
                                            await handleAssignPlayerToTeam(p, team.id);
                                          }
                                          
                                          // Reset and close
                                          setSelectedPlayersToAssign(prev => ({ ...prev, [team.id]: [] }));
                                          setExpandedAddPlayerTeams(prev => ({ ...prev, [team.id]: false }));
                                        } catch (error) {
                                          console.error('Error assigning multiple players:', error);
                                          alert('Failed to assign some or all players.');
                                        } finally {
                                          setIsAssigningMulti(false);
                                        }
                                      }}
                                      disabled={!(selectedPlayersToAssign[team.id]?.length > 0) || isAssigningMulti}
                                      className={`px-4 py-2 font-bold text-white shadow-sm rounded-lg transition-colors flex items-center gap-2 ${
                                        !(selectedPlayersToAssign[team.id]?.length > 0) || isAssigningMulti
                                          ? 'bg-gray-400 cursor-not-allowed'
                                          : 'bg-green-600 hover:bg-green-700'
                                      }`}
                                    >
                                      {isAssigningMulti ? 'Adding...' : `Add ${(selectedPlayersToAssign[team.id]?.length || 0) > 0 ? selectedPlayersToAssign[team.id].length : ''} Player${(selectedPlayersToAssign[team.id]?.length || 0) !== 1 ? 's' : ''}`}
                                    </button>
                                  </div>
                                </>
                              )}
                            </>
                          );
                        })()}
                            </div>
                          )}

                          {team.players && team.players.length > 0 ? (
                            <div className="space-y-2">
                              {[...team.players]
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                .map(player => {
                                  // Highlight if matches search
                                  const isMatch = playerSearchTerm && (
                                    player.name.toLowerCase().includes(playerSearchTerm.toLowerCase()) ||
                                    player.email.toLowerCase().includes(playerSearchTerm.toLowerCase())
                                  );

                                  return (
                                    <div key={player.id} className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 rounded-lg hover:bg-gray-50 ${isMatch ? 'bg-yellow-50 border border-yellow-200' : 'bg-white border border-gray-100'}`}>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <div className="font-semibold truncate">{player.name}</div>
                                          {player.teamRole === 'captain' && (
                                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-semibold whitespace-nowrap">
                                              ⭐ Captain
                                            </span>
                                          )}
                                          {player.teamRole === 'vice-captain' && (
                                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold whitespace-nowrap">
                                              👤 Vice Captain
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-sm text-gray-600 truncate">{player.email}</div>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        <select
                                          value={player.teamRole || 'player'}
                                          onChange={(e) => handleUpdatePlayerRole(team.id, player.id, e.target.value)}
                                          className="text-sm px-2 py-1 border rounded hover:border-blue-400 flex-shrink-0"
                                        >
                                          <option value="player">Player</option>
                                          <option value="captain">Captain</option>
                                          <option value="vice-captain">Vice Captain</option>
                                        </select>
                                        {isSuperAdmin() && (
                                          <button
                                            onClick={() => handlePromoteToAdmin({
                                              id: player.userId || player.id,
                                              name: player.name,
                                              clubName: selectedClub?.name
                                            })}
                                            className="text-sm px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 whitespace-nowrap"
                                          >
                                            Make Admin
                                          </button>
                                        )}
                                        <button
                                          onClick={() => handleRemovePlayer(team.id, player.id, player.name)}
                                          className="text-sm px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 whitespace-nowrap"
                                          title="Remove player from team"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  )
                                })}
                            </div>
                          ) : (
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Users className="text-blue-600" size={18} />
                                <span className="font-semibold text-blue-900">Add players to this team</span>
                              </div>

                              {/* Get all available players (unassigned + from other teams) */}
                              {(() => {
                                // Get all unique players in the club
                                const allClubPlayers = [];
                                const seenIds = new Set();

                                // Add unassigned players first
                                unassignedPlayers.forEach(p => {
                                  if (!seenIds.has(p.id)) {
                                    seenIds.add(p.id);
                                    allClubPlayers.push({ ...p, source: 'unassigned' });
                                  }
                                });

                                // Add players from other teams (they can be on multiple teams)
                                teams.forEach(t => {
                                  if (t.id !== team.id && t.players) {
                                    t.players.forEach(p => {
                                      const playerId = p.userId || p.id;
                                      if (!seenIds.has(playerId)) {
                                        seenIds.add(playerId);
                                        allClubPlayers.push({
                                          ...p,
                                          id: playerId,
                                          source: 'other-team',
                                          currentTeam: t.name
                                        });
                                      }
                                    });
                                  }
                                });

                                // Filter by search term
                                const filteredAll = playerSearchTerm
                                  ? allClubPlayers.filter(p => p.name.toLowerCase().includes(playerSearchTerm.toLowerCase()))
                                  : allClubPlayers;

                                if (allClubPlayers.length === 0) {
                                  return (
                                    <div className="text-center py-3 text-gray-500 text-sm">
                                      No players available. Players need to register first using the club code.
                                    </div>
                                  );
                                }

                                if (filteredAll.length === 0 && playerSearchTerm) {
                                  return (
                                    <div className="text-center py-3 text-gray-500 text-sm">
                                      No players match your search.
                                    </div>
                                  );
                                }

                                return (
                                  <>
                                    <div className="mb-3">
                                      <input
                                        type="text"
                                        placeholder="Search available players by name..."
                                        value={teamPlayerSearchTerm[team.id] || ''}
                                        onChange={(e) => setTeamPlayerSearchTerm(prev => ({...prev, [team.id]: e.target.value}))}
                                        className="w-full px-3 py-2 border rounded-lg text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500"
                                      />
                                    </div>
                                    
                                    {filteredAll.length === 0 ? (
                                      <div className="text-center py-3 text-gray-500 text-sm border border-dashed rounded-lg bg-gray-50">
                                        No players match "{teamPlayerSearchTerm[team.id]}"
                                      </div>
                                    ) : (
                                      <>
                                        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                                          {filteredAll.map(player => (
                                        <div
                                          key={player.id}
                                          className={`flex justify-between items-center p-2 rounded-lg border transition-colors ${player.hasRegistrationForm === false
                                            ? 'bg-red-50 border-red-200'
                                            : 'bg-white border-gray-200 hover:border-green-400'
                                            }`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                                              {player.name?.charAt(0).toUpperCase() || '?'}
                                            </div>
                                            <div>
                                              <div className="font-medium text-gray-900 text-sm flex items-center gap-1">
                                                {player.name}
                                                {player.hasRegistrationForm === false && (
                                                  <span className="text-xs text-red-600">⚠️</span>
                                                )}
                                              </div>
                                              <div className="text-xs text-gray-500 flex items-center gap-2">
                                                {player.hasRegistrationForm === false ? (
                                                  <span className="text-red-600">No registration form</span>
                                                ) : player.source === 'other-team' ? (
                                                  <span className="text-green-600">In {player.currentTeam}</span>
                                                ) : player.source === 'unassigned' ? (
                                                  <span className="text-orange-600">Unassigned</span>
                                                ) : null}
                                                <span>•</span>
                                                <span>{player.clubInfo?.joinedAt ? formatDate(player.clubInfo.joinedAt) : (player.createdAt ? formatDate(player.createdAt) : 'Unknown')}</span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-center p-1">
                                            <input
                                              type="checkbox"
                                              checked={selectedPlayersToAssign[team.id]?.includes(player.id) || false}
                                              onChange={(e) => {
                                                setSelectedPlayersToAssign(prev => {
                                                  const teamSelected = prev[team.id] || [];
                                                  if (e.target.checked) {
                                                    return { ...prev, [team.id]: [...teamSelected, player.id] };
                                                  } else {
                                                    return { ...prev, [team.id]: teamSelected.filter(id => id !== player.id) };
                                                  }
                                                });
                                              }}
                                              disabled={player.hasRegistrationForm === false || isAssigningMulti}
                                              className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                              title={player.hasRegistrationForm === false ? 'Player must complete registration form first' : 'Select to add to team'}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  
                                  <div className="mt-3 flex justify-end">
                                    <button
                                      onClick={async () => {
                                        const selectedIds = selectedPlayersToAssign[team.id] || [];
                                        if (selectedIds.length === 0) return;
                                        setIsAssigningMulti(true);
                                        
                                        try {
                                          const playersToAdd = allClubPlayers.filter(p => selectedIds.includes(p.id));
                                          for (const p of playersToAdd) {
                                            await handleAssignPlayerToTeam(p, team.id);
                                          }
                                          
                                          // Reset and close
                                          setSelectedPlayersToAssign(prev => ({ ...prev, [team.id]: [] }));
                                          // Note: We don't close any panel here because it's the empty team state!
                                        } catch (error) {
                                          console.error('Error assigning multiple players:', error);
                                          alert('Failed to assign some or all players.');
                                        } finally {
                                          setIsAssigningMulti(false);
                                        }
                                      }}
                                      disabled={!(selectedPlayersToAssign[team.id]?.length > 0) || isAssigningMulti}
                                      className={`px-4 py-2 font-bold text-white shadow-sm rounded-lg transition-colors flex items-center gap-2 ${
                                        !(selectedPlayersToAssign[team.id]?.length > 0) || isAssigningMulti
                                          ? 'bg-gray-400 cursor-not-allowed'
                                          : 'bg-green-600 hover:bg-green-700'
                                      }`}
                                    >
                                      {isAssigningMulti ? 'Adding...' : `Add ${(selectedPlayersToAssign[team.id]?.length || 0) > 0 ? selectedPlayersToAssign[team.id].length : ''} Player${(selectedPlayersToAssign[team.id]?.length || 0) !== 1 ? 's' : ''}`}
                                    </button>
                                  </div>
                                </>
                              )}
                            </>
                          );
                        })()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Manual Add Player Form - SECONDARY */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Plus className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold">Add Player Manually</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                For players who haven't registered yet, add them manually. They can claim their account later.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="px-4 py-3 border rounded-lg"
                >
                  <option value="">Select Team</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  className="px-4 py-3 border rounded-lg"
                  placeholder="Player Name"
                />
                <input
                  type="email"
                  value={playerEmail}
                  onChange={(e) => setPlayerEmail(e.target.value)}
                  className="px-4 py-3 border rounded-lg"
                  placeholder="Player Email"
                />
                <button
                  onClick={handleAddPlayer}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Add Player
                </button>
              </div>
            </div>

            {/* Remove Player from Club - DANGER ZONE (Collapsible) */}
            <div className="bg-white rounded-xl shadow-sm border-2 border-red-200 mt-6 overflow-hidden">
              <button
                onClick={() => setShowDeletePlayerSection(!showDeletePlayerSection)}
                className="w-full p-4 flex items-center justify-between hover:bg-red-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Trash2 className="text-red-600" size={24} />
                  <h3 className="text-xl font-bold text-red-700">Remove Player from Club</h3>
                  <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full font-medium">
                    Danger Zone
                  </span>
                </div>
                <ChevronDown
                  className={`text-red-600 transition-transform ${showDeletePlayerSection ? 'rotate-180' : ''}`}
                  size={24}
                />
              </button>

              {showDeletePlayerSection && (
                <div className="p-6 pt-2 border-t border-red-200">
                  <p className="text-sm text-gray-600 mb-4">
                    <strong className="text-red-600">⚠️ Warning:</strong> Permanently remove a player and all their associated data from this club. This action cannot be undone.
                  </p>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-800">
                      <strong>This will delete:</strong>
                    </p>
                    <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                      <li>User account from this club</li>
                      <li>All team memberships</li>
                      <li>All expenses (solo) or redistribute shared expenses</li>
                      <li>All wallet transactions and payment submissions</li>
                      <li>Registration form and waiver agreements</li>
                      <li>All notifications</li>
                    </ul>
                  </div>

                  {/* Player Selection */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Player to Remove
                      </label>
                      <select
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          if (selectedId) {
                            // Find player from all available sources
                            const allPlayers = [
                              ...players,
                              ...unassignedPlayers,
                              ...pendingPlayers
                            ];
                            const player = allPlayers.find(p => (p.id || p.userId) === selectedId);
                            if (player) {
                              setPlayerToDelete(player);
                              setShowDeletePlayerModal(true);
                              setDeleteConfirmEmail('');
                            }
                          }
                          e.target.value = ''; // Reset selection
                        }}
                        className="w-full px-4 py-3 border-2 border-red-200 rounded-lg text-gray-700 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        defaultValue=""
                      >
                        <option value="" disabled>Select a player...</option>

                        {/* Approved/Active Players */}
                        {players.length > 0 && (
                          <optgroup label="Active Players">
                            {players.map(player => (
                              <option key={player.id || player.userId} value={player.id || player.userId}>
                                {player.name} ({player.email})
                              </option>
                            ))}
                          </optgroup>
                        )}

                        {/* Unassigned Players */}
                        {unassignedPlayers.length > 0 && (
                          <optgroup label="Unassigned Players">
                            {unassignedPlayers.filter(p => !players.some(ap => ap.id === p.id)).map(player => (
                              <option key={player.id} value={player.id}>
                                {player.name} ({player.email}) - Not in any team
                              </option>
                            ))}
                          </optgroup>
                        )}

                        {/* Pending Players */}
                        {pendingPlayers.length > 0 && (
                          <optgroup label="Pending Approval">
                            {pendingPlayers.map(player => (
                              <option key={player.id} value={player.id}>
                                {player.name} ({player.email}) - Pending
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Delete Player Confirmation Modal */}
            {showDeletePlayerModal && playerToDelete && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                  {/* Modal Header */}
                  <div className="bg-red-600 text-white p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <Trash2 size={24} />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">Delete Player</h2>
                        <p className="text-red-100 text-sm">This action cannot be undone</p>
                      </div>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6">
                    <div className="mb-6">
                      <p className="text-gray-700 mb-2">
                        You are about to permanently delete:
                      </p>
                      <div className="bg-gray-100 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {playerToDelete.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{playerToDelete.name}</p>
                            <p className="text-sm text-gray-600">{playerToDelete.email}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                      <p className="text-orange-800 text-sm">
                        <strong>⚠️ Warning:</strong> All data associated with this player will be permanently deleted, including team memberships, expenses, payments, and registration forms.
                      </p>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type <strong className="text-red-600">{playerToDelete.email}</strong> to confirm:
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmEmail}
                        onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        placeholder="Enter email to confirm"
                        disabled={deletingPlayer}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowDeletePlayerModal(false);
                          setPlayerToDelete(null);
                          setDeleteConfirmEmail('');
                        }}
                        disabled={deletingPlayer}
                        className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeletePlayerFromClub}
                        disabled={deletingPlayer || deleteConfirmEmail.toLowerCase() !== playerToDelete.email?.toLowerCase()}
                        className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {deletingPlayer ? (
                          <>
                            <RefreshCw className="animate-spin" size={18} />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 size={18} />
                            Delete Permanently
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        );

      case 'games-list':
        // Combine all team players for name lookup in GamesList
        const allTeamPlayers = teams.flatMap(team =>
          (team.players || []).map(p => ({
            id: p.id,
            oderId: p.oderId,
            userId: p.userId,
            name: p.name,
            email: p.email
          }))
        );
        // Merge with users list, preferring team player data
        const combinedPlayers = [...allTeamPlayers];
        players.forEach(p => {
          if (!combinedPlayers.find(tp => tp.id === p.id || tp.userId === p.id)) {
            combinedPlayers.push(p);
          }
        });

        return (
          <GamesList
            isAdmin={true}
            games={games}
            players={combinedPlayers}
            teams={teams}
            tournaments={tournaments}
            onBack={() => setView('overview')}
            onAddGame={() => setView('add-game')}
            onImportGames={handleImportGames}
            onEditGame={(game) => {
              setEditingGame(game);
              setView('edit-game');
            }}
            onDeleteGame={handleDeleteGame}
            onSelectPlaying11={handleSelectPlaying11}
            onSendGameReminder={handleSendGameReminder}
            onSendAvailabilityReminder={handleSendAvailabilityReminder}
            onViewAvailability={handleViewAvailability}
            onMarkLateArrival={handleMarkLateArrival}
            onAssignDuty={handleAssignDuty}
            onUpdateGameStatus={handleUpdateGameStatus}
            onSaveMatchRecap={handleSaveMatchRecap}
          />
        );

      case 'add-game':
        return (
          <AddGame
            teams={teams}
            tournaments={tournaments}
            selectedClub={selectedClub}
            onBack={() => setView('games-list')}
            onSave={handleCreateGame}
          />
        );

      case 'edit-game':
        return editingGame && (
          <EditGame
            isAdmin={true}
            game={editingGame}
            teams={teams}
            tournaments={tournaments}
            onBack={() => {
              setView('games-list');
              setEditingGame(null);
            }}
            onSave={handleUpdateGame}
          />
        );

      case 'select-playing11':
        // Get tournament for this game
        const gameForSquad = selectedGameForPlayers;
        const tournamentForSquad = gameForSquad?.tournamentId
          ? tournaments.find(t => t.id === gameForSquad.tournamentId)
          : null;
        // Get team for fallback (use homeTeamId or teamId)
        const teamIdForSquad = gameForSquad?.homeTeamId || gameForSquad?.teamId;
        const teamForSquad = teamIdForSquad ? teams.find(t => t.id === teamIdForSquad) : null;

        return (
          <SquadSelection
            game={selectedGameForPlayers}
            tournament={tournamentForSquad}
            team={teamForSquad}
            availablePlayers={availablePlayers}
            expenses={expenses}
            walletTransactions={walletTransactions}
            canOverridePaymentBlock={true}
            onBack={() => setView('games-list')}
            onSave={handleSavePlaying11}
          />
        );

      case 'events-list':
        return (
          <EventsList
            events={events}
            players={players}
            onBack={() => setView('overview')}
            onAddEvent={(initialValues) => {
              setAddEventInitialValues(initialValues);
              setView('add-event');
            }}
            onEditEvent={(event) => {
              setEditingEvent(event);
              setView('edit-event');
            }}
            onDeleteEvent={handleDeleteEvent}
            onSendReminder={handleSendEventReminder}
            onOpenWhatsApp={(event) => {
              setSelectedEventForWhatsApp(event);
              setView('event-whatsapp');
            }}
          />
        );



      case 'edit-event':
        return editingEvent && (
          <EditEvent
            event={editingEvent}
            onBack={() => {
              setView('events-list');
              setEditingEvent(null);
            }}
            onSave={handleUpdateEvent}
          />
        );

      case 'event-whatsapp':
        return selectedEventForWhatsApp && (
          <EventWhatsAppMessenger
            event={selectedEventForWhatsApp}
            clubName={selectedClub?.name}
            players={players}
            whatsappGroupLink={selectedClub?.eventsWhatsappLink || selectedClub?.whatsappGroupLink}
            onUpdateGroupLink={async (newLink) => {
              try {
                const clubRef = doc(db, 'club', selectedClub.id);
                await updateDoc(clubRef, {
                  eventsWhatsappLink: newLink,
                  updatedAt: new Date().toISOString()
                });
                const updatedClub = { ...selectedClub, eventsWhatsappLink: newLink };
                setSelectedClub(updatedClub);
                await reloadClubs();
                return true;
              } catch (error) {
                console.error("Error updating link", error);
                throw error;
              }
            }}
            onBack={() => {
              setView('events-list');
              setSelectedEventForWhatsApp(null);
            }}
          />
        );

      case 'member-stats':
        return (
          <MemberStatsView
            players={players}
            teams={teams}
            unassignedPlayers={unassignedPlayers}
            onNavigate={setView}
            onBack={() => setView('overview')}
          />
        );

      case 'registration-fee-validation':
        return (
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => setView('overview')}
              className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
            >
              ← Back to Overview
            </button>

            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Initial Donation Validation</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Ensure all players are charged correctly based on ball types they play
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      const feeAmount = getInitialDonation('leather');
                      if (!confirm(`Create a initial donation for yourself (admin)?\n\nThis will use your Firebase Auth UID and link properly to your Player Dashboard.\n\nAmount: $${feeAmount}`)) {
                        return;
                      }

                      try {
                        // Check for existing active initial donation first
                        const existingFee = expenses.find(e =>
                          (e.isInitialDonation || e.isRegistrationFee) &&
                          e.approvalStatus !== 'market_deleted' &&
                          e.splitAmong?.includes(user.uid)
                        );

                        if (existingFee) {
                          alert(`⚠️ You already have an active Initial Donation created on ${formatDate(existingFee.createdAt)}.\n\nNo new fee was created.`);
                          return;
                        }

                        const expensesRef = collection(db, 'expenses');
                        await addDoc(expensesRef, {
                          description: 'Initial Donation',
                          amount: feeAmount,
                          amountPerPlayer: feeAmount,
                          gameId: null,
                          gameName: null,
                          tournamentId: null,
                          tournamentName: null,
                          clubId: selectedClub.id,
                          splitAmong: [user.uid], // ✅ Using Firebase UID
                          receiptURL: null,
                          createdAt: new Date().toISOString(),
                          createdBy: user.email,
                          createdByName: user.displayName || user.email.split('@')[0],
                          addedByPlayerId: user.uid,
                          addedByPlayerName: user.displayName || user.email.split('@')[0],
                          approvalStatus: 'approved',
                          approvedBy: user.email,
                          approvedAt: new Date().toISOString(),
                          approvedByName: user.displayName || user.email.split('@')[0],
                          isInitialDonation: true,
                          isDividable: false,
                          type: 'Initial Donation'
                        });

                        alert(`✅ Initial Donation created for you!\n\nAmount: $${feeAmount}\n\nGo to Player Dashboard and refresh to see your balance.`);
                        await loadExpenses();
                      } catch (error) {
                        console.error('Error creating fee:', error);
                        alert('Error creating fee: ' + error.message);
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                  >
                    Create My Fee
                  </button>
                </div>
              </div>

              {/* Fee Rules Explanation */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <DollarSign size={20} />
                  Initial Donation Rules
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                    <div className="font-semibold text-green-700 mb-2">✅ One Fee Per Player</div>
                    <p className="text-gray-600">Each player pays a single initial donation regardless of how many teams they join. Adding a player to additional teams does NOT create new fees.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                    <div className="font-semibold text-blue-700 mb-2">✏️ Admin Editable</div>
                    <p className="text-gray-600">Club admins can edit initial donations from Financial Reports → Registrations, or change defaults in Club Settings.</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>💡 Current Fees:</strong> ${getInitialDonation('leather')} for leather ball teams, ${getInitialDonation('tennis')} for tennis ball teams.
                    <span className="ml-2 text-amber-600">Change these in Club Settings.</span>
                  </p>
                </div>
              </div>

              {(() => {
                const issues = validateAllRegistrationFees();

                // Summary cards - Use 'players' state as source of truth
                const allPlayers = players.map(player => {
                  // Find all teams this player is on
                  const playerTeams = teams.filter(t =>
                    t.players?.some(p => (p.userId === player.id || p.id === player.id))
                  );

                  return {
                    ...player,
                    allTeams: playerTeams.map(t => ({ id: t.id, name: t.name, ballType: t.ballType || 'leather' }))
                  };
                });

                // Count players with and without fees, and mismatches
                let withFeeCount = 0;
                let missingFeeCount = 0;
                let mismatchCount = 0;
                let totalFeeAmount = 0;
                let totalExpectedAmount = 0;

                allPlayers.forEach(player => {
                  const feeExpense = getPlayerInitialDonation(player.id, player.email, player.userId);
                  const expectedFee = calculateCorrectInitialDonation(player.id, player.email);
                  totalExpectedAmount += expectedFee;

                  if (feeExpense) {
                    withFeeCount++;
                    totalFeeAmount += feeExpense.amount || 0;
                    // Check if fee amount doesn't match expected
                    if (feeExpense.amount !== expectedFee) {
                      mismatchCount++;
                    }
                  } else {
                    missingFeeCount++;
                  }
                });

                const hasIssues = missingFeeCount > 0 || mismatchCount > 0;

                return (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                      <div className="bg-blue-50 rounded-xl shadow-sm border-2 border-blue-200 p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="bg-blue-600 text-white rounded-full p-2">
                            <Users size={16} />
                          </div>
                          <span className="text-sm font-semibold text-blue-900">Total Players</span>
                        </div>
                        <div className="text-3xl font-bold text-blue-700">{allPlayers.length}</div>
                        <div className="text-sm text-blue-600 mt-1">Unique players in club</div>
                      </div>

                      <div className="bg-green-50 rounded-xl shadow-sm border-2 border-green-200 p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="bg-green-600 text-white rounded-full p-2">
                            ✓
                          </div>
                          <span className="text-sm font-semibold text-green-900">Correct</span>
                        </div>
                        <div className="text-3xl font-bold text-green-700">{withFeeCount - mismatchCount}</div>
                        <div className="text-sm text-green-600 mt-1">Players with correct fee</div>
                      </div>

                      <div className="bg-orange-50 rounded-xl shadow-sm border-2 border-orange-200 p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="bg-orange-600 text-white rounded-full p-2">
                            !
                          </div>
                          <span className="text-sm font-semibold text-orange-900">Wrong Amount</span>
                        </div>
                        <div className="text-3xl font-bold text-orange-700">{mismatchCount}</div>
                        <div className="text-sm text-orange-600 mt-1">Need fee update</div>
                      </div>

                      <div className="bg-red-50 rounded-xl shadow-sm border-2 border-red-200 p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="bg-red-600 text-white rounded-full p-2">
                            ✕
                          </div>
                          <span className="text-sm font-semibold text-red-900">Missing Fee</span>
                        </div>
                        <div className="text-3xl font-bold text-red-700">{missingFeeCount}</div>
                        <div className="text-sm text-red-600 mt-1">Players without fee</div>
                      </div>

                      <div className="bg-purple-50 rounded-xl shadow-sm border-2 border-purple-200 p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="bg-purple-600 text-white rounded-full p-2">
                            <DollarSign size={16} />
                          </div>
                          <span className="text-sm font-semibold text-purple-900">Expected Total</span>
                        </div>
                        <div className="text-3xl font-bold text-purple-700">${totalExpectedAmount}</div>
                        <div className="text-sm text-purple-600 mt-1">
                          {totalFeeAmount !== totalExpectedAmount && (
                            <span className="text-orange-600">Current: ${totalFeeAmount}</span>
                          )}
                          {totalFeeAmount === totalExpectedAmount && 'All fees correct'}
                        </div>
                      </div>
                    </div>

                    {/* Issues Section */}
                    {!hasIssues ? (
                      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-12 text-center mb-6">
                        <div className="text-6xl mb-4">✅</div>
                        <h3 className="text-2xl font-bold text-green-900 mb-2">All Fees Correct!</h3>
                        <p className="text-green-700">
                          All {allPlayers.length} player{allPlayers.length !== 1 ? 's have' : ' has'} the correct initial donation.
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Mismatched Fees Section */}
                        {mismatchCount > 0 && (
                          <div className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden mb-6">
                            <div className="bg-orange-50 border-b-2 border-orange-200 p-4">
                              <h3 className="font-bold text-orange-900 flex items-center gap-2">
                                ⚠️ Fee Amount Mismatch ({mismatchCount})
                              </h3>
                              <p className="text-sm text-orange-700 mt-1">
                                These players have fees that don't match the current club settings (${getInitialDonation('leather')} leather / ${getInitialDonation('tennis')} tennis)
                              </p>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50 border-b-2 border-gray-200">
                                  <tr>
                                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Player</th>
                                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Teams</th>
                                    <th className="text-center py-4 px-6 font-semibold text-gray-700">Current Fee</th>
                                    <th className="text-center py-4 px-6 font-semibold text-gray-700">Expected Fee</th>
                                    <th className="text-center py-4 px-6 font-semibold text-gray-700">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {allPlayers
                                    .filter(player => {
                                      const feeExpense = getPlayerInitialDonation(player.id, player.email, player.userId);
                                      const expectedFee = calculateCorrectInitialDonation(player.id, player.email);
                                      return feeExpense && feeExpense.amount !== expectedFee;
                                    })
                                    .map((player, idx) => {
                                      const feeExpense = getPlayerInitialDonation(player.id, player.email, player.userId);
                                      const expectedFee = calculateCorrectInitialDonation(player.id, player.email);

                                      return (
                                        <tr key={idx} className="hover:bg-gray-50">
                                          <td className="py-4 px-6">
                                            <div className="font-semibold text-gray-900">{player.name}</div>
                                            <div className="text-sm text-gray-500">{player.email}</div>
                                          </td>
                                          <td className="py-4 px-6">
                                            <div className="flex flex-wrap gap-1">
                                              {player.allTeams?.map((team, teamIdx) => (
                                                <span
                                                  key={teamIdx}
                                                  className={`text-xs font-medium px-2 py-1 rounded ${team.ballType === 'tennis'
                                                    ? 'bg-yellow-100 text-yellow-700'
                                                    : 'bg-red-100 text-red-700'
                                                    }`}
                                                >
                                                  {team.name} {team.ballType === 'tennis' ? '🎾' : '🏏'}
                                                </span>
                                              ))}
                                            </div>
                                          </td>
                                          <td className="py-4 px-6 text-center">
                                            <span className="text-lg font-bold text-orange-600">${feeExpense.amount}</span>
                                          </td>
                                          <td className="py-4 px-6 text-center">
                                            <span className="text-lg font-bold text-green-600">${expectedFee}</span>
                                          </td>
                                          <td className="py-4 px-6 text-center">
                                            <button
                                              onClick={async () => {
                                                try {
                                                  const feeRef = doc(db, 'expenses', feeExpense.id);
                                                  await updateDoc(feeRef, {
                                                    amount: expectedFee,
                                                    amountPerPlayer: expectedFee,
                                                    updatedAt: new Date().toISOString(),
                                                    updatedBy: user.email,
                                                    updateReason: 'Updated to match club fee settings'
                                                  });

                                                  alert(`✅ Fee updated for ${player.name}!\n\n$${feeExpense.amount} → $${expectedFee}`);
                                                  await loadExpenses();
                                                } catch (error) {
                                                  console.error('Error updating fee:', error);
                                                  alert('Error updating fee: ' + error.message);
                                                }
                                              }}
                                              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold text-sm"
                                            >
                                              Update to ${expectedFee}
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                            <div className="bg-orange-50 border-t border-orange-200 p-4">
                              <button
                                onClick={async () => {
                                  if (!confirm(`Update ALL ${mismatchCount} mismatched fees to match club settings?`)) return;

                                  try {
                                    let updated = 0;
                                    for (const player of allPlayers) {
                                      const feeExpense = getPlayerInitialDonation(player.id, player.email, player.userId);
                                      const expectedFee = calculateCorrectInitialDonation(player.id, player.email);

                                      if (feeExpense && feeExpense.amount !== expectedFee) {
                                        const feeRef = doc(db, 'expenses', feeExpense.id);
                                        await updateDoc(feeRef, {
                                          amount: expectedFee,
                                          amountPerPlayer: expectedFee,
                                          updatedAt: new Date().toISOString(),
                                          updatedBy: user.email,
                                          updateReason: 'Bulk update to match club fee settings'
                                        });
                                        updated++;
                                      }
                                    }

                                    alert(`✅ Updated ${updated} initial donations!`);
                                    await loadExpenses();
                                  } catch (error) {
                                    console.error('Error bulk updating fees:', error);
                                    alert('Error updating fees: ' + error.message);
                                  }
                                }}
                                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold"
                              >
                                Update All {mismatchCount} Fees
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Missing Fees Section */}
                        {missingFeeCount > 0 && (
                          <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden mb-6">
                            <div className="bg-red-50 border-b-2 border-red-200 p-4">
                              <h3 className="font-bold text-red-900 flex items-center gap-2">
                                ❌ Players Missing Initial Donation ({missingFeeCount})
                              </h3>
                              <p className="text-sm text-red-700 mt-1">These players need a initial donation created</p>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50 border-b-2 border-gray-200">
                                  <tr>
                                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Player</th>
                                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Teams</th>
                                    <th className="text-center py-4 px-6 font-semibold text-gray-700">Action</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {allPlayers
                                    .filter(player => !getPlayerInitialDonation(player.id, player.email, player.userId))
                                    .map((player, idx) => {
                                      const hasLeather = player.allTeams?.some(t => t.ballType !== 'tennis');
                                      const defaultFee = hasLeather ? getInitialDonation('leather') : getInitialDonation('tennis');

                                      return (
                                        <tr key={idx} className="hover:bg-gray-50">
                                          <td className="py-4 px-6">
                                            <div className="font-semibold text-gray-900">{player.name}</div>
                                            <div className="text-sm text-gray-500">{player.email}</div>
                                          </td>
                                          <td className="py-4 px-6">
                                            <div className="flex flex-wrap gap-1">
                                              {player.allTeams?.map((team, teamIdx) => (
                                                <span
                                                  key={teamIdx}
                                                  className={`text-xs font-medium px-2 py-1 rounded ${team.ballType === 'tennis'
                                                    ? 'bg-yellow-100 text-yellow-700'
                                                    : 'bg-red-100 text-red-700'
                                                    }`}
                                                >
                                                  {team.name} {team.ballType === 'tennis' ? '🎾' : '🏏'}
                                                </span>
                                              ))}
                                            </div>
                                          </td>
                                          <td className="py-4 px-6 text-center">
                                            <button
                                              onClick={async () => {
                                                try {
                                                  const expensesRef = collection(db, 'expenses');
                                                  await addDoc(expensesRef, {
                                                    description: 'Initial Donation',
                                                    amount: defaultFee,
                                                    amountPerPlayer: defaultFee,
                                                    gameId: null,
                                                    gameName: null,
                                                    tournamentId: null,
                                                    tournamentName: null,
                                                    clubId: selectedClub.id,
                                                    splitAmong: [player.userId || player.id],
                                                    receiptURL: null,
                                                    createdAt: new Date().toISOString(),
                                                    createdBy: user.email,
                                                    createdByName: user.displayName || user.email.split('@')[0],
                                                    addedByPlayerId: user.uid,
                                                    addedByPlayerName: user.displayName || user.email.split('@')[0],
                                                    approvalStatus: 'approved',
                                                    approvedBy: user.email,
                                                    approvedAt: new Date().toISOString(),
                                                    approvedByName: user.displayName || user.email.split('@')[0],
                                                    isInitialDonation: true,
                                                    isDividable: false,
                                                    type: 'Initial Donation',
                                                    playerEmail: player.email
                                                  });

                                                  alert(`✅ Initial Donation created for ${player.name}!\n\nAmount: $${defaultFee}`);
                                                  await loadExpenses();
                                                } catch (error) {
                                                  console.error('Error creating fee:', error);
                                                  alert('Error creating fee: ' + error.message);
                                                }
                                              }}
                                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-sm"
                                            >
                                              Create Fee (${defaultFee})
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Complete Player List with Payment Status */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="bg-blue-50 border-b-2 border-blue-200 p-4">
                        <h3 className="font-bold text-blue-900 flex items-center gap-2">
                          📋 All Players - Initial Donation Status
                        </h3>
                        <p className="text-sm text-blue-700 mt-1">Complete list with fee and payment status</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b-2 border-gray-200">
                            <tr>
                              <th className="text-left py-4 px-6 font-semibold text-gray-700">Player</th>
                              <th className="text-left py-4 px-6 font-semibold text-gray-700">Teams</th>
                              <th className="text-center py-4 px-6 font-semibold text-gray-700">Fee Amount</th>
                              <th className="text-center py-4 px-6 font-semibold text-gray-700">Payment Status</th>
                              <th className="text-center py-4 px-6 font-semibold text-gray-700">Fee Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {allPlayers.map((player, idx) => {
                              const feeExpense = getPlayerInitialDonation(player.id, player.email, player.userId);
                              const actualFee = feeExpense?.amount || 0;

                              return (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="py-4 px-6">
                                    <div className="font-semibold text-gray-900">{player.name}</div>
                                    <div className="text-sm text-gray-500">{player.email}</div>
                                  </td>
                                  <td className="py-4 px-6">
                                    <div className="flex flex-wrap gap-1">
                                      {player.allTeams?.map((team, teamIdx) => (
                                        <span
                                          key={teamIdx}
                                          className={`text-xs font-medium px-2 py-1 rounded ${team.ballType === 'tennis'
                                            ? 'bg-yellow-100 text-yellow-700'
                                            : 'bg-red-100 text-red-700'
                                            }`}
                                        >
                                          {team.name} {team.ballType === 'tennis' ? '🎾' : '🏏'}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="py-4 px-6 text-center">
                                    <span className="text-lg font-bold text-gray-900">
                                      ${actualFee}
                                    </span>
                                  </td>
                                  <td className="py-4 px-6 text-center">
                                    {(() => {
                                      if (!feeExpense) {
                                        return (
                                          <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-semibold bg-red-100 text-red-700">
                                            ❌ No Fee Created
                                          </span>
                                        );
                                      }

                                      // Check for actual payment in wallet transactions
                                      const isPaid = walletTransactions?.some(t =>
                                        t.type === 'payment' &&
                                        t.status === 'approved' &&
                                        t.expenseId === feeExpense.id
                                      );

                                      if (isPaid) {
                                        return (
                                          <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-semibold bg-green-100 text-green-700">
                                            ✓ Paid
                                          </span>
                                        );
                                      }

                                      return (
                                        <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-semibold bg-orange-100 text-orange-700">
                                          ⏰ Pending Payment
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td className="py-4 px-6 text-center">
                                    {!feeExpense ? (
                                      <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-semibold bg-red-100 text-red-700">
                                        Missing
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-semibold bg-green-100 text-green-700">
                                        ✓ Created
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        );

      case 'club-players':
        return (
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => setView('overview')}
              className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
            >
              ← Back to Overview
            </button>

            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Club Players</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Track initial donations and player status
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Total Players</div>
                  <div className="text-2xl font-bold">{players.length}</div>
                </div>
              </div>

              {/* Initial Donation Summary Cards */}
              {(() => {
                const allPlayers = [];
                teams.forEach(team => {
                  if (team.players) {
                    team.players.forEach(player => {
                      allPlayers.push({
                        ...player,
                        teamId: team.id,
                        teamName: team.name
                      });
                    });
                  }
                });

                // Calculate initial donation stats
                let totalPlayers = allPlayers.length;
                let paidCount = 0;
                let unpaidCount = 0;
                let totalCollected = 0;
                let totalReceivables = 0;

                allPlayers.forEach(player => {
                  const regFee = expenses.find(e =>
                    e.isInitialDonation &&
                    e.splitAmong?.includes(player.id)
                  );

                  if (regFee) {
                    // Check if wallet reimbursement exists (meaning player paid upfront)
                    const walletReimbursement = walletTransactions.find(t =>
                      t.userId === player.id &&
                      t.expenseId === regFee.id &&
                      t.status === 'approved'
                    );

                    if (walletReimbursement && walletReimbursement.amount >= 250) {
                      paidCount++;
                      totalCollected += 250;
                    } else {
                      unpaidCount++;
                      totalReceivables += 250;
                    }
                  } else {
                    // No reg fee created = unpaid
                    unpaidCount++;
                    totalReceivables += 250;
                  }
                });

                const collectionRate = totalPlayers > 0 ? (paidCount / totalPlayers) * 100 : 0;

                return (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="text-green-600" size={20} />
                        <span className="text-sm font-medium text-green-900">Paid</span>
                      </div>
                      <div className="text-2xl font-bold text-green-700">{paidCount}</div>
                      <div className="text-xs text-green-600 mt-1">${totalCollected.toFixed(2)} collected</div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="text-red-600" size={20} />
                        <span className="text-sm font-medium text-red-900">Unpaid</span>
                      </div>
                      <div className="text-2xl font-bold text-red-700">{unpaidCount}</div>
                      <div className="text-xs text-red-600 mt-1">${totalReceivables.toFixed(2)} receivables</div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="text-blue-600" size={20} />
                        <span className="text-sm font-medium text-blue-900">Total Expected</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-700">${(totalPlayers * 250).toFixed(2)}</div>
                      <div className="text-xs text-blue-600 mt-1">{totalPlayers} × $250</div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="text-purple-600" size={20} />
                        <span className="text-sm font-medium text-purple-900">Collection Rate</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-700">{collectionRate.toFixed(0)}%</div>
                      <div className="text-xs text-purple-600 mt-1">
                        {paidCount} of {totalPlayers}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Players Table */}
              {(() => {
                const allPlayers = [];
                teams.forEach(team => {
                  if (team.players) {
                    team.players.forEach(player => {
                      allPlayers.push({
                        ...player,
                        teamId: team.id,
                        teamName: team.name
                      });
                    });
                  }
                });

                if (allPlayers.length === 0) {
                  return (
                    <div className="text-center py-12 text-gray-500">
                      <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg mb-2">No players yet</p>
                      <p className="text-sm">Add players to teams to see them here</p>
                    </div>
                  );
                }

                return (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Player</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Team</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Joined</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-700">Reg Fee</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allPlayers
                          .sort((a, b) => {
                            // Sort by unpaid first, then by name
                            const aRegFee = expenses.find(e => e.isInitialDonation && e.splitAmong?.includes(a.id));
                            const bRegFee = expenses.find(e => e.isInitialDonation && e.splitAmong?.includes(b.id));
                            const aWallet = walletTransactions.find(t => t.userId === a.id && t.expenseId === aRegFee?.id && t.status === 'approved');
                            const bWallet = walletTransactions.find(t => t.userId === b.id && t.expenseId === bRegFee?.id && t.status === 'approved');
                            const aPaid = aWallet && aWallet.amount >= 250;
                            const bPaid = bWallet && bWallet.amount >= 250;

                            if (aPaid === bPaid) return a.name.localeCompare(b.name);
                            return aPaid ? 1 : -1; // Unpaid first
                          })
                          .map(player => {
                            const regFee = expenses.find(e =>
                              e.isInitialDonation &&
                              e.splitAmong?.includes(player.id)
                            );

                            const walletReimbursement = walletTransactions.find(t =>
                              t.userId === player.id &&
                              t.expenseId === regFee?.id &&
                              t.status === 'approved'
                            );

                            const isPaid = walletReimbursement && walletReimbursement.amount >= 250;
                            const hasRegFee = !!regFee;

                            return (
                              <tr
                                key={player.id}
                                className="border-b border-gray-100 hover:bg-gray-50"
                              >
                                <td className="py-4 px-4">
                                  <div className="font-semibold">{player.name}</div>
                                  <div className="text-xs text-gray-500">{player.email}</div>
                                </td>
                                <td className="py-4 px-4">
                                  <span className="text-sm text-blue-600 font-medium">
                                    {player.teamName}
                                  </span>
                                </td>
                                <td className="py-4 px-4">
                                  <span className="text-sm text-gray-600">
                                    {player.joinedAt
                                      ? formatDate(player.joinedAt)
                                      : 'Unknown'
                                    }
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-center">
                                  {hasRegFee ? (
                                    <span className="inline-flex items-center text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold">
                                      ✓ Created
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-semibold">
                                      ⚠ Missing
                                    </span>
                                  )}
                                </td>
                                <td className="py-4 px-4 text-center">
                                  {isPaid ? (
                                    <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                                      <CheckCircle size={14} />
                                      Paid
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-semibold">
                                      <Clock size={14} />
                                      Unpaid
                                    </span>
                                  )}
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className={`font-bold ${isPaid ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                    ${hasRegFee ? '250.00' : '0.00'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>
        );

      case 'all-players':
        return (
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => setView('clubs-list')}
              className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
            >
              ← Back to Clubs
            </button>

            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">All Players</h2>
                <div className="text-sm text-gray-600">
                  {players.length} total players across all clubs
                </div>
              </div>

              {players.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg mb-2">No players yet</p>
                  <p className="text-sm">Players will appear here when they sign up for clubs</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {players.map(player => {
                    const isAdmin = player.role === 'admin' || player.additionalRoles?.includes('admin');
                    // Find clubs this player is already admin of (check joinedClubs map)
                    const playerAdminClubIds = new Set();
                    if (player.joinedClubs) {
                      for (const [jClubId, jClubData] of Object.entries(player.joinedClubs)) {
                        if (jClubData.role === 'admin') {
                          playerAdminClubIds.add(jClubId);
                        }
                      }
                    }
                    // Also include their primary club if they're an admin
                    if (isAdmin && player.clubId) {
                      playerAdminClubIds.add(player.clubId);
                    }
                    const availableClubsForPromotion = clubs.filter(c => !playerAdminClubIds.has(c.id));

                    return (
                      <div
                        key={player.id}
                        className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <div className="font-semibold text-lg">{player.name}</div>
                          <div className="text-sm text-gray-600">{player.email}</div>
                          <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                            {/* Role Badge */}
                            <span className={`px-2 py-1 rounded font-semibold ${isAdmin
                              ? 'bg-purple-50 text-purple-700'
                              : 'bg-blue-50 text-blue-700'
                              }`}>
                              {isAdmin ? 'Admin' : 'Player'}
                            </span>

                            {/* Club Badge(s) — show all clubs from joinedClubs and legacy club */}
                            {(() => {
                              const uniqueClubs = new Map();
                              
                              // Legacy primary club
                              if (player.clubId) {
                                uniqueClubs.set(player.clubId, {
                                  name: player.clubName || clubs.find(c => c.id === player.clubId)?.name || 'Unknown Club',
                                  role: player.role || 'player'
                                });
                              } else if (player.clubInfo?.clubId) {
                                uniqueClubs.set(player.clubInfo.clubId, {
                                  name: player.clubInfo.clubName || clubs.find(c => c.id === player.clubInfo.clubId)?.name || 'Unknown Club',
                                  role: player.clubInfo.role || player.role || 'player'
                                });
                              }
                              
                              // New joinedClubs
                              if (player.joinedClubs) {
                                Object.entries(player.joinedClubs).forEach(([cId, cInfo]) => {
                                  uniqueClubs.set(cId, {
                                    name: cInfo.clubName || clubs.find(c => c.id === cId)?.name || 'Unknown Club',
                                    role: cInfo.role || 'player'
                                  });
                                });
                              }
                              
                              const clubList = Array.from(uniqueClubs.entries());
                              
                              if (clubList.length > 0) {
                                return clubList.map(([cId, cInfo]) => (
                                  <span key={cId} className={`px-2 py-1 rounded text-xs font-medium ${cInfo.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                                    {cInfo.name}
                                    {cInfo.role === 'admin' && ' (Admin)'}
                                  </span>
                                ));
                              } else {
                                return (
                                  <span className="bg-blue-50 px-2 py-1 rounded text-blue-700">
                                    No Club
                                  </span>
                                );
                              }
                            })()}

                            {/* Status Badge */}
                            <span className={`px-2 py-1 rounded font-semibold ${player.clubStatus === 'pending'
                              ? 'bg-yellow-50 text-yellow-700'
                              : player.clubStatus === 'approved'
                                ? 'bg-green-50 text-green-700'
                                : player.clubStatus === 'rejected'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-gray-50 text-gray-700'
                              }`}>
                              {player.clubStatus === 'pending' && '⏳ Pending'}
                              {player.clubStatus === 'approved' && '✓ Approved'}
                              {player.clubStatus === 'rejected' && '✗ Rejected'}
                              {player.clubStatus === 'no-club' && '○ No Club'}
                            </span>

                            {/* Team Badge */}
                            {player.teamId && (
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                Team: {teams.find(t => t.id === player.teamId)?.name || 'Unknown'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-gray-500">
                            {player.createdAt ? formatDate(player.createdAt) : 'Date unknown'}
                          </div>

                          {/* Approve/Reject buttons for pending players */}
                          {player.clubStatus === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprovePlayer(player)}
                                className="bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
                              >
                                ✓ Approve
                              </button>
                              <button
                                onClick={() => handleRejectPlayer(player)}
                                className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}

                          {/* Make Admin button - shows for non-admin players OR admin players (to add to additional clubs) */}
                          {player.clubStatus !== 'pending' && availableClubsForPromotion.length > 0 && (
                            <div className="flex items-center gap-1">
                              <select
                                id={`club-select-${player.id}`}
                                defaultValue=""
                                className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:ring-2 focus:ring-green-500"
                              >
                                <option value="" disabled>Select Club...</option>
                                {availableClubsForPromotion.map(c => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => {
                                  const selectEl = document.getElementById(`club-select-${player.id}`);
                                  const targetClubId = selectEl?.value;
                                  if (!targetClubId) {
                                    alert('Please select a club first.');
                                    return;
                                  }
                                  handlePromoteToAdmin(player, targetClubId);
                                }}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
                              >
                                Make Admin
                              </button>
                            </div>
                          )}

                          {/* Demote button(s) for admins — one per club they're admin of */}
                          {isAdmin && (() => {
                            const adminClubsMap = new Map();
                            
                            // Check primary legacy club
                            const primaryClubId = player.clubId || player.clubInfo?.clubId;
                            if (primaryClubId && player.role === 'admin') {
                              adminClubsMap.set(primaryClubId, { 
                                id: primaryClubId, 
                                name: player.clubName || player.clubInfo?.clubName || clubs.find(c => c.id === primaryClubId)?.name || 'Unknown Club' 
                              });
                            }
                            
                            // Check joinedClubs
                            if (player.joinedClubs) {
                              for (const [cId, cInfo] of Object.entries(player.joinedClubs)) {
                                if (cInfo.role === 'admin') {
                                  adminClubsMap.set(cId, { 
                                    id: cId, 
                                    name: cInfo.clubName || clubs.find(c => c.id === cId)?.name || 'Unknown Club' 
                                  });
                                }
                              }
                            }

                            const adminClubs = Array.from(adminClubsMap.values());
                            
                            return adminClubs.map(ac => (
                              <button
                                key={ac.id}
                                onClick={() => handleDemoteAdmin({ ...player, clubId: ac.id, clubName: ac.name })}
                                className="bg-orange-600 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap"
                              >
                                Demote from {ac.name}
                              </button>
                            ));
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      case 'whatsapp-messenger':
        // Only accessible by club admins for their club
        if (!selectedClub || isSuperAdmin()) {
          setView('overview');
          return null;
        }

        return (
          <WhatsAppGroupMessenger
            team={selectedTeamForWhatsApp || teams[0]}
            teams={teams}
            players={players}
            games={games}
            selectedPlayers={squadSelection}
            userRole="admin"
            userId={user.uid}
            tournaments={tournaments}
            expenses={expenses}
            walletTransactions={walletTransactions}
            clubWhatsAppLink={selectedClub?.whatsappGroupLink || ''}
            onUpdateGroupLink={async (teamId, newLink) => {
              try {
                const teamRef = doc(db, 'teams', teamId);
                await updateDoc(teamRef, {
                  whatsappGroupLink: newLink,
                  updatedAt: new Date().toISOString()
                });

                const updatedTeams = teams.map(t => t.id === teamId ? { ...t, whatsappGroupLink: newLink } : t);
                setTeams(updatedTeams);
                return true;
              } catch (error) {
                console.error("Error updating team link", error);
                throw error;
              }
            }}
            onBack={() => {
              setView('overview');
              setSelectedGameForReminder(null);
              setSelectedTeamForWhatsApp(null);
            }}
          />
        );

      case 'club-feedback':
        // Only accessible by club admins for their club
        if (!selectedClub || isSuperAdmin()) {
          setView('overview');
          return null;
        }

        return (
          <div className="max-w-4xl mx-auto">
            <ClubFeedback
              clubId={selectedClub.id}
              clubName={selectedClub.name}
              onBack={() => setView('overview')}
            />
          </div>
        );

      case 'feedback-management':
        // Only accessible by super admin
        if (!isSuperAdmin()) {
          setView('overview');
          return null;
        }

        return (
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <button
                onClick={() => setView('clubs-list')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
              >
                ← Back to Clubs
              </button>
            </div>
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900">Bug Reports & Feature Requests</h2>
              <p className="text-gray-600 mt-1">Feedback from all users across all clubs</p>
            </div>
            <FeedbackManagement />
          </div>
        );

      case 'volunteer-tracking':
        return (
          <div className="max-w-6xl mx-auto">
            <button onClick={() => setView('overview')} className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2">
              ← Back to Overview
            </button>
            <VolunteerManagement
              clubId={selectedClub?.id}
              players={players}
              games={games}
              events={events}
              isAdmin={true}
            />
          </div>
        );

      case 'receipt-management':
        return (
          <ReceiptManagement
            clubId={selectedClub?.id}
            players={players}
            games={games}
            tournaments={tournaments}
            onBack={() => setView('financial-reports-hub')}
          />
        );

      case 'custom-merch-orders':
        return (
          <MerchandiseOrders
            clubId={selectedClub?.id}
            players={players}
            expenses={expenses}
            onBack={() => setView('overview')}
          />
        );

      case 'team-roster':
        // Get unique players across all teams
        const playerMap = new Map();
        teams.forEach(team => {
          (team.players || []).forEach(player => {
            const id = player.userId || player.id;
            if (!playerMap.has(id)) {
              playerMap.set(id, { ...player, teams: [{ teamName: team.name, teamRole: player.teamRole }] });
            } else {
              playerMap.get(id).teams.push({ teamName: team.name, teamRole: player.teamRole });
            }
          });
        });
        const allUniquePlayers = Array.from(playerMap.values());

        return (
          <div className="max-w-6xl mx-auto">
            <button onClick={() => setView('overview')} className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2">
              ← Back to Overview
            </button>

            <div className="mb-6">
              <h1 className="text-3xl font-bold mb-2">Team Roster</h1>
              <p className="text-gray-600">
                {allUniquePlayers.length} registered player{allUniquePlayers.length !== 1 ? 's' : ''} across {teams.length} team{teams.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* All Registered Players */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Users className="text-blue-600" size={24} />
                All Registered Players ({allUniquePlayers.length})
              </h2>

              {allUniquePlayers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No players registered yet</p>
              ) : (
                <div className="grid gap-3">
                  {allUniquePlayers
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(player => (
                      <div key={player.userId || player.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          {player.profilePicUrl ? (
                            <img src={player.profilePicUrl} alt={player.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                              {player.name?.charAt(0).toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900">{player.name}</p>
                            <p className="text-sm text-gray-500">{player.email}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {player.teams.map((t, idx) => (
                            <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center gap-1">
                              {t.teamName}
                              {t.teamRole === 'captain' && <span>⭐</span>}
                              {t.teamRole === 'vice-captain' && <span>🔷</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Teams Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold mb-4">Teams Breakdown</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {teams.map(team => (
                  <div key={team.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-lg">{team.name}</h3>
                      <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {team.players?.length || 0} players
                      </span>
                    </div>
                    <div className="space-y-2">
                      {(team.players || []).map(player => (
                        <div key={player.userId || player.id} className="text-sm flex items-center justify-between">
                          <span className="text-gray-700">{player.name}</span>
                          {player.teamRole && (
                            <span className={`text-xs px-2 py-0.5 rounded ${player.teamRole === 'captain' ? 'bg-yellow-100 text-yellow-800' :
                              player.teamRole === 'vice-captain' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                              {player.teamRole === 'captain' ? '⭐ Captain' :
                                player.teamRole === 'vice-captain' ? '🔷 Vice Captain' :
                                  player.teamRole}
                            </span>
                          )}
                        </div>
                      ))}
                      {(!team.players || team.players.length === 0) && (
                        <p className="text-gray-400 text-sm">No players assigned</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        console.error('❌ View not found:', view);
        return (
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm p-8 text-center">
            <h2 className="text-2xl font-bold mb-4 text-red-600">View not found</h2>
            <p className="text-gray-600 mb-4">Current view: "{view}"</p>
            <button
              onClick={() => setView('overview')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Go to Overview
            </button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <PullToRefresh
      onRefresh={handlePullToRefresh}
      pullingContent={
        <div className="flex justify-center items-center py-4 bg-gradient-to-br from-green-50 via-white to-blue-50">
          <span className="text-gray-500">↓ Pull to refresh</span>
        </div>
      }
      refreshingContent={
        <div className="flex justify-center items-center py-4 bg-gradient-to-br from-green-50 via-white to-blue-50">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
          <span className="ml-2 text-green-600">Refreshing...</span>
        </div>
      }
      resistance={2.5}
      maxPullDownDistance={120}
      pullDownThreshold={80}
    >
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 relative pb-20 md:pb-0">
        {/* Club Logo Watermark Background */}
        {(selectedClub?.logoUrl || selectedClub?.logo) && (
          <div
            className="fixed inset-0 pointer-events-none z-0"
            style={{
              backgroundImage: `url(${selectedClub.logoUrl || selectedClub.logo})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '70%',
              opacity: 0.08
            }}
          />
        )}
        <div className="relative z-10">
          <Header
            user={user}
            role={isSuperAdmin() ? 'super-admin' : (permissions.isAdmin && user?.role !== 'treasurer' ? 'admin' : (permissions.isTreasurer ? 'treasurer' : 'admin'))}
            onLogout={onLogout}
            clubName={selectedClub?.name}
            clubLogo={selectedClub?.logoUrl || selectedClub?.logo}
            teamName={null}
            onBackToClubs={view !== 'clubs-list' ? () => setView('clubs-list') : null}
          />

          {/* Legacy Role View Toggle Banner Removed as requested by user - functionality moved to Nav */}

          {/* Test Mode Toggle - Super Admin Only */}
          {selectedClub && isSuperAdmin() && (
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 border-b border-purple-700">
              <div className="max-w-7xl mx-auto px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🧪</span>
                    <div>
                      <span className="font-bold text-white">TEST MODE</span>
                      <span className="text-purple-200 text-sm ml-2">
                        {selectedClub.testModeEnabled ? 'Enabled for admins' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        try {
                          console.log('🔵 Attempting to toggle test mode for club:', selectedClub.id);
                          const clubRef = doc(db, 'club', selectedClub.id);
                          const newTestModeValue = !selectedClub.testModeEnabled;
                          console.log('🔵 Setting testModeEnabled to:', newTestModeValue);

                          await setDoc(clubRef, {
                            testModeEnabled: newTestModeValue
                          }, { merge: true });

                          console.log('✅ Test mode saved to Firestore');

                          // Reload the full club document to get updated data
                          const updatedClubDoc = await getDoc(clubRef);
                          if (updatedClubDoc.exists()) {
                            const updatedClubData = { id: updatedClubDoc.id, ...updatedClubDoc.data() };
                            console.log('✅ Reloaded club data with testModeEnabled:', updatedClubData.testModeEnabled);
                            setSelectedClub(updatedClubData);
                          }

                          console.log('✅ Test mode toggled:', newTestModeValue);
                        } catch (error) {
                          console.error('❌ Error toggling test mode:', error);
                          alert('Failed to toggle test mode. Please try again.');
                        }
                      }}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${selectedClub.testModeEnabled
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-white text-purple-700 hover:bg-purple-50'
                        }`}
                    >
                      {selectedClub.testModeEnabled ? '🔴 Disable Test Mode' : '🟢 Enable Test Mode'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Test Mode - Impersonation Selector (for Club Admins ONLY when Test Mode enabled) */}
          {selectedClub?.testModeEnabled && !isSuperAdmin() && !impersonatingUser && (
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 border-b border-amber-600">
              <div className="max-w-7xl mx-auto px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🧪</span>
                    <div>
                      <span className="font-bold text-white">TEST MODE ACTIVE</span>
                      <span className="text-amber-100 text-sm ml-2">
                        Select a user to impersonate
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                          const [odlIdOrId, role, source] = value.split('|');

                          // Find user from either players array or team players
                          let selectedUser = null;

                          if (source === 'team') {
                            // Find from team players
                            for (const team of teams) {
                              if (team.players) {
                                const teamPlayer = team.players.find(p => p.odlId === odlIdOrId || p.id === odlIdOrId);
                                if (teamPlayer) {
                                  selectedUser = {
                                    ...teamPlayer,
                                    id: teamPlayer.odlId || teamPlayer.id,
                                    teamId: team.id,
                                    teamName: team.name
                                  };
                                  break;
                                }
                              }
                            }
                          } else {
                            // Find from registered players
                            selectedUser = players.find(p => p.id === odlIdOrId);
                          }

                          if (selectedUser) {
                            setImpersonatingUser({
                              uid: selectedUser.id || selectedUser.odlId,
                              odlId: selectedUser.odlId || selectedUser.id,
                              email: selectedUser.email || `${selectedUser.name?.toLowerCase().replace(/\s+/g, '.')}@external.player`,
                              name: selectedUser.name,
                              displayName: selectedUser.name,
                              role: role,
                              viewAs: 'player',
                              isExternal: source === 'team',
                              teamId: selectedUser.teamId,
                              teamName: selectedUser.teamName,
                              clubInfo: {
                                clubId: selectedClub.id,
                                clubName: selectedClub.name,
                                status: 'approved'
                              }
                            });
                          }
                        }
                      }}
                      className="px-4 py-2 bg-white/20 text-white rounded-lg border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm min-w-[280px]"
                      defaultValue=""
                    >
                      <option value="" className="text-gray-900">Select user to impersonate...</option>
                      <optgroup label="👤 Registered Players" className="text-gray-900">
                        {players.filter(p => !p.role || (p.role !== 'admin' && p.role !== 'super-admin')).map(player => (
                          <option key={player.id} value={`${player.id}|player|registered`} className="text-gray-900">
                            {player.name} ({player.email})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="🏏 External/Team Players" className="text-gray-900">
                        {teams.flatMap(team =>
                          (team.players || [])
                            .filter(p => !players.some(rp => rp.email === p.email)) // Exclude already registered players
                            .map(player => (
                              <option key={`${team.id}-${player.odlId || player.id}`} value={`${player.odlId || player.id}|player|team`} className="text-gray-900">
                                {player.name} ({team.name})
                              </option>
                            ))
                        )}
                      </optgroup>
                      <optgroup label="👑 Admins" className="text-gray-900">
                        {players.filter(p => p.role === 'admin').map(admin => (
                          <option key={admin.id} value={`${admin.id}|admin|registered`} className="text-gray-900">
                            {admin.name} ({admin.email}) - Admin
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Debug: Show test mode status for club admins */}
          {selectedClub && !isSuperAdmin() && (
            <div className="hidden">
              {console.log('🧪 Club Admin Test Mode Debug:', {
                clubId: selectedClub.id,
                testModeEnabled: selectedClub.testModeEnabled,
                isSuperAdmin: isSuperAdmin(),
                impersonatingUser: impersonatingUser,
                playersCount: players.length,
                playersWithoutRole: players.filter(p => !p.role).length,
                playersWithRole: players.filter(p => p.role).map(p => ({ name: p.name, role: p.role }))
              })}
            </div>
          )}

          {/* Impersonation Active Banner */}
          {impersonatingUser && (
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 border-b-4 border-amber-600 sticky top-0 z-50">
              <div className="max-w-7xl mx-auto px-4 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">⚠️</span>
                    <span className="font-bold text-white">
                      TEST MODE - Viewing as: {impersonatingUser.name}
                    </span>
                    <span className="text-amber-100 text-sm">
                      ({impersonatingUser.email})
                    </span>
                    {impersonatingUser.role === 'admin' && (
                      <span className="bg-white/20 text-white text-xs px-2 py-1 rounded font-semibold">
                        ADMIN
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Role Toggle for impersonated admins */}
                    {impersonatingUser.role === 'admin' && (
                      <div className="flex gap-1 p-1 bg-white/20 rounded-lg">
                        <button
                          onClick={() => setImpersonatingUser(prev => ({ ...prev, viewAs: 'admin' }))}
                          className={`px-3 py-1 rounded text-xs font-semibold transition-all ${impersonatingUser.viewAs === 'admin'
                            ? 'bg-white text-amber-700'
                            : 'text-white hover:bg-white/10'
                            }`}
                        >
                          📊 Admin
                        </button>
                        <button
                          onClick={() => setImpersonatingUser(prev => ({ ...prev, viewAs: 'player' }))}
                          className={`px-3 py-1 rounded text-xs font-semibold transition-all ${impersonatingUser.viewAs === 'player'
                            ? 'bg-white text-amber-700'
                            : 'text-white hover:bg-white/10'
                            }`}
                        >
                          🏏 Player
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => setImpersonatingUser(null)}
                      className="px-4 py-1.5 bg-white text-amber-700 rounded-lg font-semibold hover:bg-amber-50 transition-colors text-sm"
                    >
                      ✕ Exit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Render based on impersonation or normal view */}
          {impersonatingUser ? (
            impersonatingUser.viewAs === 'player' ? (
              <PlayerDashboard user={impersonatingUser} onLogout={() => setImpersonatingUser(null)} />
            ) : (
              <div className="max-w-7xl mx-auto px-4 py-8">{renderView()}</div>
            )
          ) : (
            <div className="max-w-7xl mx-auto px-4 py-8">{renderView()}</div>
          )}

          {/* Availability & Meal Preference Modal */}
          {viewingAvailability && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold">Player Availability {viewingAvailability.askMealPreference && '& Meals'}</h2>
                    <p className="text-sm text-gray-600 mt-1">{viewingAvailability.name}</p>
                  </div>
                  <button
                    onClick={() => setViewingAvailability(null)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="p-6">
                  {viewingAvailability.availability && Object.keys(viewingAvailability.availability).length > 0 ? (
                    <>
                      {/* Availability Summary */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-green-600">
                            {Object.values(viewingAvailability.availability).filter(a => a.status === 'available').length}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">✓ Available</div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-red-600">
                            {Object.values(viewingAvailability.availability).filter(a => a.status === 'unavailable').length}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">✗ Unavailable</div>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-yellow-600">
                            {Object.values(viewingAvailability.availability).filter(a => a.status === 'maybe').length}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">? Maybe</div>
                        </div>
                      </div>

                      {/* Meal Preference Summary - Only show if askMealPreference is enabled */}
                      {viewingAvailability.askMealPreference && viewingAvailability.mealPreferences && Object.keys(viewingAvailability.mealPreferences).length > 0 && (
                        <div className="mb-6">
                          <h3 className="font-semibold text-lg mb-3">🍽️ Meal Summary</h3>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-green-600">
                                {Object.values(viewingAvailability.mealPreferences).filter(m => m.preference === 'veg').length}
                              </div>
                              <div className="text-sm text-gray-600">🥗 Veg</div>
                            </div>
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-orange-600">
                                {Object.values(viewingAvailability.mealPreferences).filter(m => m.preference === 'non-veg').length}
                              </div>
                              <div className="text-sm text-gray-600">🍗 Non-Veg</div>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-gray-600">
                                {Object.values(viewingAvailability.mealPreferences).filter(m => m.preference === 'opt-out').length}
                              </div>
                              <div className="text-sm text-gray-600">❌ Opt Out</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Player List */}
                      <div className="space-y-2">
                        <h3 className="font-semibold text-lg mb-3">Player Responses</h3>
                        {Object.entries(viewingAvailability.availability)
                          .sort((a, b) => {
                            // Sort: changed players first, then by timestamp
                            if (a[1].isChanged && !b[1].isChanged) return -1;
                            if (!a[1].isChanged && b[1].isChanged) return 1;
                            return new Date(b[1].updatedAt) - new Date(a[1].updatedAt);
                          })
                          .map(([playerId, data]) => {
                            // Get meal preference for this player
                            const mealPref = viewingAvailability.mealPreferences?.[playerId];

                            return (
                              <div key={playerId} className={`flex justify-between items-center p-3 rounded-lg ${data.isChanged ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-gray-50'
                                }`}>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <div className="font-medium">{data.playerName}</div>
                                    {data.isChanged && (
                                      <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded font-semibold">
                                        CHANGED
                                      </span>
                                    )}
                                    {data.previousStatus && data.isChanged && (
                                      <span className="text-xs text-gray-500">
                                        (was: {data.previousStatus})
                                      </span>
                                    )}
                                  </div>
                                  {data.updatedAt && (
                                    <div className="text-xs text-gray-500">
                                      Last updated: {new Date(data.updatedAt).toLocaleString()}
                                    </div>
                                  )}
                                  {data.history && data.history.length > 1 && (
                                    <details className="text-xs text-gray-500 mt-1">
                                      <summary className="cursor-pointer hover:text-gray-700">
                                        View change history ({data.history.length} updates)
                                      </summary>
                                      <div className="ml-3 mt-1 space-y-1">
                                        {data.history.map((h, idx) => (
                                          <div key={idx} className="flex items-center gap-2">
                                            <span className={`font-semibold ${h.status === 'available' ? 'text-green-600' :
                                              h.status === 'unavailable' ? 'text-red-600' :
                                                'text-yellow-600'
                                              }`}>
                                              {h.status}
                                            </span>
                                            <span className="text-gray-400">→</span>
                                            <span>{new Date(h.changedAt).toLocaleString()}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Meal Preference Badge */}
                                  {viewingAvailability.askMealPreference && mealPref && (
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${mealPref.preference === 'veg' ? 'bg-green-100 text-green-700' :
                                      mealPref.preference === 'non-veg' ? 'bg-orange-100 text-orange-700' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>
                                      {mealPref.preference === 'veg' ? '🥗 Veg' :
                                        mealPref.preference === 'non-veg' ? '🍗 Non-Veg' :
                                          '❌ Opt Out'}
                                    </span>
                                  )}
                                  {viewingAvailability.askMealPreference && !mealPref && (
                                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-400">
                                      🍽️ Not set
                                    </span>
                                  )}

                                  {/* Availability Badge */}
                                  {data.status === 'available' && (
                                    <span className="px-3 py-1 bg-green-600 text-white rounded text-sm font-semibold">
                                      ✓ Available
                                    </span>
                                  )}
                                  {data.status === 'unavailable' && (
                                    <span className="px-3 py-1 bg-red-600 text-white rounded text-sm font-semibold">
                                      ✗ Unavailable
                                    </span>
                                  )}
                                  {data.status === 'maybe' && (
                                    <span className="px-3 py-1 bg-yellow-600 text-white rounded text-sm font-semibold">
                                      ? Maybe
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-6xl mb-4">📋</div>
                      <p className="text-gray-500">No availability responses yet</p>
                      <p className="text-sm text-gray-400 mt-2">Send an availability reminder to players</p>
                    </div>
                  )}
                </div>

                <div className="border-t px-6 py-4 bg-gray-50">
                  <button
                    onClick={() => setViewingAvailability(null)}
                    className="w-full bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700 font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Feedback Button */}
          <FeedbackButton user={user} currentPage="admin-dashboard" clubId={selectedClub?.id} clubName={selectedClub?.name} />

          {/* Footer */}
          <footer className="mt-12 py-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Copyright ©️ 2026 ClubHudl, All rights reserved
            </p>
          </footer>
        </div>{/* End z-10 wrapper */}
      </div>
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav view={view} setView={setView} />
    </PullToRefresh>
  );
};

export default AdminDashboard;